"use client";

import {
  useEffect,
  useState,
  useMemo,
  useRef,
  use,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { useCart } from "@/components";
import { Breadcrumbs, Header } from "@/components";
import EntryModal from "@/components/modals/EntryModal";
import LogoLoader from "@/components/ui/LogoLoader";
import {
  generateProductSlug,
  cleanProductName,
  extractBrandName,
  generateProductSEOTitle,
  generateProductSEODescription,
} from "@shared/lib/slugify";
import {
  parseProduct,
  generateSEODescription,
  isCannabisProduct,
} from "@shared/lib/product-parser";
import { getStoredStoreId } from "@/lib/store-storage";
import { getProductMedia } from "@shared/lib/schema-helpers";
import type { SizeOption } from "@/lib/types/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  LinkIcon,
  ShareIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  MinusIcon,
  PlusIcon,
  CartIcon,
  ChevronUpIcon,
  ArrowNarrowRightIcon,
  HeartIcon,
} from "@/components/icons";
import { ProductCard } from "@/components";
import Button from "@/components/ui/Button";
import ExpandableText from "@/components/ui/ExpandableText";
import {
  extractVariants,
  extractColorsAndSizes,
  getColorHex,
  generateProductGroupKey,
} from "@/lib/variant-extractor";
import {
  resolveSellingPriceFromAvailability,
  strikethroughCompareAtPrice,
} from "@/lib/storefront-pricing";
import { CTA_ADD_TO_CART, STATUS_OUT_OF_STOCK } from "@/lib/ux-copy";
import {
  mergePdpRecommendations,
  rankStoreRecommendations,
} from "@/lib/recommendations";
import { trackFunnelEvent, trackFunnelEventOnce } from "@/lib/analytics/funnel";
import { mergeCannabisFieldsFromAvailability } from "@/lib/product-potency-merge";
import FavoriteButton from "@/components/ui/FavoriteButton";

/**
 * Prefix a stored image URL with the backend base only when it isn't already
 * an absolute URL. The backend now emits absolute URLs for media items, so
 * blindly concatenating `${base}${m.url}` produces double prefixes like
 * `http://localhost:4000http://localhost:4000/uploads/...`.
 */
function joinImageUrl(base: string, url: string | null | undefined): string {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("data:")) return url;
  return `${base}${url.startsWith("/") ? url : `/${url}`}`;
}

/** THC/CBD label for schema.org when percent or mg/g totals are stored. */
function schemaCannabinoidPercentLabel(
  p: { thcPercent?: unknown; thcMg?: unknown; cbdPercent?: unknown; cbdMg?: unknown } | null | undefined,
  kind: "thc" | "cbd"
): string | null {
  if (!p) return null;
  if (kind === "thc") {
    if (
      p.thcPercent !== null &&
      p.thcPercent !== undefined &&
      !Number.isNaN(Number(p.thcPercent)) &&
      Number(p.thcPercent) > 0
    ) {
      return `${Number(p.thcPercent)}%`;
    }
    if (
      p.thcMg !== null &&
      p.thcMg !== undefined &&
      !Number.isNaN(Number(p.thcMg)) &&
      Number(p.thcMg) > 0
    ) {
      return `${(Number(p.thcMg) / 10).toFixed(1)}%`;
    }
  } else {
    if (
      p.cbdPercent !== null &&
      p.cbdPercent !== undefined &&
      !Number.isNaN(Number(p.cbdPercent)) &&
      Number(p.cbdPercent) > 0
    ) {
      return `${Number(p.cbdPercent)}%`;
    }
    if (
      p.cbdMg !== null &&
      p.cbdMg !== undefined &&
      !Number.isNaN(Number(p.cbdMg)) &&
      Number(p.cbdMg) > 0
    ) {
      return `${(Number(p.cbdMg) / 10).toFixed(1)}%`;
    }
  }
  return null;
}

// Helper function to organize clothing variants by color and size
function organizeClothingVariants(
  sizeOptions: Product["sizeOptions"],
  classificationName?: string | null
) {
  if (!sizeOptions || sizeOptions.length === 0) return null;

  // Check if this is a clothing/merch product
  const isClothing =
    classificationName &&
    (classificationName.toLowerCase().includes("clothing") ||
      classificationName.toLowerCase().includes("merch") ||
      classificationName.toLowerCase().includes("apparel") ||
      classificationName.toLowerCase().includes("accessories"));

  if (!isClothing) return null;

  // Parse each size option to extract color (variant1) and size (variant2)
  const colorGroups = new Map<
    string,
    Array<(typeof sizeOptions)[0] & { color?: string; size?: string }>
  >();

  for (const option of sizeOptions) {
    // Try to parse the product name to extract variants
    // We need to get the original product name - check if we can get it from the product
    // For now, we'll extract from sizeOrWeight or use a heuristic
    const sizeOrWeight = option.sizeOrWeight || "";

    // Try to extract color from the product name if available
    // For clothing, variant1 is usually color, variant2 is size
    // We'll need to parse the actual product name, but for now we'll organize by sizeOrWeight
    // which might contain both color and size info

    // Group by size (variant2) - size is usually in sizeOrWeight
    const size = sizeOrWeight || "One Size";

    // For now, we'll create a simple grouping by size
    // In a full implementation, we'd parse the product name to get color
    if (!colorGroups.has(size)) {
      colorGroups.set(size, []);
    }
    colorGroups.get(size)!.push({ ...option, size });
  }

  return {
    colors: Array.from(colorGroups.keys()),
    sizes: Array.from(
      new Set(sizeOptions.map((opt) => opt.sizeOrWeight || "One Size"))
    ),
    groupedBySize: Object.fromEntries(colorGroups),
  };
}

type ProductAvailability = {
  id: string;
  packageId: string;
  inStock: number;
  unitCost: number;
  price: number;
  batchId?: string | null;
  dominantTerpene?: string | null;
  strain?: string | null;
  totalTerpenePercent?: number | null;
  createdAt: Date;
};

type Product = {
  id: string;
  name: string;
  masterProductName?: string | null;
  catalogSku?: string;
  vendorSku?: string;
  classificationName?: string;
  sizeOrWeight?: string | null;
  slug?: string;
  shortDescription?: string;
  longDescription?: string;
  ingredients?: string | null;
  foodAllergens?: string | null;
  carrierOil?: string | null;
  heroShotUri?: string;
  brandName?: string;
  strain?: string;
  thcMin?: number;
  thcMax?: number;
  thcUom?: string;
  thcPercent?: number;
  thcMg?: number;
  cbdMin?: number;
  cbdMax?: number;
  cbdUom?: string;
  cbdPercent?: number;
  cbdMg?: number;
  cbnPercent?: number;
  cbnMg?: number;
  cbgPercent?: number;
  cbgMg?: number;
  dominantTerpene?: string;
  totalTerpenePercent?: number | null;
  netContent?: string;
  pieces?: number;
  equivalenceDriedFlowerG?: number;
  upcs?: { upc: string }[];
  supplierSkus?: { sku: string }[];
  specifications?: { name: string; value: string }[] | null;
  options?: any; // JSON field that may contain effects or other product options
  productMedia?: Array<{
    id?: string;
    type: "image" | "video";
    url: string;
    alt?: string | null;
    position: number;
  }>; // Legacy - use getProductMedia() helper
  media?: Array<{
    type: "image" | "video";
    url: string;
    alt?: string | null;
    position: number;
  }> | null; // JSON array (consolidated from ProductMedia)
  seoTitle?: string | null;
  seoDescription?: string | null;
  availabilities: ProductAvailability[];
  sizeOptions?: Array<{
    packageId: string;
    sizeOrWeight?: string | null;
    netContent?: string | null; // Legacy support
    inStock: number;
    price: number;
    compareAtPrice?: number | null;
    productId?: string;
    productSlug?: string | null;
    variationId?: string | null;
    longDescription?: string | null;
    heroShotUri?: string | null;
    shortDescription?: string | null;
    originalProductName?: string | null; // Original product name for variant parsing
  }>;
  /** AGLC "Flavour & Terpene Profile" string; we only surface flavour tags, not scraped terpene %. */
  flavourTerpeneProfile?: string | null;
  /** AGLC / import fields for detail accordions */
  companyName?: string | null;
  licensedProducer?: string | null;
  provinceOfOrigin?: string | null;
  regionOfProduct?: string | null;
  growingMethod?: string | null;
  directions?: string | null;
  typeAlt?: string | null;
  format?: string | null;
  altCategory?: string | null;
  altSubCategory?: string | null;
  additionalCannabinoids?: string | null;
  /** When true, the page lists every lot's lab data (house products like
   *  Whole Lotta Dank selling multiple lots at once). */
  showLotInfo?: boolean;
  lots?: Array<{
    lotCode: string;
    thcPercent: number | null;
    thcMg: number | null;
    cbdPercent: number | null;
    cbdMg: number | null;
    cbnMg: number | null;
    cbgMg: number | null;
    cbcMg: number | null;
    thcvMg: number | null;
    totalTerpenePercent: number | null;
    dominantTerpene: string | null;
    strain: string | null;
    harvestDate: string | null;
    packageDate: string | null;
  }>;
  groupProducts?: Array<{
    id: string;
    productId: string;
    packageId: string;
    inStock: number;
    unitCost: number;
    compareAtPrice?: number | null;
    productSlug?: string | null;
    sizeOrWeight: string | null;
    variationId: string | null;
    price: number;
    product: {
      id: string;
      name: string; // Original product name
      classificationName?: string;
      brandName?: string;
    };
  }>;
};

type ProductData = {
  success: boolean;
  product: Product;
  priceMap: Record<string, number>;
  error?: string;
};

function parseImportedTerpeneProfile(raw: string | null | undefined): {
  dominant: Array<{ name: string; percent: number }>;
  other: string[];
} {
  if (!raw || !String(raw).trim()) return { dominant: [], other: [] };
  const text = String(raw).trim();

  const dominantSegment =
    text
      .split("|")
      .map((s) => s.trim())
      .find((s) => /^dominant terpenes\s*:/i.test(s)) || "";
  const dominantText = dominantSegment.replace(/^dominant terpenes\s*:\s*/i, "");
  const dominant = dominantText
    ? dominantText
      .split(",")
      .map((part) => {
        const m = part.trim().match(/^(.+?)\s+([\d.]+)%$/);
        if (!m) return null;
        const name = m[1].trim();
        const percent = Number(m[2]);
        if (!name || Number.isNaN(percent)) return null;
        return { name, percent };
      })
      .filter((x): x is { name: string; percent: number } => Boolean(x))
      .slice(0, 3)
    : [];

  const otherSegment =
    text
      .split("|")
      .map((s) => s.trim())
      .find((s) => /^other terpenes\s*:/i.test(s)) || "";
  const otherText = otherSegment.replace(/^other terpenes\s*:\s*/i, "").trim();
  const other = otherText
    ? otherText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
    : [];

  return { dominant, other };
}

function specValueByNames(
  specs: { name: string; value: string }[] | null | undefined,
  keys: string[]
): string | null {
  if (!Array.isArray(specs)) return null;
  const want = keys.map((k) => k.toLowerCase());
  for (const s of specs) {
    const n = String(s?.name || "")
      .trim()
      .toLowerCase();
    if (!n) continue;
    if (want.some((w) => n === w || n.includes(w))) {
      const v = String(s?.value || "").trim();
      return v || null;
    }
  }
  return null;
}

function formatMinorCannabinoidsLine(
  p: Product | null | undefined
): string | null {
  if (!p) return null;
  const parts: string[] = [];
  const push = (label: string, pctRaw: unknown, mgRaw: unknown) => {
    if (pctRaw !== null && pctRaw !== undefined && !Number.isNaN(Number(pctRaw))) {
      const n = Number(pctRaw);
      parts.push(n < 1 ? `${label} <1%` : `${label} ${n}%`);
      return;
    }
    if (mgRaw !== null && mgRaw !== undefined && !Number.isNaN(Number(mgRaw))) {
      const pct = Number(mgRaw) / 10;
      parts.push(pct < 0.1 ? `${label} <1%` : `${label} ${pct.toFixed(1)}%`);
    }
  };
  push("CBN", p.cbnPercent, p.cbnMg);
  push("CBG", p.cbgPercent, p.cbgMg);
  if (parts.length) return parts.join(", ");
  const addl =
    typeof p.additionalCannabinoids === "string"
      ? p.additionalCannabinoids.trim()
      : "";
  return addl || null;
}

function formatThcBadgeFromProduct(p: Product | null | undefined): string | null {
  if (!p) return null;
  const min = p.thcMin != null ? Number(p.thcMin) : null;
  const max = p.thcMax != null ? Number(p.thcMax) : null;
  const uom = (p.thcUom || "").toLowerCase();
  if (
    min !== null &&
    max !== null &&
    !Number.isNaN(min) &&
    !Number.isNaN(max)
  ) {
    if (uom === "%" || uom === "percent" || uom === "") {
      return Math.abs(min - max) < 0.001
        ? `${min}% THC`
        : `${min}-${max}% THC`;
    }
    return `${min}–${max} ${p.thcUom || ""} THC`.trim();
  }
  if (p.thcPercent != null && Number(p.thcPercent) > 0) {
    return `${Number(p.thcPercent)}% THC`;
  }
  if (p.thcMg != null && Number(p.thcMg) > 0) {
    return `${(Number(p.thcMg) / 10).toFixed(1)}% THC`;
  }
  return null;
}

function sanitizeNutritionHtml(raw: string): string {
  return raw
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/\s(href|src)=["']javascript:[^"']*["']/gi, "");
}

function parseNutritionRowsText(raw: string): Array<{ label: string; value: string; isSubrow: boolean }> {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const isSubrow = line.startsWith('>');
      const normalized = isSubrow ? line.slice(1).trim() : line;
      const [labelRaw, valueRaw] = normalized.split('|');
      return {
        label: (labelRaw || '').trim(),
        value: (valueRaw || '').trim(),
        isSubrow,
      };
    })
    .filter((row) => row.label);
}

export default function ProductPage({
  params,
}: {
  params: Promise<{ productId: string }>;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const searchParamsString = useMemo(
    () => searchParams.toString(),
    [searchParams]
  );
  const variantParam = searchParams.get("variant");
  const resolvedParams = use(params);
  const productSlug = resolvedParams.productId; // This is now a slug, not an ID
  const base = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000";
  const [data, setData] = useState<ProductData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    null
  );
  // When packageId is empty, we use productId or productSlug as the identifier
  // This handles cases where packageId is empty string (like accessories with color variants)
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );
  const [quantity, setQuantity] = useState(1);
  const [stores, setStores] = useState<
    Array<{
      covaLocationId: number;
      name: string;
      city?: string;
      province?: string;
      address?: string;
    }>
  >([]);
  const { state, addItem, isInitialized, setStore } = useCart();
  const { user } = useAuth();
  // Product compare feature removed.
  const storedStoreId = getStoredStoreId();
  const storeLocationId = storedStoreId || state.storeCovaLocationId;
  const resolvedStoreId = storeLocationId;
  const hasCleanedUpStoreParam = useRef(false);
  const isSwitchingSize = useRef(false);
  const processedVariantParam = useRef<string | null>(null);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  /** Mobile-only tab between the product info column and the nutrition info column.
   *  Desktop renders both side-by-side and ignores this state. */
  const [activeMobileTab, setActiveMobileTab] = useState<"details" | "nutrition">("details");
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    productOverview: true,
    descriptionFull: false,
    lots: true,
    effects: true,
    aboutBrand: true,
    classification: true,
    terpenes: true,
    formatPack: true,
    directions: true,
  });
  const [relatedProducts, setRelatedProducts] = useState<any[]>([]);
  const [loadingRelatedProducts, setLoadingRelatedProducts] = useState(false);
  const [relatedProductsPriceMap, setRelatedProductsPriceMap] = useState<
    Record<string, number>
  >({});
  const [pdpRecFeedbackDone, setPdpRecFeedbackDone] = useState(false);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  // Refs for the thumbnail strip: the scroll container itself, plus each tile so we can
  // auto-center the active one when selectedImageIndex changes (e.g. when the hero image
  // carousel advances or a size/color switch shifts the active media).
  const thumbStripRef = useRef<HTMLDivElement>(null);
  const thumbRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [thumbFadeLeft, setThumbFadeLeft] = useState(false);
  const [thumbFadeRight, setThumbFadeRight] = useState(false);

  // Reset selected image index when product changes, heroShotUri changes, or selected color changes
  useEffect(() => {
    if (data?.product) {
      setSelectedImageIndex(0);
    }
  }, [data?.product?.id, data?.product?.heroShotUri, selectedColor]);

  /** Center the active thumbnail in the strip whenever selectedImageIndex changes.
   *  Manually computed scrollTo (instead of element.scrollIntoView) so only the strip
   *  scrolls, never the page. Block:'nearest' on scrollIntoView still occasionally
   *  budges the page when the strip itself sits near a viewport edge. */
  useEffect(() => {
    const container = thumbStripRef.current;
    const tile = thumbRefs.current[selectedImageIndex];
    if (!container || !tile) return;
    const target = tile.offsetLeft - container.clientWidth / 2 + tile.clientWidth / 2;
    const clamped = Math.max(0, Math.min(target, container.scrollWidth - container.clientWidth));
    container.scrollTo({ left: clamped, behavior: 'smooth' });
  }, [selectedImageIndex, data?.product?.id, selectedColor]);

  /** Show / hide edge-fade hints based on whether the strip can scroll further. Updated
   *  on scroll, on resize, and on the underlying media list changing. */
  useEffect(() => {
    const el = thumbStripRef.current;
    if (!el) return;
    const update = () => {
      const atStart = el.scrollLeft <= 1;
      const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1;
      const overflowing = el.scrollWidth > el.clientWidth + 1;
      setThumbFadeLeft(overflowing && !atStart);
      setThumbFadeRight(overflowing && !atEnd);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [data?.product?.id, selectedColor]);

  useEffect(() => {
    const productId = data?.product?.id;
    if (!productId || !user) {
      setIsFavorite(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const accessToken = localStorage.getItem("accessToken");
        const response = await fetch(`${base}/api/auth/account/favorites/${productId}/status`, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
          credentials: "include",
        });
        const json = await response.json();
        if (!cancelled && json?.success) {
          setIsFavorite(!!json.isFavorite);
        }
      } catch {
        if (!cancelled) {
          setIsFavorite(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [base, data?.product?.id, user]);

  // Fetch stores for header
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${base}/api/stores`, { cache: "no-store" });
        if (res.ok) {
          const json = await res.json();
          if (json?.stores) setStores(json.stores);
        }
      } catch { }
    })();
  }, [base]);

  // Remove store parameter from URL if it exists (cleanup - only once)
  useEffect(() => {
    if (hasCleanedUpStoreParam.current) return;
    const params = new URLSearchParams(searchParamsString);
    if (params.has("store")) {
      hasCleanedUpStoreParam.current = true;
      params.delete("store");
      const queryString = params.toString();
      router.replace(`${pathname}${queryString ? `?${queryString}` : ""}`, {
        scroll: false,
      });
    }
  }, [pathname, router, searchParamsString]);

  // Check if store ID exists, show EntryModal if not
  useEffect(() => {
    if (!isInitialized) return;
    if (!resolvedStoreId || !Number.isFinite(resolvedStoreId)) {
      setShowEntryModal(true);
    }
  }, [isInitialized, resolvedStoreId]);

  // Set store from localStorage when available
  useEffect(() => {
    if (!isInitialized) return;
    if (storedStoreId && storedStoreId !== state.storeCovaLocationId) {
      setStore(storedStoreId);
    }
  }, [isInitialized, storedStoreId, state.storeCovaLocationId, setStore]);

  /**
   * Switch to a different variant using cached group product data
   * This prevents reloading the product details page when switching variants
   * Updates all product fields instantly without showing loading spinner
   */
  const switchVariant = useMemo(() => {
    return (
      targetProductSlug: string | null,
      sizeOption: SizeOption | null
    ) => {
      if (!data?.success || !data.product || !sizeOption || !targetProductSlug)
        return;

      // Prevent API call by setting the switching flag BEFORE any state updates
      isSwitchingSize.current = true;

      // Get product details from sizeOption.product (full product object) if available
      const productDetails = (sizeOption as any).product || null;

      // Update product state with variant-specific details from the cached sizeOption
      // Replace ALL values instantly - no loading state
      // Priority: productDetails (from sizeOption.product) > sizeOption fields > existing product fields
      const updatedProduct: Product = {
        ...data.product,
        longDescription:
          productDetails?.longDescription ??
          sizeOption.longDescription ??
          data.product.longDescription ??
          null,
        heroShotUri:
          productDetails?.heroShotUri ??
          sizeOption.heroShotUri ??
          data.product.heroShotUri ??
          null,
        shortDescription:
          productDetails?.shortDescription ??
          sizeOption.shortDescription ??
          data.product.shortDescription ??
          null,
        slug: targetProductSlug,
        // Include product details from productDetails if available (THC/CBD/Effects)
        ...(productDetails
          ? {
            thcMin:
              productDetails.thcMin !== undefined &&
                productDetails.thcMin !== null
                ? Number(productDetails.thcMin)
                : data.product.thcMin,
            thcMax:
              productDetails.thcMax !== undefined &&
                productDetails.thcMax !== null
                ? Number(productDetails.thcMax)
                : data.product.thcMax,
            thcUom: productDetails.thcUom || data.product.thcUom,
            thcPercent:
              productDetails.thcPercent !== undefined &&
                productDetails.thcPercent !== null
                ? Number(productDetails.thcPercent)
                : data.product.thcPercent,
            thcMg:
              productDetails.thcMg !== undefined &&
                productDetails.thcMg !== null
                ? Number(productDetails.thcMg)
                : data.product.thcMg,
            cbdMin:
              productDetails.cbdMin !== undefined &&
                productDetails.cbdMin !== null
                ? Number(productDetails.cbdMin)
                : data.product.cbdMin,
            cbdMax:
              productDetails.cbdMax !== undefined &&
                productDetails.cbdMax !== null
                ? Number(productDetails.cbdMax)
                : data.product.cbdMax,
            cbdUom: productDetails.cbdUom || data.product.cbdUom,
            cbdPercent:
              productDetails.cbdPercent !== undefined &&
                productDetails.cbdPercent !== null
                ? Number(productDetails.cbdPercent)
                : data.product.cbdPercent,
            cbdMg:
              productDetails.cbdMg !== undefined &&
                productDetails.cbdMg !== null
                ? Number(productDetails.cbdMg)
                : data.product.cbdMg,
            specifications:
              productDetails.specifications || data.product.specifications,
            options: productDetails.options || data.product.options,
          }
          : {}),
        // Keep all sizeOptions and availabilities (all group products are already loaded)
        sizeOptions: data.product.sizeOptions,
        availabilities: data.product.availabilities,
        groupProducts: data.product.groupProducts,
      };

      // Update state instantly - this will trigger re-render with new values
      setData({
        ...data,
        product: updatedProduct,
      });

      // Update URL with new variant slug without triggering reload
      const canonicalSlug = generateProductSlug({
        name: data.product.name,
        id: data.product.id,
        brandName: data.product.brandName,
        classificationName: data.product.classificationName,
        sizeOrWeight: null, // Base slug should NOT include size/weight
      });

      if (canonicalSlug) {
        const params = new URLSearchParams();
        params.set("variant", targetProductSlug);
        const newUrl = `/product/${canonicalSlug}?${params.toString()}`;
        // Use replace with scroll: false to prevent page reload
        router.replace(newUrl, { scroll: false });
      }

      // Reset the flag after state updates complete
      // Use a longer delay to ensure useEffect doesn't run
      setTimeout(() => {
        isSwitchingSize.current = false;
      }, 300);
    };
  }, [data, router]);

  // Fetch product data
  // Priority: 1) variant parameter (product slug), 2) productSlug from URL path (cleaned name slug)
  useEffect(() => {
    // Wait for CartContext unless we already have a store param to use
    if (!isInitialized && !resolvedStoreId) return;

    // Skip fetch if we're switching sizes (data is updated directly from sizeOption)
    // Also skip if we already have product data and only the variant param changed
    if (isSwitchingSize.current) {
      return;
    }

    // If we already have product data and only variantParam changed, don't reload
    // This prevents showing loading spinner when switching variants
    if (data?.success && data.product) {
      // If we have sizeOptions and the variantParam matches one of them, this is just a variant switch
      if (
        data.product.sizeOptions &&
        data.product.sizeOptions.length > 0 &&
        variantParam
      ) {
        const hasMatchingVariant = data.product.sizeOptions.some(
          (opt) =>
            opt.productSlug === variantParam ||
            (opt as any).product?.slug === variantParam
        );
        if (hasMatchingVariant) {
          // This is just a variant switch within the same product group
          // Don't reload - the switchVariant function already updated the state
          return;
        }
      }
      // Also check groupProducts for variant matching (for clothing with colors)
      if (
        data.product.groupProducts &&
        data.product.groupProducts.length > 0 &&
        variantParam
      ) {
        const hasMatchingGroupProduct = data.product.groupProducts.some(
          (gp: any) =>
            gp.productSlug === variantParam || gp.product?.slug === variantParam
        );
        if (hasMatchingGroupProduct) {
          // This is just a variant switch within the same product group
          // Don't reload - the switchVariant function already updated the state
          return;
        }
      }
      // If variantParam is null/empty but we have data, and productSlug hasn't changed, don't reload
      if (
        !variantParam &&
        productSlug ===
        (data.product.slug ||
          generateProductSlug({
            name: data.product.name,
            id: data.product.id,
            brandName: data.product.brandName,
            classificationName: data.product.classificationName,
            sizeOrWeight: null,
          }))
      ) {
        // Same product, no variant change, don't reload
        return;
      }
    }

    if (!resolvedStoreId || Number.isNaN(resolvedStoreId)) {
      setLoading(false);
      setData({
        success: false,
        product: {} as Product,
        priceMap: {},
        error: "Please select a store to view this product.",
      });
      setSelectedPackageId(null); // Reset selected package when no store
      return;
    }

    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setSelectedPackageId(null); // Reset selected package when store changes
      try {
        // Use variant parameter (exact product slug from COVA) if available, otherwise use productSlug from URL
        // The variant parameter contains the exact product slug and should be used directly
        const productIdentifier = variantParam || productSlug;

        const res = await fetch(
          `${base}/api/store/${resolvedStoreId}/products/${encodeURIComponent(
            productIdentifier
          )}`,
          {
            cache: "no-store",
            signal: controller.signal,
          }
        );
        const json = await res.json();
        if (!controller.signal.aborted) {
          // When variant parameter is provided, update product state with the matching sizeOption's details
          // This ensures the correct product details (image, description) are displayed for the selected variant
          if (json?.success && variantParam && json.product.sizeOptions) {
            const matchingSizeOption = json.product.sizeOptions?.find(
              (opt: NonNullable<Product["sizeOptions"]>[number]) =>
                opt.productSlug === variantParam
            );
            if (matchingSizeOption) {
              // Update product state with variant-specific details from the matching sizeOption
              // Always replace values, even if empty/null (don't use fallback to previous values)
              json.product = {
                ...json.product,
                longDescription: matchingSizeOption.longDescription ?? null,
                heroShotUri: matchingSizeOption.heroShotUri ?? null,
                shortDescription: matchingSizeOption.shortDescription ?? null,
                slug: matchingSizeOption.productSlug ?? null,
              };
            }
          }

          setData(json);

          // Console log for debugging: Show products fetched when loading product details page
          // Gated to development so it never ships to production PDP views.
          if (process.env.NODE_ENV === "development" && json?.success && json.product) {
            console.log("=== Product Details Page - Products Fetched ===");
            console.log("Product:", {
              id: json.product.id,
              name: json.product.name,
              classificationName: json.product.classificationName,
              brandName: json.product.brandName,
            });
            console.log(
              "Size Options Count:",
              json.product.sizeOptions?.length || 0
            );
            console.log(
              "Group Products Count:",
              (json.product.groupProducts as any[])?.length || 0
            );
            console.log("All Size Options:", json.product.sizeOptions);
            console.log("All Group Products:", json.product.groupProducts);

            // For clothing products, log structure by color
            const classification = json.product.classificationName || "";
            const isClothing =
              classification &&
              (classification.toLowerCase().includes("clothing") ||
                classification.toLowerCase().includes("merch") ||
                classification.toLowerCase().includes("apparel") ||
                classification.toLowerCase().includes("accessories"));

            if (
              isClothing &&
              json.product.groupProducts &&
              Array.isArray(json.product.groupProducts)
            ) {
              console.log("=== Clothing Product - Structure by Color ===");
              console.log(
                "Raw groupProducts from backend:",
                json.product.groupProducts
              );

              // Group by color to show available sizes for each color
              const colorMap = new Map<string, Array<any>>();

              for (const gp of json.product.groupProducts as any[]) {
                const originalProductName =
                  gp.product?.name || gp.productId || "";
                const sizeOrWeight = gp.sizeOrWeight || null;

                // Extract variants
                const variants = extractVariants(
                  originalProductName,
                  json.product.classificationName || null,
                  gp.product?.brandName || json.product.brandName || null,
                  sizeOrWeight
                );

                const color = variants.color || "Default";
                if (!colorMap.has(color)) {
                  colorMap.set(color, []);
                }

                // Include all product details from groupProduct
                colorMap.get(color)!.push({
                  // Product identifiers
                  id: gp.id,
                  productId: gp.productId,
                  packageId: gp.packageId || null, // Keep null instead of empty string to see if it's actually missing
                  variationId: gp.variationId || null,

                  // Product info
                  size: variants.size || sizeOrWeight || "Default",
                  sizeOrWeight: gp.sizeOrWeight || null,
                  originalProductName: originalProductName,
                  color: variants.color || null,

                  // Stock and pricing
                  inStock:
                    gp.inStock !== undefined && gp.inStock !== null
                      ? gp.inStock
                      : 0,
                  price: gp.price || null,
                  unitCost: gp.unitCost || null,

                  // Product details
                  product: gp.product || null,
                });
              }

              // Log structure: For each color, show all available sizes with all details
              console.log(
                "Color Options and Available Sizes (with all details):"
              );
              for (const [color, products] of colorMap.entries()) {
                const uniqueSizes = Array.from(
                  new Set(products.map((p) => p.size))
                ).filter(Boolean);
                console.log(`  ${color}:`, {
                  sizes: uniqueSizes,
                  totalProducts: products.length,
                  products: products, // Show all product details
                });
              }
            }
            console.log("==========================================");
          }

          // Cache all group product details for variant switching without reload
          if (
            json?.success &&
            json.product.sizeOptions &&
            json.product.sizeOptions.length > 0
          ) {
            // All variant details are already in sizeOptions, so we have everything we need
            // The backend returns all products in the same group via sizeOptions array
            // Each sizeOption contains: productId, productSlug, longDescription, heroShotUri, shortDescription, etc.
            // This means we already have all product details for all variants in the group
          }

          // Set default selected package
          // Priority: 1) Find size option matching variant slug, 2) First size option, 3) FIFO
          if (json?.success) {
            // Check if product has sizeOptions (grouped products)
            if (
              json.product.sizeOptions &&
              json.product.sizeOptions.length > 0
            ) {
              // When variant parameter is provided, it contains the exact product slug
              // Find the size option that matches the variant slug
              let matchingSizeOption = null;
              if (variantParam) {
                matchingSizeOption = json.product.sizeOptions.find(
                  (opt: any) => opt.productSlug === variantParam
                );
              }

              // Step 1: Filter availabilities to only include those with stock >= 1 (after stock-1 rule)
              // Backend applies stock-1 rule in response, so we filter by inStock >= 1
              const availableProducts = (
                json.product.availabilities || []
              ).filter((avail: ProductAvailability) => avail.inStock >= 1);

              if (availableProducts.length === 0) {
                // No available products, fallback to first size option (will show out of stock)
                const firstSizeOption = json.product.sizeOptions?.[0];
                if (firstSizeOption) {
                  setSelectedPackageId(firstSizeOption.packageId);
                }
                return;
              }

              // Step 2: Group available products by size/weight
              // Backend already sorted by receivedDate (FIFO), so we maintain that order
              const sizeGroups = new Map<string, ProductAvailability[]>();

              for (const avail of availableProducts) {
                // Extract productId from availability.id (format: productId::locationId)
                const availProductId = avail.id.split("::")[0];

                // Match by packageId first, then by productId if packageId is empty
                let matchingOpt = null;
                if (avail.packageId && avail.packageId.trim() !== "") {
                  matchingOpt = json.product.sizeOptions?.find(
                    (opt: NonNullable<Product["sizeOptions"]>[number]) =>
                      opt.packageId === avail.packageId
                  );
                } else {
                  // When packageId is empty, match by productId
                  matchingOpt = json.product.sizeOptions?.find(
                    (opt: NonNullable<Product["sizeOptions"]>[number]) =>
                      opt.productId === availProductId
                  );
                }

                if (matchingOpt) {
                  const availSize = (
                    matchingOpt.sizeOrWeight ||
                    matchingOpt.netContent ||
                    ""
                  )
                    .toLowerCase()
                    .trim();
                  if (!sizeGroups.has(availSize)) {
                    sizeGroups.set(availSize, []);
                  }
                  sizeGroups.get(availSize)!.push(avail);
                }
              }

              // Step 3: Determine target size and variant productSlug
              let targetSize = "";
              let targetProductSlug: string | null = null;

              if (matchingSizeOption) {
                // When variant is specified, use the matching size option's size and productSlug
                targetSize = (
                  matchingSizeOption.sizeOrWeight ||
                  matchingSizeOption.netContent ||
                  ""
                )
                  .toLowerCase()
                  .trim();
                targetProductSlug = matchingSizeOption.productSlug || null;
              } else {
                // No variant specified - choose the first size/weight that has available products
                // Get size options in order and find the first one with available stock
                for (const sizeOpt of json.product.sizeOptions || []) {
                  const sizeKey = (
                    sizeOpt.sizeOrWeight ||
                    sizeOpt.netContent ||
                    ""
                  )
                    .toLowerCase()
                    .trim();
                  if (
                    sizeGroups.has(sizeKey) &&
                    sizeGroups.get(sizeKey)!.length > 0
                  ) {
                    targetSize = sizeKey;
                    targetProductSlug = sizeOpt.productSlug || null;
                    break;
                  }
                }
              }

              // Step 4: For the target size, apply FIFO to find the oldest package (lot)
              // Prioritize packages that match the target productSlug (variant)
              if (targetSize && sizeGroups.has(targetSize)) {
                const sameSizePackages = sizeGroups.get(targetSize)!;

                // If we have a target productSlug (variant), prioritize packages from that variant
                let packagesToSelect = sameSizePackages;
                if (targetProductSlug) {
                  // Find packages that belong to the target variant by matching their sizeOption's productSlug
                  const variantPackages = sameSizePackages.filter(
                    (avail: ProductAvailability) => {
                      const availProductId = avail.id.split("::")[0];
                      let matchingOpt = null;
                      if (avail.packageId && avail.packageId.trim() !== "") {
                        matchingOpt = json.product.sizeOptions?.find(
                          (opt: NonNullable<Product["sizeOptions"]>[number]) =>
                            opt.packageId === avail.packageId
                        );
                      } else {
                        matchingOpt = json.product.sizeOptions?.find(
                          (opt: NonNullable<Product["sizeOptions"]>[number]) =>
                            opt.productId === availProductId
                        );
                      }
                      return matchingOpt?.productSlug === targetProductSlug;
                    }
                  );

                  // If we found packages from the target variant, use those; otherwise use all packages with that size
                  if (variantPackages.length > 0) {
                    packagesToSelect = variantPackages;
                  }
                }

                // Backend already sorted by receivedDate (FIFO), but we need to ensure proper sorting
                // Filter to only include packages with stock >= 1 (after stock-1 rule)
                const availablePackagesToSelect = packagesToSelect.filter(
                  (p: ProductAvailability) => p.inStock >= 1
                );

                if (availablePackagesToSelect.length > 0) {
                  // Sort by createdAt (oldest first) - FIFO logic for multiple packages (lots)
                  availablePackagesToSelect.sort(
                    (a: ProductAvailability, b: ProductAvailability) => {
                      const dateA = new Date(a.createdAt).getTime();
                      const dateB = new Date(b.createdAt).getTime();
                      return dateA - dateB; // Oldest first (FIFO)
                    }
                  );

                  // Select the oldest available package for this size (prioritizing the target variant if specified)
                  const selectedAvail = availablePackagesToSelect[0];
                  const selectedProductIdValue =
                    selectedAvail.id.split("::")[0];

                  // Set both packageId and productId (handle empty packageId case)
                  setSelectedPackageId(
                    selectedAvail.packageId || selectedProductIdValue
                  );
                  setSelectedProductId(selectedProductIdValue);
                } else {
                  // No available packages for this size, fallback to first size option (will show out of stock)
                  const firstSizeOption = json.product.sizeOptions?.[0];
                  if (firstSizeOption) {
                    setSelectedPackageId(
                      firstSizeOption.packageId ||
                      firstSizeOption.productId ||
                      ""
                    );
                    setSelectedProductId(firstSizeOption.productId || null);
                  }
                }
              } else {
                // Fallback: if target size not found or no available packages, use first available product
                availableProducts.sort(
                  (a: ProductAvailability, b: ProductAvailability) => {
                    const dateA = new Date(a.createdAt).getTime();
                    const dateB = new Date(b.createdAt).getTime();
                    return dateA - dateB; // Oldest first (FIFO)
                  }
                );
                const selectedAvail = availableProducts[0];
                const selectedProductIdValue = selectedAvail.id.split("::")[0];
                setSelectedPackageId(
                  selectedAvail.packageId || selectedProductIdValue
                );
                setSelectedProductId(selectedProductIdValue);
              }
            } else if (json.product.availabilities?.length > 0) {
              // Regular products without size options - use FIFO (backend already applies stock-1 rule)
              // Find oldest availability with inStock >= 1 (backend already applied stock-1)
              const oldestAvailability = json.product.availabilities
                .filter((avail: ProductAvailability) => avail.inStock >= 1) // Backend already applies stock-1 rule
                .sort((a: ProductAvailability, b: ProductAvailability) => {
                  const dateA = new Date(a.createdAt).getTime();
                  const dateB = new Date(b.createdAt).getTime();
                  return dateA - dateB; // Oldest first (FIFO)
                })[0];

              if (oldestAvailability) {
                setSelectedPackageId(oldestAvailability.packageId);
              } else {
                // No available packages, use first one (will show out of stock)
                setSelectedPackageId(json.product.availabilities[0].packageId);
              }
            }
          }
        }
      } catch (e: unknown) {
        const name = e instanceof Error ? e.name : "";
        const message =
          e instanceof Error ? e.message : "Failed to load product";
        if (!controller.signal.aborted && name !== "AbortError") {
          setData({
            success: false,
            product: {} as Product,
            priceMap: {},
            error: message || "Failed to load product",
          });
          setSelectedPackageId(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    })();
    return () => controller.abort();
  }, [resolvedStoreId, productSlug, variantParam, base, isInitialized]);

  // All hooks must be called before any conditional returns
  // Calculate values that depend on data, but handle null cases
  const product = data?.product;
  const nutritionHtmlRaw =
    product?.options &&
      typeof product.options === "object" &&
      !Array.isArray(product.options) &&
      typeof (product.options as Record<string, unknown>).nutritionHtml === "string"
      ? ((product.options as Record<string, unknown>).nutritionHtml as string)
      : "";
  const nutritionHtml = nutritionHtmlRaw
    ? sanitizeNutritionHtml(nutritionHtmlRaw)
    : "";
  const nutritionLabelDataRaw =
    product?.options &&
      typeof product.options === "object" &&
      !Array.isArray(product.options) &&
      (product.options as Record<string, unknown>).nutritionLabelData &&
      typeof (product.options as Record<string, unknown>).nutritionLabelData === "object"
      ? ((product.options as Record<string, unknown>).nutritionLabelData as Record<string, unknown>)
      : null;
  const nutritionLabelData = nutritionLabelDataRaw
    ? {
      titleEn:
        typeof nutritionLabelDataRaw.titleEn === "string" && nutritionLabelDataRaw.titleEn.trim()
          ? nutritionLabelDataRaw.titleEn.trim()
          : "Nutrition Facts",
      titleFr:
        typeof nutritionLabelDataRaw.titleFr === "string" && nutritionLabelDataRaw.titleFr.trim()
          ? nutritionLabelDataRaw.titleFr.trim()
          : "Valeur nutritive",
      servingEn:
        typeof nutritionLabelDataRaw.servingEn === "string" ? nutritionLabelDataRaw.servingEn.trim() : "",
      servingFr:
        typeof nutritionLabelDataRaw.servingFr === "string" ? nutritionLabelDataRaw.servingFr.trim() : "",
      calories:
        typeof nutritionLabelDataRaw.calories === "string" ? nutritionLabelDataRaw.calories.trim() : "",
      rows: parseNutritionRowsText(
        typeof nutritionLabelDataRaw.rowsText === "string" ? nutritionLabelDataRaw.rowsText : ""
      ),
      footnoteEn:
        typeof nutritionLabelDataRaw.footnoteEn === "string" ? nutritionLabelDataRaw.footnoteEn.trim() : "",
      footnoteFr:
        typeof nutritionLabelDataRaw.footnoteFr === "string" ? nutritionLabelDataRaw.footnoteFr.trim() : "",
    }
    : null;
  const nutritionIngredients = product?.ingredients?.trim() || "";
  const nutritionAllergens = product?.foodAllergens?.trim() || "";
  const nutritionCarrierOil = product?.carrierOil?.trim() || "";
  const hasNutritionExtras =
    Boolean(nutritionIngredients) ||
    Boolean(nutritionAllergens) ||
    Boolean(nutritionCarrierOil);
  const hasNutritionSection = Boolean(nutritionLabelData) || Boolean(nutritionHtml) || hasNutritionExtras;
  const nutritionExtrasRows = [
    { label: "Ingredients", value: nutritionIngredients },
    { label: "Allergens", value: nutritionAllergens },
    { label: "Carrier Oil", value: nutritionCarrierOil },
  ].filter((row) => row.value);

  // Product compare feature removed.

  // Get selected availability - check sizeOptions first (for grouped products)
  // Organize clothing variants by color for better UX (using same logic as admin panel)
  // Admin panel groups by: parsedBrand|parsedProductName|classificationName, then by color, then by size
  // Moved here before selectedAvailability to avoid "Cannot access before initialization" error
  const clothingVariants = useMemo(() => {
    if (!product) return null;

    const classification = product.classificationName || "";
    const isClothing =
      classification &&
      (classification.toLowerCase().includes("clothing") ||
        classification.toLowerCase().includes("merch") ||
        classification.toLowerCase().includes("apparel") ||
        classification.toLowerCase().includes("accessories"));

    // IMPORTANT: Use groupProducts ONLY - backend already filters to same group
    // groupProducts contains ALL products from the SAME GROUP (same parsedBrand + parsedProductName + classificationName)
    // DO NOT mix products from different groups - backend grouping logic ensures all products in groupProducts are from the same group
    const groupProducts = product.groupProducts || [];

    if (
      !isClothing ||
      (!groupProducts.length &&
        (!product.sizeOptions || product.sizeOptions.length <= 1))
    ) {
      return null;
    }

    // Build a map of all products with their variants (matching admin panel logic)
    // ONLY use products from groupProducts (backend already filtered to same group)
    // Each product in groupProducts is from the same group (same parsedBrand + parsedProductName + classificationName)
    const allProducts: Array<{
      productId: string;
      packageId: string | null;
      productSlug: string | null;
      variationId: string | null;
      originalProductName: string;
      sizeOrWeight: string | null;
      inStock: number;
      price: number;
      unitCost: number | null;
      compareAtPrice?: number | null;
      heroShotUri: string | null;
      color: string | null;
      size: string | null;
      // THC/CBD fields
      thcMin?: number | null;
      thcMax?: number | null;
      thcUom?: string | null;
      thcPercent?: number | null;
      thcMg?: number | null;
      cbdMin?: number | null;
      cbdMax?: number | null;
      cbdUom?: string | null;
      cbdPercent?: number | null;
      cbdMg?: number | null;
      // Effects fields
      specifications?: any | null;
      options?: any | null;
      product: any | null; // Full product object from backend
    }> = [];

    if (groupProducts.length > 0) {
      // ONLY process products from groupProducts - these are all from the SAME GROUP
      // Backend ensures all products in groupProducts share the same grouping key:
      // - For accessories: parsedBrand + parsedProductName + classificationName
      // - For cannabis: masterProductId + parsedBrand + parsedProductName + classificationName
      // This ensures colors/sizes displayed are only from the same grouped products, not other groups
      for (const gp of groupProducts) {
        const originalProductName =
          (gp as any).product?.name || gp.productId || "";
        const sizeOrWeight = (gp as any).sizeOrWeight || null;

        // Extract variants from original product name (matching admin panel)
        const variants = extractVariants(
          originalProductName,
          product.classificationName || null,
          (gp as any).product?.brandName || product.brandName || null,
          sizeOrWeight
        );

        // Find corresponding sizeOption for this groupProduct to get price, productSlug, heroShotUri, etc.
        // Note: sizeOptions groups by size, so we match by productId to get product-specific details
        const matchingSizeOption = product.sizeOptions?.find(
          (opt: any) => opt.productId === gp.productId
        );

        // Use inStock directly from groupProduct (it's the stock for this specific product)
        // groupProducts.inStock is the total stock for this specific product (raw value from backend)
        // The backend doesn't apply stock-1 rule to groupProducts, so we need to check if it's needed
        // Actually, looking at the backend, groupProducts has raw stock, so for client display we might need stock-1
        // But since sizeOptions already has stock-1 applied and we're using groupProducts for variant parsing,
        // we should use the raw stock value here and let the UI handle display logic
        const inStockValue =
          gp.inStock !== undefined && gp.inStock !== null ? gp.inStock : 0;

        // Get product details (THC/CBD/Effects) from the product object
        const productDetails = (gp.product as any) || null;

        // Include all product details from groupProduct
        allProducts.push({
          // Product identifiers
          productId: gp.productId,
          packageId: gp.packageId || null, // Keep null if empty to distinguish from missing value
          productSlug: matchingSizeOption?.productSlug || null,
          variationId: gp.variationId || null,

          // Product info
          originalProductName,
          sizeOrWeight,
          color: variants.color,
          size: variants.size,

          // Stock and pricing
          inStock: inStockValue,
          price: matchingSizeOption?.price ?? gp.price ?? gp.unitCost ?? 0,
          unitCost: gp.unitCost || null,
          compareAtPrice:
            (gp as { compareAtPrice?: number | null }).compareAtPrice ??
            (matchingSizeOption as { compareAtPrice?: number | null } | undefined)
              ?.compareAtPrice ??
            null,

          // Product details
          heroShotUri:
            matchingSizeOption?.heroShotUri ||
            productDetails?.heroShotUri ||
            null,

          // THC/CBD fields from product
          thcMin:
            productDetails?.thcMin !== undefined &&
              productDetails?.thcMin !== null
              ? Number(productDetails.thcMin)
              : null,
          thcMax:
            productDetails?.thcMax !== undefined &&
              productDetails?.thcMax !== null
              ? Number(productDetails.thcMax)
              : null,
          thcUom: productDetails?.thcUom || null,
          thcPercent:
            productDetails?.thcPercent !== undefined &&
              productDetails?.thcPercent !== null
              ? Number(productDetails.thcPercent)
              : null,
          thcMg:
            productDetails?.thcMg !== undefined &&
              productDetails?.thcMg !== null
              ? Number(productDetails.thcMg)
              : null,
          cbdMin:
            productDetails?.cbdMin !== undefined &&
              productDetails?.cbdMin !== null
              ? Number(productDetails.cbdMin)
              : null,
          cbdMax:
            productDetails?.cbdMax !== undefined &&
              productDetails?.cbdMax !== null
              ? Number(productDetails.cbdMax)
              : null,
          cbdUom: productDetails?.cbdUom || null,
          cbdPercent:
            productDetails?.cbdPercent !== undefined &&
              productDetails?.cbdPercent !== null
              ? Number(productDetails.cbdPercent)
              : null,
          cbdMg:
            productDetails?.cbdMg !== undefined &&
              productDetails?.cbdMg !== null
              ? Number(productDetails.cbdMg)
              : null,

          // Effects fields (specifications and options)
          specifications: productDetails?.specifications || null,
          options: productDetails?.options || null,

          product: productDetails, // Include full product object
        });
      }
    } else if (product.sizeOptions && product.sizeOptions.length > 0) {
      // Fallback to sizeOptions if groupProducts not available
      for (const option of product.sizeOptions) {
        let originalProductName =
          (option as any).originalProductName || product.name;

        // Extract variants
        const variants = extractVariants(
          originalProductName,
          product.classificationName || null,
          product.brandName || null,
          option.sizeOrWeight || null
        );

        // Include all product details from sizeOption
        allProducts.push({
          // Product identifiers
          productId: option.productId || "",
          packageId: option.packageId || null,
          productSlug: option.productSlug || null,
          variationId: option.variationId || null,

          // Product info
          originalProductName,
          sizeOrWeight: option.sizeOrWeight || null,
          color: variants.color,
          size: variants.size,

          // Stock and pricing
          inStock: option.inStock || 0,
          price: option.price || 0,
          unitCost: null, // Not available in sizeOptions
          compareAtPrice:
            (option as { compareAtPrice?: number | null }).compareAtPrice ??
            null,

          // Product details
          heroShotUri: option.heroShotUri || null,
          product: null, // Not available in sizeOptions
        });
      }
    }

    if (allProducts.length === 0) {
      return null;
    }

    // Count variant types to determine primary grouping (matching admin panel)
    const variantTypeCounts = {
      color: 0,
      size: 0,
      weight: 0,
    };

    allProducts.forEach((p) => {
      if (p.color) variantTypeCounts.color++;
      if (p.size) variantTypeCounts.size++;
      // Weight detection would go here if needed
    });

    const hasAnyColor = variantTypeCounts.color > 0;
    const hasAnySize = variantTypeCounts.size > 0;

    // Determine primary grouping: color if most common, otherwise size (matching admin panel)
    // All products in allProducts are from the SAME GROUP (filtered by backend)
    const primaryGrouping: "color" | "size" | null =
      variantTypeCounts.color >= variantTypeCounts.size && hasAnyColor
        ? "color"
        : hasAnySize
          ? "size"
          : null;

    // Group by primary variant (color for clothing)
    // IMPORTANT: All products being grouped here are from the same group (same parsedBrand + parsedProductName + classificationName)
    // Colors and sizes displayed will ONLY be from products in the same group, not from other groups
    const variantsByColor = new Map<string, typeof allProducts>();
    const colorsSet = new Set<string>();
    const sizesSet = new Set<string>();

    if (primaryGrouping === "color") {
      // Group by color first (matching admin panel)
      allProducts.forEach((product) => {
        const color = product.color || "Default";
        colorsSet.add(color);
        if (product.size) sizesSet.add(product.size);

        if (!variantsByColor.has(color)) {
          variantsByColor.set(color, []);
        }
        variantsByColor.get(color)!.push(product);
      });
    } else {
      // No color grouping, create a single group
      allProducts.forEach((product) => {
        if (product.size) sizesSet.add(product.size);
      });
      variantsByColor.set("Default", allProducts);
      colorsSet.add("Default");
    }

    const colors = Array.from(colorsSet).sort();
    const sizes = Array.from(sizesSet).sort((a, b) => {
      // Sort sizes: numeric first, then alphabetical
      const aNum = parseFloat(a.replace(/[^0-9.]/g, "")) || 0;
      const bNum = parseFloat(b.replace(/[^0-9.]/g, "")) || 0;
      if (aNum !== 0 || bNum !== 0) {
        return aNum - bNum;
      }
      // Size order: XS, S, M, L, XL, XXL, etc.
      const sizeOrder = [
        "xs",
        "s",
        "m",
        "l",
        "xl",
        "xxl",
        "xxxl",
        "2xl",
        "3xl",
        "4xl",
        "small",
        "medium",
        "large",
        "extra small",
        "extra large",
        "one size",
      ];
      const aIndex = sizeOrder.findIndex((size) =>
        a.toLowerCase().includes(size)
      );
      const bIndex = sizeOrder.findIndex((size) =>
        b.toLowerCase().includes(size)
      );
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });

    // Only return if we have multiple colors (for clothing)
    if (primaryGrouping === "color" && colors.length <= 1) {
      return null;
    }

    // Console log: Show clothing variants structure - for each color, all available sizes
    // Gated to development so it never ships to production PDP views.
    if (process.env.NODE_ENV === "development" && primaryGrouping === "color") {
      console.log("=== Clothing Variants Structure (Processed) ===");
      console.log("Colors:", colors);
      console.log("For each color option, available sizes:");
      for (const color of colors) {
        const colorProducts = variantsByColor.get(color) || [];
        const uniqueSizes = Array.from(
          new Set(
            colorProducts.map((p) => p.size || p.sizeOrWeight || "Default")
          )
        ).filter(Boolean);
        console.log(`  ${color}:`, {
          sizes: uniqueSizes,
          totalProducts: colorProducts.length,
          products: colorProducts, // Show all product details
        });
      }
      console.log("==============================================");
    }

    return {
      colors,
      sizes,
      variantsByColor,
      allProducts,
      primaryGrouping,
    };
  }, [product]);

  // Handle package selection - variant parameter is product slug (not packageId)
  // This effect ensures package selection when product loads
  useEffect(() => {
    if (!data?.success || !data.product) return;

    // If variantParam is present, prioritize it - but wait for clothingVariants to be ready
    // The variantParam handling is done in a separate useEffect that runs after clothingVariants
    if (variantParam && clothingVariants) {
      // Don't override variantParam selection here - let the other useEffect handle it
      return;
    }

    // If no package is selected yet, select oldest available (FIFO with stock-1 rule)
    if (!selectedPackageId) {
      // Check if product has sizeOptions (grouped products)
      if (data.product.sizeOptions && data.product.sizeOptions.length > 0) {
        // Step 1: Filter all products and keep only those with stock >= 1 (backend already applies stock-1 rule)
        const availableProducts =
          data.product.availabilities?.filter(
            (avail: ProductAvailability) => avail.inStock >= 1
          ) || [];

        if (availableProducts.length === 0) {
          // No available products, fallback to first size option (will show out of stock)
          const firstSizeOption = data.product.sizeOptions[0];
          if (firstSizeOption) {
            setSelectedPackageId(
              firstSizeOption.packageId || firstSizeOption.productId || ""
            );
            setSelectedProductId(firstSizeOption.productId || null);
          }
          return;
        }

        // Step 2: Group available products by size/weight
        const sizeGroups = new Map<string, ProductAvailability[]>();

        for (const avail of availableProducts) {
          // Extract productId from availability.id (format: productId::locationId)
          const availProductId = avail.id.split("::")[0];

          // Match by packageId first, then by productId if packageId is empty
          let matchingOpt = null;
          if (avail.packageId && avail.packageId.trim() !== "") {
            matchingOpt = data.product.sizeOptions.find(
              (opt: any) => opt.packageId === avail.packageId
            );
          } else {
            matchingOpt = data.product.sizeOptions.find(
              (opt: any) => opt.productId === availProductId
            );
          }

          if (matchingOpt) {
            const availSize = (
              matchingOpt.sizeOrWeight ||
              matchingOpt.netContent ||
              ""
            )
              .toLowerCase()
              .trim();
            if (!sizeGroups.has(availSize)) {
              sizeGroups.set(availSize, []);
            }
            sizeGroups.get(availSize)!.push(avail);
          }
        }

        // Step 3: Choose the first size/weight that has available products
        let targetSize = "";
        for (const sizeOpt of data.product.sizeOptions) {
          const sizeKey = (sizeOpt.sizeOrWeight || sizeOpt.netContent || "")
            .toLowerCase()
            .trim();
          if (sizeGroups.has(sizeKey) && sizeGroups.get(sizeKey)!.length > 0) {
            targetSize = sizeKey;
            break;
          }
        }

        // Step 4: For the selected size, apply FIFO to find the oldest package (lot)
        if (targetSize && sizeGroups.has(targetSize)) {
          const sameSizePackages = sizeGroups.get(targetSize)!;

          // Filter to only include packages with stock >= 1 (after stock-1 rule)
          const availableSameSizePackages = sameSizePackages.filter(
            (p: ProductAvailability) => p.inStock >= 1
          );

          if (availableSameSizePackages.length > 0) {
            // Sort by createdAt (oldest first) - FIFO logic for multiple packages (lots)
            availableSameSizePackages.sort(
              (a: ProductAvailability, b: ProductAvailability) => {
                const dateA = new Date(a.createdAt).getTime();
                const dateB = new Date(b.createdAt).getTime();
                return dateA - dateB; // Oldest first (FIFO)
              }
            );

            // Select the oldest available package for this size
            const oldestPackage = availableSameSizePackages[0];
            const oldestProductId = oldestPackage.id.split("::")[0];
            // Handle empty packageId by using productId
            setSelectedPackageId(
              oldestPackage.packageId && oldestPackage.packageId.trim() !== ""
                ? oldestPackage.packageId
                : oldestProductId
            );
            setSelectedProductId(oldestProductId);
          } else {
            // No available packages for this size, fallback to first size option (will show out of stock)
            const firstSizeOption = data.product.sizeOptions[0];
            if (firstSizeOption) {
              setSelectedPackageId(
                firstSizeOption.packageId || firstSizeOption.productId || ""
              );
              setSelectedProductId(firstSizeOption.productId || null);
            }
          }
        } else {
          // Fallback: use first available product
          availableProducts.sort(
            (a: ProductAvailability, b: ProductAvailability) => {
              const dateA = new Date(a.createdAt).getTime();
              const dateB = new Date(b.createdAt).getTime();
              return dateA - dateB; // Oldest first (FIFO)
            }
          );
          const firstProduct = availableProducts[0];
          const firstProductId = firstProduct.id.split("::")[0];
          setSelectedPackageId(
            firstProduct.packageId && firstProduct.packageId.trim() !== ""
              ? firstProduct.packageId
              : firstProductId
          );
          setSelectedProductId(firstProductId);
        }
      } else if (data.product.availabilities.length > 0) {
        // Regular products - select oldest available (FIFO, backend already applies stock-1 rule)
        const firstAvailable = data.product.availabilities
          .filter((avail) => avail.inStock >= 1) // Backend already applies stock-1 rule
          .sort((a: ProductAvailability, b: ProductAvailability) => {
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return dateA - dateB; // Oldest first (FIFO)
          })[0];

        if (firstAvailable) {
          setSelectedPackageId(firstAvailable.packageId);
        } else {
          // No available packages, use first one (will show out of stock)
          setSelectedPackageId(data.product.availabilities[0].packageId);
        }
      }
    }
  }, [data, selectedPackageId]);

  // Update URL to use canonical slug (from product name) if different from current
  // Always set variant parameter to product slug for product identification
  useEffect(() => {
    if (!data?.success || !data.product) return;
    // Generate base slug (same for all sizes in a group) - do NOT include sizeOrWeight
    const canonicalSlug = generateProductSlug({
      name: data.product.name, // Use parsed Product Name from backend
      id: data.product.id,
      brandName: data.product.brandName,
      classificationName: data.product.classificationName,
      sizeOrWeight: null, // Base slug should NOT include size/weight (same for all sizes in group)
    });
    if (!canonicalSlug) return;

    // Get product slug (stored slug or generated canonical slug)
    const productSlugValue = data.product.slug || canonicalSlug;

    // Update pathname if canonical slug is different
    const needsPathUpdate = canonicalSlug !== productSlug;

    // Always set variant parameter to product slug (for product identification)
    const params = new URLSearchParams(searchParamsString);
    const currentVariant = params.get("variant");
    const needsVariantUpdate =
      productSlugValue && currentVariant !== productSlugValue;

    if (needsVariantUpdate) {
      params.set("variant", productSlugValue);
    }

    if (needsPathUpdate || needsVariantUpdate) {
      const queryString = params.toString();
      router.replace(
        `/product/${canonicalSlug}${queryString ? `?${queryString}` : ""}`,
        { scroll: false }
      );
    }
  }, [data, productSlug, router, searchParamsString]);

  // Variant parameter is always set to product slug (not packageId)
  // This effect ensures variant is set when package is selected (though variant should already be set)
  // The variant parameter identifies the product, not the specific size/package
  // Skip this effect if we're switching variants (switchVariant already handles URL updates)
  useEffect(() => {
    if (isSwitchingSize.current) return; // Skip if switching variants
    if (!data?.success || !data.product) return;

    const productSlugValue =
      data.product.slug ||
      generateProductSlug({
        name: data.product.name, // Use parsed Product Name from backend
        id: data.product.id,
        brandName: data.product.brandName,
        classificationName: data.product.classificationName,
        sizeOrWeight: data.product.sizeOrWeight,
      });

    if (!productSlugValue) return;

    const params = new URLSearchParams(searchParamsString);
    const currentVariant = params.get("variant");

    // Always ensure variant is set to product slug
    if (currentVariant !== productSlugValue) {
      params.set("variant", productSlugValue);
      const queryString = params.toString();
      router.replace(`${pathname}${queryString ? `?${queryString}` : ""}`, {
        scroll: false,
      });
    }
  }, [data, pathname, router, searchParamsString]);

  const handleAddToCart = () => {
    if (!data?.success || !data.product || !selectedPackageId) return;

    const selectedAvailability = data.product.availabilities.find(
      (avail) => avail.packageId === selectedPackageId
    );
    // Backend already applies stock-1 rule, so check inStock >= 1
    if (!selectedAvailability || selectedAvailability.inStock < 1) return;

    const price =
      data.priceMap[selectedPackageId] ||
      selectedAvailability.price ||
      selectedAvailability.unitCost;

    // Use variant parameter (product slug) for cart identification instead of packageId
    // Base slug (same for all sizes) - do NOT include sizeOrWeight
    const productSlug =
      data.product.slug ||
      generateProductSlug({
        name: data.product.name, // Use parsed Product Name from backend
        id: data.product.id,
        brandName: data.product.brandName,
        classificationName: data.product.classificationName,
        sizeOrWeight: null, // Base slug should NOT include size/weight
      });

    addItem(
      {
        productId: data.product.id,
        name: cleanProductName(data.product.name, data.product.brandName),
        catalogItemId: data.product.id,
        packageId: productSlug,
        price: price,
        actualPackageId: selectedPackageId,
        inStock: selectedAvailability.inStock,
      } as any,
      quantity
    );
  };

  const handleToggleFavorite = async () => {
    const productId = data?.product?.id;
    if (!productId) return;
    if (!user) {
      router.push("/login");
      return;
    }

    try {
      setFavoriteLoading(true);
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(`${base}/api/auth/account/favorites/${productId}`, {
        method: isFavorite ? "DELETE" : "POST",
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
        credentials: "include",
      });
      const json = await response.json();
      if (json?.success) {
        setIsFavorite(!!json.isFavorite);
      }
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Handle empty packageId by using productId or productSlug for matching
  const selectedAvailability = useMemo(() => {
    if (!product) return null;

    // For clothing products with colors, use products[index].inStock directly
    // This is the raw stock value from the colorProducts array (products[index].inStock: 2, 2, 1, 3, 1)
    if (
      clothingVariants &&
      selectedColor &&
      clothingVariants.colors.includes(selectedColor)
    ) {
      const colorProducts =
        clothingVariants.variantsByColor.get(selectedColor) || [];

      // Find matching colorProduct by productId, packageId, or productSlug
      let matchingColorProduct = null;
      if (selectedPackageId && selectedPackageId.trim() !== "") {
        matchingColorProduct = colorProducts.find(
          (p: any) => p.packageId === selectedPackageId
        );
      }
      if (!matchingColorProduct && selectedProductId) {
        matchingColorProduct = colorProducts.find(
          (p: any) => p.productId === selectedProductId
        );
      }
      if (!matchingColorProduct && variantParam) {
        matchingColorProduct = colorProducts.find(
          (p: any) =>
            p.product?.slug === variantParam || p.productSlug === variantParam
        );
      }

      // Use products[index].inStock directly (raw stock from backend: 2, 2, 1, 3, 1)
      if (matchingColorProduct) {
        const rawStock = matchingColorProduct.inStock || 0; // products[index].inStock
        const displayStock = Math.max(0, rawStock - 1); // Apply stock-1 rule: 2 -> 1, 1 -> 0, 3 -> 2
        const packageAvailability = product.availabilities?.find(
          (avail) => avail.packageId === matchingColorProduct.packageId
        );

        return {
          packageId:
            matchingColorProduct.packageId ||
            matchingColorProduct.productId ||
            "",
          inStock: displayStock, // Apply stock-1 rule for quantity limit
          price: matchingColorProduct.price || 0,
          unitCost:
            matchingColorProduct.unitCost || matchingColorProduct.price || 0,
          dominantTerpene: packageAvailability?.dominantTerpene ?? product.dominantTerpene ?? null,
          strain: packageAvailability?.strain ?? product.strain ?? null,
        } as ProductAvailability;
      }
    }

    // Check if product has sizeOptions (grouped products)
    if (product.sizeOptions && product.sizeOptions.length > 0) {
      // When packageId is empty, match by productId or productSlug
      let sizeOption = null;
      if (selectedPackageId && selectedPackageId.trim() !== "") {
        // Try matching by packageId first
        sizeOption = product.sizeOptions.find(
          (opt) => opt.packageId === selectedPackageId
        );
      }

      // If no match by packageId, try matching by productId
      if (!sizeOption && selectedProductId) {
        sizeOption = product.sizeOptions.find(
          (opt) => opt.productId === selectedProductId
        );
      }

      // If still no match, try matching by productSlug (from variant parameter)
      if (!sizeOption && variantParam) {
        sizeOption = product.sizeOptions.find(
          (opt) => opt.productSlug === variantParam
        );
      }

      if (sizeOption) {
        const packageAvailability = product.availabilities?.find(
          (avail) => avail.packageId === (sizeOption.packageId || sizeOption.productId || "")
        );
        // For grouped products, find the matching groupProduct to get raw stock
        // The raw stock value comes from groupProducts (backend response shows inStock: 2, 2, 1, 3, 1)
        const matchingGroupProduct = product.groupProducts?.find(
          (gp: any) => gp.productId === sizeOption.productId
        );

        // Extract raw stock from groupProduct.inStock (this is the product.inStock value from backend)
        // The backend response shows: inStock: 2, 2, 1, 3, 1 - these are the raw stock values
        // We need to extract these exact values from product.groupProducts
        let rawStock = 0;
        if (
          matchingGroupProduct?.inStock !== undefined &&
          matchingGroupProduct?.inStock !== null
        ) {
          rawStock = matchingGroupProduct.inStock; // Raw stock from groupProduct (product.inStock from backend)
        } else {
          // Fallback: search all groupProducts directly (in case productId matching failed)
          // This ensures we extract the exact inStock value from the product data
          const fallbackGroupProduct = product.groupProducts?.find(
            (gp: any) =>
              gp.productId === sizeOption.productId ||
              gp.product?.id === sizeOption.productId ||
              gp.productSlug === sizeOption.productSlug
          );
          if (
            fallbackGroupProduct?.inStock !== undefined &&
            fallbackGroupProduct?.inStock !== null
          ) {
            rawStock = fallbackGroupProduct.inStock; // Extract raw stock from product.inStock
          }
        }

        // Apply stock-1 rule: if raw stock is 2, available stock is 1
        // Examples: 2 -> 1, 1 -> 0, 3 -> 2
        const displayStock = Math.max(0, rawStock - 1);

        return {
          packageId: sizeOption.packageId || sizeOption.productId || "",
          inStock: displayStock, // Apply stock-1 rule for quantity limit
          price: sizeOption.price,
          unitCost: (sizeOption as any).unitCost || sizeOption.price,
          dominantTerpene: packageAvailability?.dominantTerpene ?? product.dominantTerpene ?? null,
          strain: packageAvailability?.strain ?? product.strain ?? null,
        } as ProductAvailability;
      }
    }

    // Fallback to regular availabilities (for non-grouped products)
    // Backend already applies stock-1 to these, so we use them directly
    if (selectedPackageId && selectedPackageId.trim() !== "") {
      return (
        product.availabilities?.find(
          (avail) => avail.packageId === selectedPackageId
        ) || null
      );
    }

    // If packageId is empty, match by productId (extract from availability.id format: productId::locationId)
    if (selectedProductId) {
      return (
        product.availabilities?.find(
          (avail) => avail.id.split("::")[0] === selectedProductId
        ) || null
      );
    }

    return null;
  }, [
    product,
    selectedPackageId,
    selectedProductId,
    variantParam,
    selectedColor,
  ]);

  const currentLotTotalTerpenePercent =
    selectedAvailability?.totalTerpenePercent ?? product?.totalTerpenePercent ?? null;

  const mergedPdpProduct = useMemo((): Product | null => {
    if (!product) return null;
    return selectedAvailability
      ? (mergeCannabisFieldsFromAvailability(
        product,
        selectedAvailability
      ) as Product)
      : product;
  }, [product, selectedAvailability]);

  const pdpSubtitle = useMemo(
    () => (product?.shortDescription ? product.shortDescription.trim() : null),
    [product?.shortDescription]
  );

  const pdpThcBadge = useMemo(
    () => formatThcBadgeFromProduct(mergedPdpProduct),
    [mergedPdpProduct]
  );

  const pdpTerpeneProfile = useMemo(
    () => parseImportedTerpeneProfile(mergedPdpProduct?.flavourTerpeneProfile),
    [mergedPdpProduct?.flavourTerpeneProfile]
  );

  const pdpCannabinoidsLine = useMemo(
    () => formatMinorCannabinoidsLine(mergedPdpProduct),
    [mergedPdpProduct]
  );

  const selectedPrice = useMemo(() => {
    if (!product) return 0;

    // For clothing products with colors, use colorProduct.unitCost directly
    if (
      clothingVariants &&
      selectedColor &&
      clothingVariants.colors.includes(selectedColor)
    ) {
      const colorProducts =
        clothingVariants.variantsByColor.get(selectedColor) || [];

      // Find matching colorProduct by productId, packageId, or productSlug
      let matchingColorProduct = null;
      if (selectedPackageId && selectedPackageId.trim() !== "") {
        matchingColorProduct = colorProducts.find(
          (p: any) => p.packageId === selectedPackageId
        );
      }
      if (!matchingColorProduct && selectedProductId) {
        matchingColorProduct = colorProducts.find(
          (p: any) => p.productId === selectedProductId
        );
      }
      if (!matchingColorProduct && variantParam) {
        matchingColorProduct = colorProducts.find(
          (p: any) =>
            p.product?.slug === variantParam || p.productSlug === variantParam
        );
      }

      if (matchingColorProduct) {
        return resolveSellingPriceFromAvailability(
          matchingColorProduct.price,
          Number(matchingColorProduct.unitCost ?? 0)
        );
      }
    }

    // Check if product has sizeOptions (grouped products)
    if (product.sizeOptions && product.sizeOptions.length > 0) {
      // When packageId is empty, match by productId or productSlug
      let sizeOption = null;
      if (selectedPackageId && selectedPackageId.trim() !== "") {
        sizeOption = product.sizeOptions.find(
          (opt) => opt.packageId === selectedPackageId
        );
      }

      if (!sizeOption && selectedProductId) {
        sizeOption = product.sizeOptions.find(
          (opt) => opt.productId === selectedProductId
        );
      }

      if (!sizeOption && variantParam) {
        sizeOption = product.sizeOptions.find(
          (opt) => opt.productSlug === variantParam
        );
      }

      if (sizeOption) {
        return resolveSellingPriceFromAvailability(
          sizeOption.price,
          Number((sizeOption as any).unitCost ?? 0)
        );
      }
    }

    // Selling price from availability; priceMap when package-level retail is keyed there
    if (!selectedAvailability) return 0;
    const pkgSelling =
      selectedPackageId && selectedPackageId.trim() !== ""
        ? data?.priceMap?.[selectedPackageId]
        : undefined;
    const raw =
      selectedAvailability.price !== null &&
        selectedAvailability.price !== undefined
        ? selectedAvailability.price
        : pkgSelling;
    return resolveSellingPriceFromAvailability(
      raw,
      Number(selectedAvailability.unitCost ?? 0)
    );
  }, [
    product,
    selectedPackageId,
    selectedProductId,
    variantParam,
    selectedAvailability,
    data,
    selectedColor,
  ]);

  // Strikethrough: compare-at above selling price (promo anchor, not unitCost vs price)
  const originalPrice = useMemo(() => {
    if (!product) return null;

    if (
      clothingVariants &&
      selectedColor &&
      clothingVariants.colors.includes(selectedColor)
    ) {
      const colorProducts =
        clothingVariants.variantsByColor.get(selectedColor) || [];

      let matchingColorProduct = null;
      if (selectedPackageId && selectedPackageId.trim() !== "") {
        matchingColorProduct = colorProducts.find(
          (p: any) => p.packageId === selectedPackageId
        );
      }
      if (!matchingColorProduct && selectedProductId) {
        matchingColorProduct = colorProducts.find(
          (p: any) => p.productId === selectedProductId
        );
      }
      if (!matchingColorProduct && variantParam) {
        matchingColorProduct = colorProducts.find(
          (p: any) =>
            p.product?.slug === variantParam || p.productSlug === variantParam
        );
      }

      if (matchingColorProduct) {
        return strikethroughCompareAtPrice(
          selectedPrice,
          matchingColorProduct.compareAtPrice
        );
      }
    }

    if (product.sizeOptions && product.sizeOptions.length > 0) {
      let sizeOption = null;
      if (selectedPackageId && selectedPackageId.trim() !== "") {
        sizeOption = product.sizeOptions.find(
          (opt) => opt.packageId === selectedPackageId
        );
      }

      if (!sizeOption && selectedProductId) {
        sizeOption = product.sizeOptions.find(
          (opt) => opt.productId === selectedProductId
        );
      }

      if (!sizeOption && variantParam) {
        sizeOption = product.sizeOptions.find(
          (opt) => opt.productSlug === variantParam
        );
      }

      if (sizeOption) {
        return strikethroughCompareAtPrice(
          selectedPrice,
          (sizeOption as any).compareAtPrice
        );
      }
    }

    if (selectedAvailability) {
      return strikethroughCompareAtPrice(
        selectedPrice,
        (selectedAvailability as any).compareAtPrice ??
        (product as any).compareAtPrice
      );
    }

    return strikethroughCompareAtPrice(
      selectedPrice,
      (product as any).compareAtPrice
    );
  }, [
    product,
    selectedPackageId,
    selectedProductId,
    variantParam,
    selectedAvailability,
    selectedPrice,
    clothingVariants,
    selectedColor,
  ]);

  // Calculate discount percentage
  const discountPercent = useMemo(() => {
    if (!originalPrice || originalPrice <= selectedPrice) return null;
    const discount = ((originalPrice - selectedPrice) / originalPrice) * 100;
    return Math.round(discount);
  }, [originalPrice, selectedPrice]);

  // Backend already applies stock-1 rule, so check inStock < 1
  const isOutOfStock =
    !selectedAvailability || selectedAvailability.inStock < 1;

  // Get variant-specific description from selected sizeOption (similar to price and stock)
  // This ensures description updates when switching size/weight, just like price and stock
  // Handle empty packageId by using productId or productSlug for matching
  // Helper function to convert RGB array to hex string
  const rgbToHex = (r: number, g: number, b: number): string => {
    return `#${[r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")}`;
  };

  // Helper function to convert color name to CSS color (uses exact same color mapping as admin panel)
  const getColorHex = (colorName: string): string => {
    if (!colorName) return "#000000";

    const colorNameLower = colorName.toLowerCase().trim();

    // Color name to RGB mapping (exact same as admin panel getColorWithOpacity)
    const colorMap: Record<string, [number, number, number]> = {
      pink: [255, 192, 203],
      white: [255, 255, 255],
      yellow: [255, 255, 0],
      red: [255, 0, 0],
      blue: [166, 185, 225],
      green: [0, 128, 0],
      black: [0, 0, 0],
      gray: [128, 128, 128],
      grey: [128, 128, 128],
      purple: [128, 0, 128],
      orange: [255, 165, 0],
      brown: [165, 42, 42],
      beige: [245, 245, 220],
      navy: [0, 0, 128],
      maroon: [128, 0, 0],
      teal: [0, 128, 128],
      coral: [255, 127, 80],
      mint: [189, 252, 201],
      lavender: [230, 230, 250],
      tan: [210, 180, 140],
      khaki: [240, 230, 140],
      olive: [128, 128, 0],
      burgundy: [128, 0, 32],
      charcoal: [54, 69, 79],
      cream: [255, 253, 208],
      ivory: [255, 255, 240],
      peach: [255, 218, 185],
      salmon: [250, 128, 114],
      turquoise: [64, 224, 208],
      violet: [238, 130, 238],
      amber: [255, 191, 0],
      aqua: [0, 255, 255],
      lime: [0, 255, 0],
      magenta: [255, 0, 255],
      cyan: [0, 255, 255],
      indigo: [75, 0, 130],
      fuchsia: [255, 0, 255],
      gold: [255, 215, 0],
      silver: [192, 192, 192],
      bronze: [205, 127, 50],
      copper: [184, 115, 51],
      "dark heather": [105, 105, 105],
      heather: [182, 182, 182],
      "light heather": [211, 211, 211],
      "forest green": [44, 62, 47],
      "dark green": [0, 100, 0],
      "light green": [144, 238, 144],
      emerald: [80, 200, 120],
      sage: [188, 184, 138],
      "mint green": [152, 251, 152],
      "hunter green": [53, 94, 59],
      "army green": [75, 83, 32],
      "dark blue": [0, 0, 139],
      "light blue": [173, 216, 230],
      "sky blue": [135, 206, 235],
      "royal blue": [65, 105, 225],
      "navy blue": [0, 0, 128],
      "powder blue": [176, 224, 230],
      "dark gray": [169, 169, 169],
      "light gray": [211, 211, 211],
      "charcoal gray": [54, 69, 79],
      "dark grey": [169, 169, 169],
      "light grey": [211, 211, 211],
      "charcoal grey": [54, 69, 79],
      "dark brown": [101, 67, 33],
      "light brown": [181, 101, 29],
      chocolate: [123, 63, 0],
      coffee: [111, 78, 55],
      caramel: [255, 213, 154],
      "dark red": [139, 0, 0],
      "light red": [255, 182, 193],
      crimson: [220, 20, 60],
      scarlet: [255, 36, 0],
      "burgundy red": [128, 0, 32],
      "dark pink": [231, 84, 128],
      "light pink": [255, 182, 193],
      "hot pink": [255, 105, 180],
      rose: [255, 0, 127],
      blush: [222, 93, 131],
      "dark purple": [128, 0, 128],
      "light purple": [221, 160, 221],
      "lavender purple": [150, 123, 182],
      "dark orange": [255, 140, 0],
      "light orange": [255, 200, 124],
      "burnt orange": [204, 85, 0],
      "dark yellow": [184, 134, 11],
      "light yellow": [255, 255, 224],
      mustard: [255, 219, 88],
      "dark black": [0, 0, 0],
      "jet black": [20, 20, 20],
      "midnight black": [25, 25, 112],
    };

    // Try exact match first
    if (colorMap[colorNameLower]) {
      const [r, g, b] = colorMap[colorNameLower];
      return rgbToHex(r, g, b);
    }

    // Try partial match (e.g., "dark heather" contains "heather")
    for (const [key, rgb] of Object.entries(colorMap)) {
      if (colorNameLower.includes(key) || key.includes(colorNameLower)) {
        const [r, g, b] = rgb;
        return rgbToHex(r, g, b);
      }
    }

    // Default to black if no match
    return "#000000";
  };

  // NOTE: clothingVariants is now defined earlier (before selectedPrice/originalPrice) to avoid initialization errors
  // See line 1074 for the actual definition

  // Initialize selectedColor and size based on variantParam when product loads
  // This runs after product data and clothingVariants are ready
  useEffect(() => {
    if (!data?.success || !product || !clothingVariants) return;

    // Skip if we've already processed this variantParam
    if (variantParam && processedVariantParam.current === variantParam) return;

    // If variantParam exists, try to match it to a color and size
    if (variantParam) {
      // Mark this variantParam as processed
      processedVariantParam.current = variantParam;

      // First, try to find the matching product in clothingVariants.allProducts
      // Check multiple fields: productSlug, product?.slug, variationId, productId, packageId
      let matchingProduct = clothingVariants.allProducts.find(
        (p) =>
          p.productSlug === variantParam ||
          p.product?.slug === variantParam ||
          p.variationId === variantParam ||
          p.productId === variantParam
      );

      // If not found in allProducts, try to match with sizeOptions
      if (!matchingProduct) {
        const matchingSizeOption = product.sizeOptions?.find(
          (opt: NonNullable<Product["sizeOptions"]>[number]) =>
            opt.productSlug === variantParam ||
            (opt as any).product?.slug === variantParam
        );

        if (matchingSizeOption) {
          // Find the product in clothingVariants.allProducts that matches this sizeOption
          matchingProduct = clothingVariants.allProducts.find(
            (p) =>
              p.productSlug === variantParam ||
              p.product?.slug === variantParam ||
              p.productId === matchingSizeOption.productId ||
              p.packageId === matchingSizeOption.packageId
          );
        }
      }

      // If still not found, try to match with groupProducts
      if (!matchingProduct && product.groupProducts) {
        const matchingGroupProduct = product.groupProducts.find(
          (gp: any) =>
            gp.productSlug === variantParam ||
            gp.product?.slug === variantParam ||
            gp.productId === variantParam ||
            gp.packageId === variantParam
        );

        if (matchingGroupProduct) {
          // Find the product in clothingVariants.allProducts that matches this groupProduct
          matchingProduct = clothingVariants.allProducts.find(
            (p) =>
              p.productId === matchingGroupProduct.productId ||
              p.packageId === matchingGroupProduct.packageId ||
              p.productSlug === variantParam ||
              p.product?.slug === variantParam
          );
        }
      }

      if (matchingProduct) {
        // Get the color from the matching product
        const variantColor = matchingProduct.color;

        // If we found a color and it exists in clothingVariants, set it (only if different)
        if (
          variantColor &&
          clothingVariants.colors.includes(variantColor) &&
          selectedColor !== variantColor
        ) {
          setSelectedColor(variantColor);
        } else if (!variantColor && clothingVariants.colors.length > 0) {
          // If no color found but we have colors, try to find color from variantsByColor
          // Check which color group contains this product
          for (const [
            color,
            products,
          ] of clothingVariants.variantsByColor.entries()) {
            if (
              products.some(
                (p) =>
                  p.productSlug === variantParam ||
                  p.product?.slug === variantParam ||
                  p.productId === matchingProduct.productId ||
                  p.packageId === matchingProduct.packageId
              )
            ) {
              if (selectedColor !== color) {
                setSelectedColor(color);
              }
              break;
            }
          }
        }

        // Set the selected package/product IDs to match the variant (only if different)
        if (
          matchingProduct.packageId &&
          matchingProduct.packageId.trim() !== "" &&
          selectedPackageId !== matchingProduct.packageId
        ) {
          setSelectedPackageId(matchingProduct.packageId);
        }
        if (
          matchingProduct.productId &&
          selectedProductId !== matchingProduct.productId
        ) {
          setSelectedProductId(matchingProduct.productId);
        }

        // Call switchVariant to update product state and URL
        const targetProductSlug =
          matchingProduct.product?.slug || matchingProduct.productSlug;
        if (targetProductSlug && switchVariant) {
          // Create a sizeOption-like object from matchingProduct
          const sizeOption = {
            productId: matchingProduct.productId,
            packageId: matchingProduct.packageId || "",
            sizeOrWeight:
              matchingProduct.sizeOrWeight || matchingProduct.size || null,
            size: matchingProduct.size || matchingProduct.sizeOrWeight || null,
            inStock: matchingProduct.inStock || 0,
            price: matchingProduct.price || 0,
            unitCost: matchingProduct.unitCost || null,
            productSlug: targetProductSlug,
            variationId: matchingProduct.variationId || null,
            heroShotUri:
              matchingProduct.heroShotUri ||
              matchingProduct.product?.heroShotUri ||
              null,
            longDescription: matchingProduct.product?.longDescription || null,
            shortDescription: matchingProduct.product?.shortDescription || null,
            originalProductName: matchingProduct.originalProductName || null,
            product: matchingProduct.product || null,
          };

          switchVariant(targetProductSlug, sizeOption);
        }
      } else {
        // If variantParam doesn't match any product, try to find in sizeOptions as fallback
        const matchingSizeOption = product.sizeOptions?.find(
          (opt: NonNullable<Product["sizeOptions"]>[number]) =>
            opt.productSlug === variantParam ||
            (opt as any).product?.slug === variantParam
        );

        if (matchingSizeOption) {
          // Set package/product IDs even if we can't find color (only if different)
          if (
            matchingSizeOption.packageId &&
            selectedPackageId !== matchingSizeOption.packageId
          ) {
            setSelectedPackageId(matchingSizeOption.packageId);
          }
          if (
            matchingSizeOption.productId &&
            selectedProductId !== matchingSizeOption.productId
          ) {
            setSelectedProductId(matchingSizeOption.productId);
          }

          // Try to find color from sizeOption
          const productFromSizeOption = clothingVariants.allProducts.find(
            (p) =>
              p.productId === matchingSizeOption.productId ||
              p.packageId === matchingSizeOption.packageId
          );

          if (
            productFromSizeOption &&
            productFromSizeOption.color &&
            clothingVariants.colors.includes(productFromSizeOption.color)
          ) {
            if (selectedColor !== productFromSizeOption.color) {
              setSelectedColor(productFromSizeOption.color);
            }
          } else if (
            clothingVariants.colors.length > 0 &&
            selectedColor === null
          ) {
            // Fallback: select first available color
            const availableColors = clothingVariants.colors.filter((color) => {
              const colorProducts =
                clothingVariants.variantsByColor.get(color) || [];
              return colorProducts.some((p) => {
                const displayStock = Math.max(0, (p.inStock || 0) - 1);
                return displayStock > 0;
              });
            });

            if (availableColors.length > 0) {
              setSelectedColor(availableColors[0]);
            }
          }

          // Call switchVariant to update product state and URL
          const targetProductSlug =
            matchingSizeOption.productSlug ||
            (matchingSizeOption as any).product?.slug;
          if (targetProductSlug && switchVariant) {
            switchVariant(targetProductSlug, matchingSizeOption as SizeOption);
          }
        } else if (
          clothingVariants.colors.length > 0 &&
          selectedColor === null
        ) {
          // If variantParam doesn't match anything, select first available color as fallback
          const availableColors = clothingVariants.colors.filter((color) => {
            const colorProducts =
              clothingVariants.variantsByColor.get(color) || [];
            return colorProducts.some((p) => {
              const displayStock = Math.max(0, (p.inStock || 0) - 1);
              return displayStock > 0;
            });
          });

          if (availableColors.length > 0) {
            setSelectedColor(availableColors[0]);
          }
        }
      }
    } else {
      // Reset processed variantParam when variantParam is cleared
      processedVariantParam.current = null;

      // No variantParam - select first available color if not already selected
      if (clothingVariants.colors.length > 0 && selectedColor === null) {
        const availableColors = clothingVariants.colors.filter((color) => {
          const colorProducts =
            clothingVariants.variantsByColor.get(color) || [];
          return colorProducts.some((p) => {
            const displayStock = Math.max(0, (p.inStock || 0) - 1);
            return displayStock > 0;
          });
        });

        if (availableColors.length > 0) {
          setSelectedColor(availableColors[0]);
        }
      }
    }
  }, [data?.success, product, variantParam, clothingVariants, switchVariant]);

  // Initialize selectedColor when clothingVariants changes (fallback if no variantParam)
  // Also auto-select first available size when color is selected
  useEffect(() => {
    if (!clothingVariants || !product || !data?.success) return;

    // If no color selected and we have colors, select first available color
    // This ensures the first available color option is always chosen when the page loads
    if (selectedColor === null && clothingVariants.colors.length > 0) {
      // Only auto-select first available color if no variantParam is specified
      // If variantParam exists, let the other useEffect handle color selection
      if (!variantParam) {
        // Filter to only available colors (with stock >= 2, so stock-1 >= 1)
        const availableColors = clothingVariants.colors.filter((color) => {
          const colorProducts =
            clothingVariants.variantsByColor.get(color) || [];
          return colorProducts.some((p) => {
            const displayStock = Math.max(0, (p.inStock || 0) - 1);
            return displayStock > 0;
          });
        });

        if (availableColors.length > 0) {
          setSelectedColor(availableColors[0]);
        }
      }
    }

    // If color is selected but no size is selected, auto-select first available size for that color
    if (selectedColor && clothingVariants.colors.includes(selectedColor)) {
      const colorProducts =
        clothingVariants.variantsByColor.get(selectedColor) || [];

      // Get size options for this color (same logic as sizeOptionsToDisplay)
      // Create size options directly from colorProducts (no need to match with product.sizeOptions)
      const sizeOptionMap = new Map<string, any>();

      colorProducts.forEach((colorProduct) => {
        // Get size from colorProduct (it has size, sizeOrWeight from allProducts structure)
        const colorProductSize =
          colorProduct.size || colorProduct.sizeOrWeight || null;
        if (!colorProductSize) return;

        // Apply stock-1 rule for display: only show if stock-1 > 0
        const displayStock = Math.max(0, (colorProduct.inStock || 0) - 1);
        if (displayStock <= 0) return; // Skip if stock-1 is not > 0

        // Use values directly from colorProduct (same as sizeOptionsToDisplay)
        const packageId = colorProduct.packageId || "";
        const productSlug =
          colorProduct.product?.slug || colorProduct.productSlug || null;

        // Apply stock-1 rule: if raw stock is 2, available stock is 1
        const rawStock = colorProduct.inStock || 0;
        const stockValue = Math.max(0, rawStock - 1);

        const sizeOption = {
          productId: colorProduct.productId,
          packageId: packageId,
          sizeOrWeight: colorProductSize,
          size:
            colorProduct.size || colorProduct.sizeOrWeight || colorProductSize,
          inStock: stockValue,
          price: colorProduct.price || 0,
          unitCost: colorProduct.unitCost || null,
          productSlug: productSlug,
          variationId: colorProduct.variationId || null,
          heroShotUri:
            colorProduct.product?.heroShotUri ||
            colorProduct.heroShotUri ||
            null,
          longDescription:
            colorProduct.product?.longDescription ||
            (colorProduct as any).longDescription ||
            null,
          shortDescription:
            colorProduct.product?.shortDescription ||
            (colorProduct as any).shortDescription ||
            null,
          originalProductName: colorProduct.originalProductName || null,
          product: colorProduct.product || null,
        };

        // Use same uniqueKey logic as sizeOptionsToDisplay
        const uniqueKey =
          packageId && String(packageId).trim() !== ""
            ? `${String(packageId)}-${String(sizeOption.sizeOrWeight || "")}`
            : productSlug
              ? `${String(productSlug)}-${String(sizeOption.sizeOrWeight || "")}`
              : String(colorProductSize).toLowerCase().trim();

        // Only add if we don't already have this unique key, or if this one is in stock and the existing one isn't
        if (!sizeOptionMap.has(uniqueKey)) {
          sizeOptionMap.set(uniqueKey, sizeOption);
        } else {
          const existing = sizeOptionMap.get(uniqueKey);
          if (sizeOption.inStock > 0 && existing.inStock < 1) {
            sizeOptionMap.set(uniqueKey, sizeOption);
          }
        }
      });

      const sizeOptionsForColor = Array.from(sizeOptionMap.values()).sort(
        (a, b) => {
          const aSize = a.sizeOrWeight || a.size || "";
          const bSize = b.sizeOrWeight || b.size || "";
          const aNum = parseFloat(String(aSize).replace(/[^0-9.]/g, "")) || 0;
          const bNum = parseFloat(String(bSize).replace(/[^0-9.]/g, "")) || 0;
          if (aNum !== 0 || bNum !== 0) return aNum - bNum;
          const sizeOrder = [
            "xs",
            "s",
            "m",
            "l",
            "xl",
            "xxl",
            "xxxl",
            "2xl",
            "3xl",
            "4xl",
            "small",
            "medium",
            "large",
            "extra small",
            "extra large",
            "one size",
          ];
          const aLower = String(aSize).toLowerCase();
          const bLower = String(bSize).toLowerCase();
          const aIndex = sizeOrder.findIndex((size) => aLower.includes(size));
          const bIndex = sizeOrder.findIndex((size) => bLower.includes(size));
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return aLower.localeCompare(bLower);
        }
      );

      // Auto-select first available size if no size is currently selected
      if (
        sizeOptionsForColor.length > 0 &&
        !selectedPackageId &&
        !selectedProductId &&
        !variantParam
      ) {
        const firstSizeOption = sizeOptionsForColor[0];
        if (
          firstSizeOption.packageId &&
          firstSizeOption.packageId.trim() !== ""
        ) {
          setSelectedPackageId(firstSizeOption.packageId);
        }
        if (firstSizeOption.productId) {
          setSelectedProductId(firstSizeOption.productId);
        }

        // Update variant and product details
        const targetProductSlug =
          firstSizeOption.product?.slug || firstSizeOption.productSlug;
        if (targetProductSlug && data?.success && data.product) {
          // Use switchVariant logic directly since it depends on data
          isSwitchingSize.current = true;

          const productDetails = firstSizeOption.product || null;
          const updatedProduct: Product = {
            ...data.product,
            longDescription:
              productDetails?.longDescription ??
              firstSizeOption.longDescription ??
              data.product.longDescription ??
              null,
            heroShotUri:
              productDetails?.heroShotUri ??
              firstSizeOption.heroShotUri ??
              data.product.heroShotUri ??
              null,
            shortDescription:
              productDetails?.shortDescription ??
              firstSizeOption.shortDescription ??
              data.product.shortDescription ??
              null,
            slug: targetProductSlug,
            ...(productDetails
              ? {
                thcMin:
                  productDetails.thcMin !== undefined &&
                    productDetails.thcMin !== null
                    ? Number(productDetails.thcMin)
                    : data.product.thcMin,
                thcMax:
                  productDetails.thcMax !== undefined &&
                    productDetails.thcMax !== null
                    ? Number(productDetails.thcMax)
                    : data.product.thcMax,
                thcUom: productDetails.thcUom || data.product.thcUom,
                thcPercent:
                  productDetails.thcPercent !== undefined &&
                    productDetails.thcPercent !== null
                    ? Number(productDetails.thcPercent)
                    : data.product.thcPercent,
                thcMg:
                  productDetails.thcMg !== undefined &&
                    productDetails.thcMg !== null
                    ? Number(productDetails.thcMg)
                    : data.product.thcMg,
                cbdMin:
                  productDetails.cbdMin !== undefined &&
                    productDetails.cbdMin !== null
                    ? Number(productDetails.cbdMin)
                    : data.product.cbdMin,
                cbdMax:
                  productDetails.cbdMax !== undefined &&
                    productDetails.cbdMax !== null
                    ? Number(productDetails.cbdMax)
                    : data.product.cbdMax,
                cbdUom: productDetails.cbdUom || data.product.cbdUom,
                cbdPercent:
                  productDetails.cbdPercent !== undefined &&
                    productDetails.cbdPercent !== null
                    ? Number(productDetails.cbdPercent)
                    : data.product.cbdPercent,
                cbdMg:
                  productDetails.cbdMg !== undefined &&
                    productDetails.cbdMg !== null
                    ? Number(productDetails.cbdMg)
                    : data.product.cbdMg,
                specifications:
                  productDetails.specifications ||
                  data.product.specifications,
                options: productDetails.options || data.product.options,
              }
              : {}),
            sizeOptions: data.product.sizeOptions,
            availabilities: data.product.availabilities,
            groupProducts: data.product.groupProducts,
          };

          setData({
            ...data,
            product: updatedProduct,
          });

          const canonicalSlug = generateProductSlug({
            name: data.product.name,
            id: data.product.id,
            brandName: data.product.brandName,
            classificationName: data.product.classificationName,
            sizeOrWeight: null,
          });

          if (canonicalSlug) {
            const params = new URLSearchParams();
            params.set("variant", targetProductSlug);
            const newUrl = `/product/${canonicalSlug}?${params.toString()}`;
            router.replace(newUrl, { scroll: false });
          }

          setTimeout(() => {
            isSwitchingSize.current = false;
          }, 300);
        }
      }
    }
  }, [
    clothingVariants,
    selectedColor,
    variantParam,
    product,
    selectedPackageId,
    selectedProductId,
    data,
    router,
  ]);

  const displayDescription = useMemo(() => {
    if (!product) return null;

    // Check if product has sizeOptions (grouped products)
    if (product.sizeOptions && product.sizeOptions.length > 0) {
      // When packageId is empty, match by productId or productSlug
      let sizeOption = null;
      if (selectedPackageId && selectedPackageId.trim() !== "") {
        sizeOption = product.sizeOptions.find(
          (opt) => opt.packageId === selectedPackageId
        );
      }

      if (!sizeOption && selectedProductId) {
        sizeOption = product.sizeOptions.find(
          (opt) => opt.productId === selectedProductId
        );
      }

      if (!sizeOption && variantParam) {
        sizeOption = product.sizeOptions.find(
          (opt) => opt.productSlug === variantParam
        );
      }

      // Use sizeOption's description if available, otherwise fallback to product's description
      if (sizeOption) {
        return sizeOption.longDescription || product.longDescription || null;
      }
    }

    // Fallback to product's longDescription (for non-grouped products or if no match found)
    return product.longDescription || null;
  }, [product, selectedPackageId, selectedProductId, variantParam]);

  const strain =
    (selectedAvailability?.strain as string | undefined) ??
    product?.strain ??
    "";
  // Use parsed Product Name from backend (product.name is now the parsed Product Name)
  const displayProductName = product?.name || "";
  const brand = product
    ? extractBrandName(displayProductName, product.brandName) || ""
    : "";
  const classification = product?.classificationName || "";

  // "Shop all" CTA for the recommendations block: targets the current
  // product's category, or all products when it has none. The label names the
  // destination so the button is self-explanatory rather than a bare "Shop all".
  const shopAllHref = classification
    ? `/search?category=${encodeURIComponent(classification)}`
    : "/search";
  const shopAllLabel = classification ? `Shop all ${classification}` : "Shop all products";

  // Related products that have at least one in-stock size, each with its
  // display price resolved once. This single derived list drives both the grid
  // and the empty state, so a fully out-of-stock recommendation set can never
  // render an empty grid with no message.
  const visibleRelatedProducts = useMemo(() => {
    return relatedProducts
      .map((item: any) => ({
        ...item,
        sizeOptions: Array.isArray(item.sizeOptions)
          ? item.sizeOptions.filter((opt: any) => opt.inStock >= 1)
          : undefined,
      }))
      .filter((item: any) =>
        item.sizeOptions ? item.sizeOptions.length > 0 : item.inStock >= 1,
      )
      .map((item: any) => {
        const price =
          relatedProductsPriceMap[item.productId] ||
          (item.sizeOptions && item.sizeOptions.length > 0
            ? item.sizeOptions[0].price
            : item.unitCost || 0);
        return { item, price: typeof price === "number" ? price : 0 };
      });
  }, [relatedProducts, relatedProductsPriceMap]);

  // Check if product is an accessory (accessories don't have strains, THC, CBD, terpenes)
  const isAccessory = product
    ? !isCannabisProduct(product.classificationName)
    : false;
  const showCannabisDetailSections = !isAccessory;

  // Parse selected package variant details
  const getSelectedPackageDetails = () => {
    if (!selectedPackageId || !product) return null;

    // Try to parse from packageId (e.g., "10x0.35g" or "3x0.75g")
    const packageMatch = selectedPackageId.match(/^(\d+)x([\d.]+)g?$/i);
    if (packageMatch) {
      const quantity = parseInt(packageMatch[1], 10);
      const weightPerUnit = parseFloat(packageMatch[2]);
      const totalWeight = quantity * weightPerUnit;
      return {
        quantity,
        weightPerUnit,
        totalWeight,
        netContent: `${quantity}x${weightPerUnit}g`,
        pieces: quantity,
      };
    }

    // Fallback: use product-level data
    return {
      quantity: product.pieces || null,
      weightPerUnit: null,
      totalWeight: null,
      netContent: product.netContent || null,
      pieces: product.pieces || null,
    };
  };

  const selectedPackageDetails = getSelectedPackageDetails();

  // Determine product type
  const getProductType = () => {
    if (classification) {
      const lower = classification.toLowerCase();
      if (lower.includes("pre-roll") || lower.includes("preroll"))
        return "Pre-Roll";
      if (lower.includes("flower")) return "Flower";
      if (lower.includes("vape") || lower.includes("cartridge")) return "Vapes";
      if (lower.includes("edible")) return "Edibles";
      if (lower.includes("concentrate")) return "Concentrates";
    }
    return classification || "Cannabis";
  };

  const productType = getProductType();

  // Get strain type (Sativa, Indica, Hybrid) - Matching backend filtering logic
  const getStrainType = () => {
    if (!strain) return "";

    const lower = strain.toLowerCase().trim();

    // Direct matches for strain type keywords
    if (lower === "sativa" || lower === "indica" || lower === "hybrid") {
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    }

    // Check if strain name contains type keywords
    if (
      lower.includes("sativa") &&
      !lower.includes("indica") &&
      !lower.includes("hybrid")
    ) {
      return "Sativa";
    }
    if (
      lower.includes("indica") &&
      !lower.includes("sativa") &&
      !lower.includes("hybrid")
    ) {
      return "Indica";
    }
    if (lower.includes("hybrid")) {
      return "Hybrid";
    }

    // Check for both sativa and indica (hybrid)
    if (lower.includes("sativa") && lower.includes("indica")) {
      return "Hybrid";
    }

    // Known Sativa-dominant strains (matching backend logic)
    const sativaStrains = [
      "sour diesel",
      "jack herer",
      "durban poison",
      "green crack",
      "super silver haze",
      "trainwreck",
      "strawberry cough",
      "ak-47",
      "white widow",
      "blue dream",
      "pineapple express",
      "amnesia haze",
      "lemon haze",
      "tangie",
      "chocolope",
    ];

    // Known Indica-dominant strains (matching backend logic)
    const indicaStrains = [
      "granddaddy purple",
      "northern lights",
      "bubba kush",
      "purple kush",
      "afghan kush",
      "og kush",
      "blueberry",
      "grape ape",
      "blackberry kush",
      "master kush",
      "hindu kush",
      "purple urkle",
      "god's gift",
      "bubba",
      "purple",
    ];

    // Check against known strain lists
    for (const sativaStrain of sativaStrains) {
      if (lower.includes(sativaStrain)) {
        return "Sativa";
      }
    }

    for (const indicaStrain of indicaStrains) {
      if (lower.includes(indicaStrain)) {
        return "Indica";
      }
    }

    // If we can't determine but have a strain value, default to Hybrid
    // (most modern strains are hybrids anyway)
    return "Hybrid";
  };

  const strainType = getStrainType();

  const structuredDataPotencySource = useMemo(() => {
    if (!product) return null;
    return selectedAvailability
      ? mergeCannabisFieldsFromAvailability(product, selectedAvailability)
      : product;
  }, [product, selectedAvailability]);

  const schemaThcLabel = schemaCannabinoidPercentLabel(structuredDataPotencySource, "thc");
  const schemaCbdLabel = schemaCannabinoidPercentLabel(structuredDataPotencySource, "cbd");

  // Generate structured data (JSON-LD) for SEO
  // Use parsed Product Name from backend for slug generation (product.name is now the parsed Product Name)
  const productNameForSlug = product?.name || "";
  const canonicalSlug = product
    ? generateProductSlug({
      name: productNameForSlug,
      id: product.id,
      brandName: product.brandName,
      classificationName: product.classificationName,
      sizeOrWeight: null, // Base slug should NOT include size/weight (same for all sizes)
    })
    : productSlug;

  const productSlugValue = product?.slug || canonicalSlug || null;
  const productSku = product?.catalogSku || product?.vendorSku || null;

  // Enhanced structured data for rich snippets
  const structuredData =
    data?.success && product
      ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name:
          product.name || cleanProductName(product.name, product.brandName), // Use parsed Product Name from backend
        description:
          product.seoDescription ||
          product.shortDescription ||
          product.longDescription ||
          `${product.name || cleanProductName(product.name, product.brandName)
          } - Premium cannabis product available at Dank`,
        brand: brand
          ? {
            "@type": "Brand",
            name: brand,
          }
          : undefined,
        image: product.heroShotUri
          ? [
            product.heroShotUri,
            ...(getProductMedia(product)
              .filter((m) => m.type === "image")
              .map((m) => m.url) || []),
          ]
          : undefined,
        offers: {
          "@type": "Offer",
          url: `${process.env.NEXT_PUBLIC_SITE_URL || "https://dank.ca"
            }/product/${canonicalSlug}`,
          priceCurrency: "CAD",
          price: selectedPrice > 0 ? selectedPrice.toFixed(2) : undefined,
          availability: isOutOfStock
            ? "https://schema.org/OutOfStock"
            : "https://schema.org/InStock",
          priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0], // 1 year from now
          seller: {
            "@type": "Organization",
            name: "Dank",
            url: process.env.NEXT_PUBLIC_SITE_URL || "https://dank.ca",
          },
          itemCondition: "https://schema.org/NewCondition",
        },
        sku: productSku || selectedPackageId,
        gtin: product.upcs?.[0]?.upc,
        mpn: product.vendorSku || product.catalogSku,
        category: product.classificationName || "Cannabis",
        aggregateRating: undefined, // Can be added when reviews are implemented
        review: undefined, // Can be added when reviews are implemented
        additionalProperty: [
          ...(schemaThcLabel
            ? [
              {
                "@type": "PropertyValue",
                name: "THC",
                value: schemaThcLabel,
              },
            ]
            : []),
          ...(schemaCbdLabel
            ? [
              {
                "@type": "PropertyValue",
                name: "CBD",
                value: schemaCbdLabel,
              },
            ]
            : []),
          ...(structuredDataPotencySource?.strain
            ? [
              {
                "@type": "PropertyValue",
                name: "Strain",
                value: String(structuredDataPotencySource.strain),
              },
            ]
            : []),
          ...(structuredDataPotencySource?.dominantTerpene
            ? [
              {
                "@type": "PropertyValue",
                name: "Dominant Terpene",
                value: String(structuredDataPotencySource.dominantTerpene),
              },
            ]
            : []),
          ...(product.netContent
            ? [
              {
                "@type": "PropertyValue",
                name: "Net Content",
                value: product.netContent,
              },
            ]
            : []),
        ],
      }
      : null;

  // Breadcrumb structured data
  const breadcrumbData =
    data?.success && product
      ? (() => {
        // Map database classificationName to display label (same as breadcrumb display)
        const getCategoryDisplayLabel = (
          classificationName: string | null | undefined
        ): string => {
          if (!classificationName) return "Products";
          const categoryMap: Record<string, string> = {
            Flower: "Flowers",
            "Pre-Roll": "Pre-rolls",
            Extract: "Extracts",
            Topical: "Topicals",
            Vaporizer: "Vaporizers",
            Edible: "Edibles",
            Tincture: "Tinctures",
            Accessories: "Accessories",
            Clothing: "Dank Merch",
          };
          return categoryMap[classificationName] || classificationName;
        };

        const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || "https://dank.ca";
        const items = [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: baseUrl,
          },
        ];

        // Add category breadcrumb if classification exists
        if (product.classificationName) {
          items.push({
            "@type": "ListItem",
            position: 2,
            name: getCategoryDisplayLabel(product.classificationName),
            item: `${baseUrl}/search?category=${encodeURIComponent(
              product.classificationName
            )}`,
          });
        }

        // Add product breadcrumb
        items.push({
          "@type": "ListItem",
          position: items.length + 1,
          name: cleanProductName(product.name, product.brandName),
          item: `${baseUrl}/product/${canonicalSlug}`,
        });

        return {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: items,
        };
      })()
      : null;

  // Organization structured data
  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Dank",
    url: process.env.NEXT_PUBLIC_SITE_URL || "https://dank.ca",
    logo: `${process.env.NEXT_PUBLIC_SITE_URL || "https://dank.ca"}/logo.png`,
    sameAs: [
      // Add social media links if available
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Service",
      areaServed: "CA",
      availableLanguage: "English",
    },
  };

  // Calculate THC percentage based on UOM (same logic as ProductCard)
  const calculateTHCPercent = (
    value: number | undefined,
    uom: string
  ): number | null => {
    if (value === undefined || value === null || !product) return null;

    const lowerUom = uom.toLowerCase().trim();

    // If already in percentage, return as is
    if (lowerUom === "%" || lowerUom === "percent") {
      return value;
    }

    // mg/g: THC % = THC (mg/g) ÷ 10
    if (lowerUom === "mg/g" || lowerUom === "mg per g" || lowerUom === "mg/g") {
      return value / 10;
    }

    // mg: THC % = (THC mg / product weight in mg) * 100
    if (lowerUom === "mg") {
      let weightInMg: number | null = null;

      // Use selected package details if available
      if (selectedPackageDetails?.totalWeight) {
        weightInMg = selectedPackageDetails.totalWeight * 1000;
      } else if (selectedPackageDetails?.netContent) {
        const netContentLower = selectedPackageDetails.netContent.toLowerCase();
        if (netContentLower.includes("g")) {
          const grams = parseFloat(netContentLower.replace(/[^0-9.]/g, ""));
          if (!isNaN(grams)) weightInMg = grams * 1000;
        } else if (netContentLower.includes("mg")) {
          const mg = parseFloat(netContentLower.replace(/[^0-9.]/g, ""));
          if (!isNaN(mg)) weightInMg = mg;
        }
      }

      // Fallback to product-level netContent
      if (weightInMg === null && product.netContent) {
        const netContentLower = product.netContent.toLowerCase();
        if (netContentLower.includes("g")) {
          const grams = parseFloat(netContentLower.replace(/[^0-9.]/g, ""));
          if (!isNaN(grams)) weightInMg = grams * 1000;
        } else if (netContentLower.includes("mg")) {
          const mg = parseFloat(netContentLower.replace(/[^0-9.]/g, ""));
          if (!isNaN(mg)) weightInMg = mg;
        }
      }

      if (
        weightInMg === null &&
        product.equivalenceDriedFlowerG !== undefined &&
        product.equivalenceDriedFlowerG !== null
      ) {
        weightInMg = Number(product.equivalenceDriedFlowerG) * 1000;
      }
      if (weightInMg !== null && weightInMg > 0) {
        return (value / weightInMg) * 100;
      }
      return null;
    }

    // mg/unit: THC % = (mg per unit / unit weight in mg) * 100
    if (lowerUom === "mg/unit" || lowerUom === "mg per unit") {
      return null;
    }

    return null;
  };

  // Get product data for selected color variant (if color is selected)
  // The color product has THC/CBD fields directly in the object (from allProducts structure)
  const selectedColorProduct = useMemo(() => {
    if (!clothingVariants || !selectedColor || !product) return null;

    const colorProducts =
      clothingVariants.variantsByColor.get(selectedColor) || [];
    if (colorProducts.length === 0) return null;

    // Get the matching product from the selected color (or match by selected size if available)
    // Use products[index].product for THC/CBD values (products[index].product.thcMin, etc.)
    const matchingProduct =
      colorProducts.find((p) => {
        if (selectedPackageId && p.packageId === selectedPackageId) return true;
        if (selectedProductId && p.productId === selectedProductId) return true;
        if (
          variantParam &&
          (p.product?.slug === variantParam || p.productSlug === variantParam)
        )
          return true;
        return false;
      }) || colorProducts[0];

    // Return the matching colorProduct (which has product.product with THC/CBD values)
    return matchingProduct;
  }, [
    clothingVariants,
    selectedColor,
    selectedPackageId,
    selectedProductId,
    variantParam,
    product,
  ]);

  // Format THC range - returns exact values from backend: "thcMin ~ thcMax" format with UOM
  // Uses selected color product if available, otherwise uses main product
  const formatTHCRange = (): string | null => {
    // Check if we have a selected color product with THC data
    // Use products[index].product for THC/CBD values (products[index].product.thcMin, etc.)
    let productToUse: any = null;
    if (selectedColorProduct) {
      // Use products[index].product for THC/CBD values
      // selectedColorProduct.product contains the full product object with THC/CBD values
      productToUse = selectedColorProduct.product || selectedColorProduct;
    } else {
      // Fallback to main product
      productToUse = product;
    }

    if (!productToUse) return null;

    if (selectedAvailability) {
      productToUse = mergeCannabisFieldsFromAvailability(
        productToUse,
        selectedAvailability
      );
    }

    // Prioritize thcMin/thcMax over thcPercent
    // Use products[index].product.thcMin, products[index].product.thcMax, etc.
    const thcMin =
      productToUse.thcMin !== undefined && productToUse.thcMin !== null
        ? productToUse.thcMin
        : null;
    const thcMax =
      productToUse.thcMax !== undefined && productToUse.thcMax !== null
        ? productToUse.thcMax
        : null;
    const thcUom = productToUse.thcUom || "";

    // Format as "thcMin ~ thcMax" if both values exist
    const minValue =
      thcMin !== undefined && thcMin !== null && !isNaN(Number(thcMin))
        ? Number(thcMin)
        : null;
    const maxValue =
      thcMax !== undefined && thcMax !== null && !isNaN(Number(thcMax))
        ? Number(thcMax)
        : null;

    if (minValue !== null && maxValue !== null) {
      // Always show as "min ~ max" format when both values exist, with UOM
      const uom = thcUom ? ` ${thcUom}` : "";
      return `${minValue} ~ ${maxValue}${uom}`;
    } else if (minValue !== null) {
      const uom = thcUom ? ` ${thcUom}` : "";
      return `${minValue}${uom}`;
    } else if (maxValue !== null) {
      const uom = thcUom ? ` ${thcUom}` : "";
      return `${maxValue}${uom}`;
    }

    const thcMgLab =
      productToUse.thcMg !== undefined && productToUse.thcMg !== null
        ? Number(productToUse.thcMg)
        : productToUse.product?.thcMg !== undefined &&
          productToUse.product?.thcMg !== null
          ? Number(productToUse.product.thcMg)
          : null;
    if (
      thcMgLab !== null &&
      !Number.isNaN(thcMgLab) &&
      thcMgLab > 0
    ) {
      return `${(thcMgLab / 10).toFixed(1)}%`;
    }

    // Fallback to thcPercent only if min/max are not available
    const thcPercent =
      productToUse.thcPercent ?? productToUse.product?.thcPercent;
    if (
      thcPercent !== undefined &&
      thcPercent !== null &&
      !isNaN(Number(thcPercent))
    ) {
      const percent = Number(thcPercent);
      if (percent > 0) {
        return `${percent}`;
      }
    }

    // Return null if no valid data available
    return null;
  };

  const thcRange = formatTHCRange();

  // Format CBD range - returns exact values from backend: "cbdMin ~ cbdMax" format with UOM
  // Uses selected color product if available, otherwise uses main product
  const formatCBDRange = (): string | null => {
    // Check if we have a selected color product with CBD data
    // Use products[index].product for THC/CBD values (products[index].product.cbdMin, etc.)
    let productToUse: any = null;
    if (selectedColorProduct) {
      // Use products[index].product for THC/CBD values
      // selectedColorProduct.product contains the full product object with THC/CBD values
      productToUse = selectedColorProduct.product || selectedColorProduct;
    } else {
      // Fallback to main product
      productToUse = product;
    }

    if (!productToUse) return null;

    if (selectedAvailability) {
      productToUse = mergeCannabisFieldsFromAvailability(
        productToUse,
        selectedAvailability
      );
    }

    // Prioritize cbdMin/cbdMax over cbdPercent
    // Use products[index].product.cbdMin, products[index].product.cbdMax, etc.
    const cbdMin =
      productToUse.cbdMin !== undefined && productToUse.cbdMin !== null
        ? productToUse.cbdMin
        : null;
    const cbdMax =
      productToUse.cbdMax !== undefined && productToUse.cbdMax !== null
        ? productToUse.cbdMax
        : null;
    const cbdUom = productToUse.cbdUom || "";

    // Format as "cbdMin ~ cbdMax" if both values exist
    const minValue =
      cbdMin !== undefined && cbdMin !== null && !isNaN(Number(cbdMin))
        ? Number(cbdMin)
        : null;
    const maxValue =
      cbdMax !== undefined && cbdMax !== null && !isNaN(Number(cbdMax))
        ? Number(cbdMax)
        : null;

    if (minValue !== null && maxValue !== null) {
      // Always show as "min ~ max" format when both values exist, with UOM
      const uom = cbdUom ? ` ${cbdUom}` : "";
      return `${minValue} ~ ${maxValue}${uom}`;
    } else if (minValue !== null) {
      const uom = cbdUom ? ` ${cbdUom}` : "";
      return `${minValue}${uom}`;
    } else if (maxValue !== null) {
      const uom = cbdUom ? ` ${cbdUom}` : "";
      return `${maxValue}${uom}`;
    }

    const cbdMgLab =
      productToUse.cbdMg !== undefined && productToUse.cbdMg !== null
        ? Number(productToUse.cbdMg)
        : productToUse.product?.cbdMg !== undefined &&
          productToUse.product?.cbdMg !== null
          ? Number(productToUse.product.cbdMg)
          : null;
    if (
      cbdMgLab !== null &&
      !Number.isNaN(cbdMgLab) &&
      cbdMgLab > 0
    ) {
      return `${(cbdMgLab / 10).toFixed(1)}%`;
    }

    // Fallback to cbdPercent only if min/max are not available
    const cbdPercent =
      productToUse.cbdPercent ?? productToUse.product?.cbdPercent;
    if (
      cbdPercent !== undefined &&
      cbdPercent !== null &&
      !isNaN(Number(cbdPercent))
    ) {
      const percent = Number(cbdPercent);
      if (percent > 0) {
        return `${percent}`;
      }
    }

    // Return null if no valid data available
    return null;
  };

  const cbdRange = formatCBDRange();

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // Generate SEO-optimized title and description using ruleset
  // Update when product, variant, or selected package changes
  const productTitle = useMemo(() => {
    if (!data?.product) return "Product | Dank";

    // Get variant-specific size/weight if available
    let variantSizeOrWeight = data.product.sizeOrWeight;
    if (selectedPackageId && data.product.sizeOptions) {
      const selectedSizeOption = data.product.sizeOptions.find(
        (opt) => opt.packageId === selectedPackageId
      );
      if (selectedSizeOption) {
        variantSizeOrWeight =
          selectedSizeOption.sizeOrWeight ||
          selectedSizeOption.netContent ||
          variantSizeOrWeight;
      }
    }

    // Use parsed Product Name from backend (product.name is now the parsed Product Name)
    const displayName = data.product.name;

    return generateProductSEOTitle({
      name: displayName,
      brandName: data.product.brandName,
      classificationName: data.product.classificationName,
      sizeOrWeight: variantSizeOrWeight,
      seoTitle: data.product.seoTitle,
    });
  }, [data?.product, data?.product?.seoTitle, selectedPackageId, variantParam]);

  const metaDescription = useMemo(() => {
    if (!data?.product)
      return "Shop premium cannabis products online from Dank, Alberta's trusted cannabis retailer.";

    // Get variant-specific size/weight if available
    let variantSizeOrWeight = data.product.sizeOrWeight;
    if (selectedPackageId && data.product.sizeOptions) {
      const selectedSizeOption = data.product.sizeOptions.find(
        (opt) => opt.packageId === selectedPackageId
      );
      if (selectedSizeOption) {
        variantSizeOrWeight =
          selectedSizeOption.sizeOrWeight ||
          selectedSizeOption.netContent ||
          variantSizeOrWeight;
      }
    }

    // Use parsed Product Name from backend (product.name is now the parsed Product Name)
    const displayName = data.product.name;

    // Use shortDescription if available (better for SEO)
    // Get variant-specific description if available
    const variantShortDescription =
      selectedPackageId && data.product.sizeOptions
        ? data.product.sizeOptions.find(
          (opt) => opt.packageId === selectedPackageId
        )?.shortDescription || data.product.shortDescription
        : data.product.shortDescription;

    if (variantShortDescription && !data.product.seoDescription) {
      const parsed = parseProduct(
        displayName,
        data.product.classificationName || null,
        data.product.brandName || null,
        variantSizeOrWeight || null
      );
      const baseDesc = generateSEODescription(
        parsed,
        data.product.seoDescription
      );
      // Combine with short description if it adds value
      const combined = `${baseDesc} ${variantShortDescription}`.substring(
        0,
        160
      );
      return combined;
    }

    return generateProductSEODescription({
      name: displayName,
      brandName: data.product.brandName,
      classificationName: data.product.classificationName,
      sizeOrWeight: variantSizeOrWeight,
      seoDescription: data.product.seoDescription,
    }).substring(0, 160); // Limit to 160 chars for SEO
  }, [
    data?.product,
    data?.product?.seoDescription,
    data?.product?.shortDescription,
    selectedPackageId,
    variantParam,
  ]);

  // Update document title and meta tags for SEO
  // This effect runs whenever productTitle or metaDescription changes (which includes selectedPackageId/variantParam changes)
  useEffect(() => {
    if (!data?.product) return;

    // Update document title (this will update when productTitle changes)
    document.title = productTitle;

    // Update or create meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", metaDescription);

    // Update or create Open Graph tags
    const updateOrCreateMeta = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("property", property);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    updateOrCreateMeta("og:title", productTitle);
    updateOrCreateMeta("og:description", metaDescription);
    updateOrCreateMeta("og:type", "product");
    if (data.product.heroShotUri) {
      updateOrCreateMeta("og:image", data.product.heroShotUri);
    }
    // Include variant parameter in og:url
    // Base slug (same for all sizes) - do NOT include sizeOrWeight
    const productSlugValue =
      data.product.slug ||
      generateProductSlug({
        name: data.product.name, // Use parsed Product Name from backend
        id: data.product.id,
        brandName: data.product.brandName,
        classificationName: data.product.classificationName,
        sizeOrWeight: null, // Base slug should NOT include size/weight
      });
    const variantParam = productSlugValue
      ? `?variant=${encodeURIComponent(productSlugValue)}`
      : "";
    updateOrCreateMeta(
      "og:url",
      `${process.env.NEXT_PUBLIC_SITE_URL || "https://dank.ca"
      }/product/${canonicalSlug}${variantParam}`
    );

    // Update or create Twitter Card tags
    const updateOrCreateTwitterMeta = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    updateOrCreateTwitterMeta("twitter:card", "summary_large_image");
    updateOrCreateTwitterMeta("twitter:title", productTitle);
    updateOrCreateTwitterMeta("twitter:description", metaDescription);
    if (data.product.heroShotUri) {
      updateOrCreateTwitterMeta("twitter:image", data.product.heroShotUri);
    }

    // Update canonical URL with variant parameter
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    // Reuse productSlugValue and variantParam already defined above
    canonical.setAttribute(
      "href",
      `${process.env.NEXT_PUBLIC_SITE_URL || "https://dank.ca"
      }/product/${canonicalSlug}${variantParam}`
    );
  }, [data?.product, productTitle, metaDescription, canonicalSlug]);

  // Inject JSON-LD structured data into head for better SEO
  useEffect(() => {
    if (!data?.success || !product) return;

    // Remove existing structured data scripts (product-specific)
    const existingScripts = document.head.querySelectorAll(
      "script[data-product-schema]"
    );
    existingScripts.forEach((script) => script.remove());

    // Add Product structured data
    if (structuredData) {
      const productScript = document.createElement("script");
      productScript.type = "application/ld+json";
      productScript.setAttribute("data-product-schema", "true");
      productScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(productScript);
    }

    // Add Breadcrumb structured data
    if (breadcrumbData) {
      const breadcrumbScript = document.createElement("script");
      breadcrumbScript.type = "application/ld+json";
      breadcrumbScript.setAttribute("data-product-schema", "true");
      breadcrumbScript.textContent = JSON.stringify(breadcrumbData);
      document.head.appendChild(breadcrumbScript);
    }

    // Add Organization structured data (only once per page load)
    if (!document.head.querySelector("script[data-organization-schema]")) {
      const orgScript = document.createElement("script");
      orgScript.type = "application/ld+json";
      orgScript.setAttribute("data-organization-schema", "true");
      orgScript.textContent = JSON.stringify(organizationData);
      document.head.appendChild(orgScript);
    }

    // Cleanup function
    return () => {
      const productScripts = document.head.querySelectorAll(
        "script[data-product-schema]"
      );
      productScripts.forEach((script) => script.remove());
    };
  }, [
    data?.success,
    product?.id,
    structuredData,
    breadcrumbData,
    organizationData,
  ]);

  // PDP recommendations (#43): same-category + best sellers, ranked for complementary categories & in-stock depth
  useEffect(() => {
    if (!data?.success || !product || !resolvedStoreId) return;

    const classification = (product.classificationName || "").trim();

    const currentProductIds = new Set<string>();
    if (product.id) currentProductIds.add(product.id);
    if (product.groupProducts) {
      product.groupProducts.forEach((gp: any) => {
        if (gp.productId) currentProductIds.add(gp.productId);
      });
    }

    const currentGroupKey = generateProductGroupKey(
      product.name,
      product.classificationName || null,
      product.brandName || null,
      (product as any).masterProductId || null,
      product.sizeOrWeight || null
    );

    const filterSameGroup = (items: any[]) =>
      items.filter((item: any) => {
        const itemProductId = item.product?.id || item.id;
        if (currentProductIds.has(itemProductId)) return false;
        const itemProduct = item.product || item;
        const itemGroupKey = generateProductGroupKey(
          itemProduct.name || "",
          itemProduct.classificationName || null,
          itemProduct.brandName || null,
          itemProduct.masterProductId || null,
          itemProduct.sizeOrWeight || null
        );
        if (currentGroupKey === itemGroupKey) return false;
        return true;
      });

    const controller = new AbortController();
    (async () => {
      setLoadingRelatedProducts(true);
      try {
        const bsParams = new URLSearchParams();
        bsParams.set("limit", "28");
        bsParams.set("page", "1");
        bsParams.set("sortBy", "best-selling");

        const bsRes = await fetch(
          `${base}/api/store/${resolvedStoreId}/products?${bsParams.toString()}`,
          { cache: "no-store", signal: controller.signal }
        );

        let bsItems: any[] = [];
        let priceMap: Record<string, number> = {};
        if (!controller.signal.aborted && bsRes.ok) {
          const bsJson = await bsRes.json();
          if (bsJson?.success && Array.isArray(bsJson.items)) {
            bsItems = filterSameGroup(bsJson.items);
            if (bsJson.priceMap)
              priceMap = { ...priceMap, ...bsJson.priceMap };
          }
        }

        if (controller.signal.aborted) return;

        if (!classification) {
          const ranked = rankStoreRecommendations(bsItems, {
            excludeProductIds: currentProductIds,
            primaryClassifications: new Set(),
            limit: 4,
          });
          setRelatedProductsPriceMap(priceMap);
          setRelatedProducts(ranked);
          return;
        }

        const catParams = new URLSearchParams();
        catParams.set("category", classification);
        catParams.set("limit", "16");
        catParams.set("page", "1");

        const catRes = await fetch(
          `${base}/api/store/${resolvedStoreId}/products?${catParams.toString()}`,
          { cache: "no-store", signal: controller.signal }
        );

        let catFiltered: any[] = [];
        if (!controller.signal.aborted && catRes.ok) {
          const catJson = await catRes.json();
          if (catJson?.success && Array.isArray(catJson.items)) {
            catFiltered = filterSameGroup(catJson.items);
            if (catJson.priceMap)
              priceMap = { ...priceMap, ...catJson.priceMap };
          }
        }

        if (controller.signal.aborted) return;

        const merged = mergePdpRecommendations(catFiltered, bsItems, {
          excludeProductIds: currentProductIds,
          primaryClassification: classification,
        });

        setRelatedProductsPriceMap(priceMap);
        setRelatedProducts(merged);
      } catch (error) {
        // A fetch aborted by the effect cleanup (re-run or unmount) is
        // expected, not a failure: swallow it silently.
        if (controller.signal.aborted) return;
        console.error("Failed to fetch related products:", error);
      } finally {
        if (!controller.signal.aborted) {
          setLoadingRelatedProducts(false);
        }
      }
    })();

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data?.success,
    product?.id,
    product?.classificationName,
    resolvedStoreId,
    base,
  ]);

  useEffect(() => {
    setPdpRecFeedbackDone(false);
  }, [product?.id]);

  useEffect(() => {
    if (!product?.id || visibleRelatedProducts.length === 0) return;
    trackFunnelEventOnce(
      `rec-module-view:pdp:${product.id}`,
      {
        event: "funnel_recommendation_module_view",
        placement: "pdp",
        product_count: visibleRelatedProducts.length,
      },
      8000
    );
  }, [product?.id, visibleRelatedProducts.length]);

  // Show EntryModal if no store ID
  if (showEntryModal) {
    return (
      <div className="min-h-screen bg-white">
        <Header stores={stores} base={base} />
        <EntryModal
          stores={stores}
          base={base}
          onClose={() => setShowEntryModal(false)}
        />
      </div>
    );
  }

  // Show loading spinner (AFTER all hooks)
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header stores={stores} base={base} />
        <main className="flex min-h-screen items-center justify-center p-6">
          <LogoLoader size="md" label="Loading product…" />
        </main>
      </div>
    );
  }

  // Show error (AFTER all hooks)
  if (!data || !data.success || !product) {
    return (
      <div className="min-h-screen bg-white">
        <Header stores={stores} base={base} />
        <main className="flex min-h-screen items-center justify-center p-6">
          <div className="text-center">
            <h1 className="text-xl font-semibold text-red-600">
              Failed to load product
            </h1>
            {data?.error && (
              <p className="mt-2 text-sm text-slate-600">{data.error}</p>
            )}
            <Button
              onClick={() => router.back()}
              variant="secondary"
              className="mt-4 px-4 py-2 rounded-lg"
            >
              Go Back
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-bg-1">
        <Header stores={stores} base={base} />
        <main className="min-h-screen bg-bg-1 max-sm:pb-8">
          {/* Mobile Header - Back + Link Icon */}
          <div className="w-full pt-4 sm:pt-4 bg-bg-1">
            <div className="w-full mx-auto px-4">
              {/* Desktop: Breadcrumb Navigation */}
              <div className="hidden sm:block bg-white border border-neutral-10 rounded-2xl p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <Breadcrumbs
                    items={[
                      { label: "Shop", href: "/search" },
                      ...(classification
                        ? [
                            {
                              // Map database classificationName to display label (same as search page chip definitions)
                              label:
                                ({
                                  Flower: "Flowers",
                                  "Pre-Roll": "Pre-rolls",
                                  Extract: "Extracts",
                                  Topical: "Topicals",
                                  Vaporizer: "Vaporizers",
                                  Edible: "Edibles",
                                  Tincture: "Tinctures",
                                  Accessories: "Accessories",
                                  Clothing: "Dank Merch",
                                } as Record<string, string>)[classification] || classification,
                              href: `/search?category=${encodeURIComponent(classification)}`,
                            },
                          ]
                        : []),
                      { label: cleanProductName(product.name, product.brandName) },
                    ]}
                    className="mb-0"
                  />
                  <Button
                    onClick={() => {
                      if (navigator.share) {
                        navigator
                          .share({
                            title: product.name,
                            text: product.shortDescription || "",
                            url: window.location.href,
                          })
                          .catch(() => { });
                      } else {
                        navigator.clipboard.writeText(window.location.href);
                      }
                    }}
                    variant="icon"
                    size="icon-md"
                    className="rounded-lg"
                    aria-label="Share product"
                  >
                    <LinkIcon size={20} color="currentColor" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Product Section */}
          <div className="w-full mx-auto px-4 sm:p-4 max-sm:mt-4">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Product Images */}
              <div className="flex flex-col w-full lg:w-[40%] space-y-2 sm:space-y-3">
                {/* Main Image */}
                <div className="group relative overflow-hidden rounded-2xl border border-neutral-10 bg-gradient-to-br from-white to-bg-1 shadow-sm">
                  <div
                    className={`relative w-full h-[185px] sm:h-auto sm:aspect-square lg:max-h-[640px] overflow-hidden ${isImageZoomed ? "cursor-zoom-out" : "cursor-zoom-in"
                      }`}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = ((e.clientX - rect.left) / rect.width) * 100;
                      const y = ((e.clientY - rect.top) / rect.height) * 100;
                      setMousePosition({ x, y });
                      setIsImageZoomed(!isImageZoomed);
                    }}
                    onMouseMove={(e) => {
                      if (!isImageZoomed) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = ((e.clientX - rect.left) / rect.width) * 100;
                      const y = ((e.clientY - rect.top) / rect.height) * 100;
                      setMousePosition({ x, y });
                    }}
                    onMouseLeave={() => {
                      if (isImageZoomed) setIsImageZoomed(false);
                    }}
                  >
                    {/* Floating share button, mobile only, top-right of image */}
                    <Button
                      variant="icon"
                      size="icon-sm"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (navigator.share) {
                          navigator
                            .share({
                              title: product.name,
                              text: product.shortDescription || "",
                              url: window.location.href,
                            })
                            .catch(() => { });
                        } else {
                          navigator.clipboard.writeText(window.location.href);
                        }
                      }}
                      aria-label="Share product"
                      className="sm:hidden !absolute top-2 right-2 z-30 inline-flex rounded-lg"
                    >
                      <LinkIcon size={16} color="currentColor" />
                    </Button>
                    {(() => {
                      // Get images from the selected variant (color or size)
                      let variantHeroShotUri = product.heroShotUri;
                      let variantProductMedia = getProductMedia(product);

                      // First, try to get heroShotUri and productMedia from the selected size/product (for size switching)
                      if (
                        selectedPackageId ||
                        selectedProductId ||
                        variantParam
                      ) {
                        const matchingSizeOption = product.sizeOptions?.find(
                          (opt: any) =>
                            (selectedPackageId &&
                              opt.packageId === selectedPackageId) ||
                            (selectedProductId &&
                              opt.productId === selectedProductId) ||
                            (variantParam &&
                              (opt.productSlug === variantParam ||
                                (opt as any).product?.slug === variantParam))
                        );

                        if (matchingSizeOption) {
                          // Get heroShotUri from the selected sizeOption (priority: product.product.heroShotUri > sizeOption.heroShotUri)
                          const sizeOptionHeroShotUri =
                            (matchingSizeOption as any).product?.heroShotUri ||
                            matchingSizeOption.heroShotUri;
                          if (sizeOptionHeroShotUri) {
                            variantHeroShotUri = sizeOptionHeroShotUri;
                          }

                          // Get productMedia from the matching sizeOption (from backend)
                          // Priority: sizeOption.productMedia (from backend) > product.productMedia
                          if (
                            (matchingSizeOption as any).productMedia &&
                            Array.isArray(
                              (matchingSizeOption as any).productMedia
                            ) &&
                            (matchingSizeOption as any).productMedia.length > 0
                          ) {
                            variantProductMedia = (matchingSizeOption as any)
                              .productMedia;
                          } else if ((matchingSizeOption as any).product) {
                            // Fallback to getting media from product.product if sizeOption.productMedia is not available
                            const productMediaFromProduct = getProductMedia(
                              (matchingSizeOption as any).product
                            );
                            if (
                              productMediaFromProduct &&
                              productMediaFromProduct.length > 0
                            ) {
                              variantProductMedia = productMediaFromProduct;
                            }
                          }
                        }
                      }

                      // For clothing with color selection, get images from the selected color variant (overrides size if color is selected)
                      if (
                        clothingVariants &&
                        selectedColor &&
                        clothingVariants.colors.length > 0
                      ) {
                        const colorProducts =
                          clothingVariants.variantsByColor.get(selectedColor) ||
                          [];

                        // Find the product that matches the selected size/product, or use the first available
                        let matchingColorProduct = null;
                        if (selectedPackageId || selectedProductId) {
                          matchingColorProduct = colorProducts.find(
                            (p) =>
                              (selectedPackageId &&
                                p.packageId === selectedPackageId) ||
                              (selectedProductId &&
                                p.productId === selectedProductId)
                          );
                        }

                        // If no match found, use first available product for the color
                        if (!matchingColorProduct) {
                          matchingColorProduct =
                            colorProducts.find((p) => p.inStock >= 1) ||
                            colorProducts[0];
                        }

                        if (matchingColorProduct) {
                          // Get heroShotUri from the matching color product (priority: product.product.heroShotUri > colorProduct.heroShotUri)
                          const colorProductHeroShotUri =
                            matchingColorProduct.product?.heroShotUri ||
                            matchingColorProduct.heroShotUri;
                          if (colorProductHeroShotUri) {
                            variantHeroShotUri = colorProductHeroShotUri;
                          }

                          // Get productMedia from the matching sizeOption for this color product (from backend)
                          const matchingSizeOption = product.sizeOptions?.find(
                            (opt: any) =>
                              opt.productId ===
                              matchingColorProduct.productId ||
                              opt.packageId === matchingColorProduct.packageId
                          );

                          if (matchingSizeOption) {
                            // Priority: sizeOption.productMedia (from backend) > product.productMedia
                            if (
                              (matchingSizeOption as any).productMedia &&
                              Array.isArray(
                                (matchingSizeOption as any).productMedia
                              ) &&
                              (matchingSizeOption as any).productMedia.length >
                              0
                            ) {
                              variantProductMedia = (matchingSizeOption as any)
                                .productMedia;
                            } else if ((matchingSizeOption as any).product) {
                              // Fallback to getting media from product.product if sizeOption.productMedia is not available
                              const productMediaFromProduct = getProductMedia(
                                (matchingSizeOption as any).product
                              );
                              if (
                                productMediaFromProduct &&
                                productMediaFromProduct.length > 0
                              ) {
                                variantProductMedia = productMediaFromProduct;
                              }
                            }
                          }
                        }
                      }

                      // Get all available images: heroShotUri + productMedia (same order as thumbnail slider)
                      const heroImage = variantHeroShotUri
                        ? {
                          url: variantHeroShotUri,
                          alt:
                            product.name ||
                            cleanProductName(product.name, product.brandName),
                        }
                        : null;

                      const mediaImages = variantProductMedia
                        .filter((m) => m.type === "image")
                        .map((m) => ({
                          url: joinImageUrl(base, m.url),
                          alt:
                            m.alt ||
                            product.name ||
                            cleanProductName(product.name, product.brandName),
                        }));

                      // Create the same array order as thumbnail slider: heroImage first (if exists), then mediaImages
                      const allImages = heroImage
                        ? [heroImage, ...mediaImages]
                        : mediaImages;

                      // Get the current image based on selectedImageIndex (matches thumbnail slider)
                      const currentImage =
                        allImages[selectedImageIndex] || allImages[0];

                      if (currentImage) {
                        // Calculate transform for zoom and pan
                        // Zoom to 200% when clicked, and pan based on mouse position when zoomed
                        const zoomScale = isImageZoomed ? 2 : 1;
                        // When zoomed, translate to keep the area under the cursor visible
                        // The translate values adjust the position so the zoomed image can be panned
                        const translateX = isImageZoomed
                          ? (50 - mousePosition.x) * 1
                          : 0;
                        const translateY = isImageZoomed
                          ? (50 - mousePosition.y) * 1
                          : 0;

                        return (
                          <>
                            <img
                              key={`${selectedImageIndex}-${variantHeroShotUri || product.heroShotUri || ""
                                }-${selectedColor || ""}-${selectedPackageId || ""
                                }-${selectedProductId || ""}`}
                              src={currentImage.url}
                              alt={currentImage.alt}
                              className="absolute inset-0 m-auto h-full w-full object-contain p-2 sm:p-8 transition-transform duration-300 ease-out"
                              style={{
                                transform: `scale(${zoomScale}) translate(${translateX}%, ${translateY}%)`,
                                transformOrigin: isImageZoomed
                                  ? `${mousePosition.x}% ${mousePosition.y}%`
                                  : "center center",
                              }}
                              loading="eager"
                            />

                            {/* Image counter (top-left), only when there are multiple images */}
                            {allImages.length > 1 && (
                              <div className="pointer-events-none absolute left-3 top-3 z-20 inline-flex items-center gap-1.5 rounded-full bg-blue-100/85 px-2.5 py-1 text-[11px] font-semibold text-constant-white backdrop-blur-sm">
                                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 7a2 2 0 012-2h2.586a1 1 0 00.707-.293l1.121-1.121A2 2 0 0111.828 3h0.343a2 2 0 011.415.586l1.12 1.121A1 1 0 0015.415 5H18a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V7z" />
                                  <circle cx="12" cy="12" r="3.2" />
                                </svg>
                                {selectedImageIndex + 1} / {allImages.length}
                              </div>
                            )}

                            {/* Zoom hint (bottom-right), fades in on hover, hidden when zoomed */}
                            {!isImageZoomed && (
                              <div className="pointer-events-none absolute bottom-3 right-3 z-20 hidden items-center gap-1 rounded-full bg-constant-white/95 px-2.5 py-1 text-[11px] font-medium text-text-secondary shadow-sm opacity-0 transition-opacity group-hover:opacity-100 sm:inline-flex">
                                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
                                  <circle cx="11" cy="11" r="7" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M8 11h6M11 8v6" />
                                </svg>
                                Click to zoom
                              </div>
                            )}

                            {/* Prev / Next arrows, only when there are multiple images */}
                            {allImages.length > 1 && (
                              <>
                                <Button raw
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedImageIndex((idx) =>
                                      idx <= 0 ? allImages.length - 1 : idx - 1
                                    );
                                  }}
                                  aria-label="Previous image"
                                  className="absolute left-3 top-1/2 z-20 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-neutral-10 bg-constant-white/95 text-blue-80 shadow-sm backdrop-blur-sm transition-all active:scale-90 sm:opacity-0 sm:group-hover:opacity-100"
                                >
                                  <ChevronLeftIcon className="h-5 w-5" />
                                </Button>
                                <Button raw
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedImageIndex((idx) =>
                                      idx >= allImages.length - 1 ? 0 : idx + 1
                                    );
                                  }}
                                  aria-label="Next image"
                                  className="absolute right-3 top-1/2 z-20 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-neutral-10 bg-constant-white/95 text-blue-80 shadow-sm backdrop-blur-sm transition-all active:scale-90 sm:opacity-0 sm:group-hover:opacity-100"
                                >
                                  <ChevronRightIcon className="h-5 w-5" />
                                </Button>
                              </>
                            )}
                          </>
                        );
                      }

                      return (
                        <div className="flex h-full w-full items-center justify-center">
                          <svg
                            className="h-24 w-24 text-text-tertiary"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      );
                    })()}

                    {/* Color Selection for Clothing Products - Inside Main Image */}
                    {(() => {
                      const isClothingForColors =
                        classification &&
                        (classification.toLowerCase().includes("clothing") ||
                          classification.toLowerCase().includes("merch") ||
                          classification.toLowerCase().includes("apparel")) &&
                        !classification.toLowerCase().includes("accessories");

                      if (
                        clothingVariants &&
                        clothingVariants.colors.length > 0 &&
                        isClothingForColors
                      ) {
                        // Filter colors to only show those with available sizes (after stock-1 rule)
                        const availableColors = clothingVariants.colors.filter(
                          (color) => {
                            const colorProducts =
                              clothingVariants.variantsByColor.get(color) || [];
                            // Check if any product has stock >= 2 (so stock-1 >= 1)
                            return colorProducts.some((p) => {
                              const displayStock = Math.max(
                                0,
                                (p.inStock || 0) - 1
                              );
                              return displayStock > 0;
                            });
                          }
                        );

                        // Don't render if no colors are available
                        if (availableColors.length === 0) return null;

                        return (
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-wrap gap-2 justify-center z-10">
                            {availableColors.map((color) => {
                              const isSelectedColor = selectedColor === color;
                              const colorProducts =
                                clothingVariants.variantsByColor.get(color) ||
                                [];
                              const hasStock = colorProducts.some((p) => {
                                const displayStock = Math.max(
                                  0,
                                  (p.inStock || 0) - 1
                                );
                                return displayStock > 0;
                              });
                              const colorHex = getColorHex(color);

                              return (
                                <Button
                                  key={color}
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent image zoom on click
                                    setSelectedColor(color);
                                    setQuantity(1);

                                    // Get all products for this color (same logic as sizeOptionsToDisplay)
                                    const colorProductsForSelected =
                                      clothingVariants.variantsByColor.get(
                                        color
                                      ) || [];

                                    // Create size options directly from colorProducts (same logic as sizeOptionsToDisplay)
                                    // This ensures we use the exact same filtering and sorting as the displayed size options
                                    const sizeOptionMap = new Map<
                                      string,
                                      any
                                    >();

                                    colorProductsForSelected.forEach(
                                      (colorProduct) => {
                                        // Get size from colorProduct (it has size, sizeOrWeight from allProducts structure)
                                        const colorProductSize =
                                          colorProduct.size ||
                                          colorProduct.sizeOrWeight ||
                                          null;
                                        if (!colorProductSize) return;

                                        // Apply stock-1 rule for display: only show if stock-1 > 0
                                        const displayStock = Math.max(
                                          0,
                                          (colorProduct.inStock || 0) - 1
                                        );
                                        if (displayStock <= 0) return; // Skip if stock-1 is not > 0

                                        // Use values directly from colorProduct (same as sizeOptionsToDisplay)
                                        const packageId =
                                          colorProduct.packageId || "";
                                        const productSlug =
                                          colorProduct.product?.slug ||
                                          colorProduct.productSlug ||
                                          null;

                                        // Apply stock-1 rule: if raw stock is 2, available stock is 1
                                        const rawStock =
                                          colorProduct.inStock || 0;
                                        const stockValue = Math.max(
                                          0,
                                          rawStock - 1
                                        );

                                        const sizeOption = {
                                          productId: colorProduct.productId,
                                          packageId: packageId,
                                          sizeOrWeight: colorProductSize,
                                          size:
                                            colorProduct.size ||
                                            colorProduct.sizeOrWeight ||
                                            colorProductSize,
                                          inStock: stockValue,
                                          price: colorProduct.price || 0,
                                          unitCost:
                                            colorProduct.unitCost || null,
                                          productSlug: productSlug,
                                          variationId:
                                            colorProduct.variationId || null,
                                          heroShotUri:
                                            colorProduct.product?.heroShotUri ||
                                            colorProduct.heroShotUri ||
                                            null,
                                          longDescription:
                                            colorProduct.product
                                              ?.longDescription ||
                                            (colorProduct as any)
                                              .longDescription ||
                                            null,
                                          shortDescription:
                                            colorProduct.product
                                              ?.shortDescription ||
                                            (colorProduct as any)
                                              .shortDescription ||
                                            null,
                                          originalProductName:
                                            colorProduct.originalProductName ||
                                            null,
                                          product: colorProduct.product || null,
                                        };

                                        // Use same uniqueKey logic as sizeOptionsToDisplay
                                        const uniqueKey =
                                          packageId &&
                                            String(packageId).trim() !== ""
                                            ? `${String(packageId)}-${String(
                                              sizeOption.sizeOrWeight || ""
                                            )}`
                                            : productSlug
                                              ? `${String(productSlug)}-${String(
                                                sizeOption.sizeOrWeight || ""
                                              )}`
                                              : String(colorProductSize)
                                                .toLowerCase()
                                                .trim();

                                        // Only add if we don't already have this unique key, or if this one is in stock and the existing one isn't
                                        if (!sizeOptionMap.has(uniqueKey)) {
                                          sizeOptionMap.set(
                                            uniqueKey,
                                            sizeOption
                                          );
                                        } else {
                                          const existing =
                                            sizeOptionMap.get(uniqueKey);
                                          if (
                                            sizeOption.inStock > 0 &&
                                            existing.inStock < 1
                                          ) {
                                            sizeOptionMap.set(
                                              uniqueKey,
                                              sizeOption
                                            );
                                          }
                                        }
                                      }
                                    );

                                    // Get all size options for this color, sorted (same sorting logic as sizeOptionsToDisplay)
                                    const sizeOptionsForColor = Array.from(
                                      sizeOptionMap.values()
                                    ).sort((a, b) => {
                                      const aSize =
                                        a.sizeOrWeight || a.size || "";
                                      const bSize =
                                        b.sizeOrWeight || b.size || "";

                                      // Sort sizes: numeric first, then alphabetical
                                      const aNum =
                                        parseFloat(
                                          String(aSize).replace(/[^0-9.]/g, "")
                                        ) || 0;
                                      const bNum =
                                        parseFloat(
                                          String(bSize).replace(/[^0-9.]/g, "")
                                        ) || 0;
                                      if (aNum !== 0 || bNum !== 0) {
                                        return aNum - bNum;
                                      }
                                      // Size order: XS, S, M, L, XL, XXL, etc.
                                      const sizeOrder = [
                                        "xs",
                                        "s",
                                        "m",
                                        "l",
                                        "xl",
                                        "xxl",
                                        "xxxl",
                                        "2xl",
                                        "3xl",
                                        "4xl",
                                        "small",
                                        "medium",
                                        "large",
                                        "extra small",
                                        "extra large",
                                        "one size",
                                      ];
                                      const aLower =
                                        String(aSize).toLowerCase();
                                      const bLower =
                                        String(bSize).toLowerCase();
                                      const aIndex = sizeOrder.findIndex(
                                        (size) => aLower.includes(size)
                                      );
                                      const bIndex = sizeOrder.findIndex(
                                        (size) => bLower.includes(size)
                                      );
                                      if (aIndex !== -1 && bIndex !== -1)
                                        return aIndex - bIndex;
                                      if (aIndex !== -1) return -1;
                                      if (bIndex !== -1) return 1;
                                      return aLower.localeCompare(bLower);
                                    });

                                    // Select the first item from the filtered and sorted list
                                    const firstSizeOption =
                                      sizeOptionsForColor[0];

                                    if (firstSizeOption) {
                                      // Get product slug from product.product.slug (full product object) if available
                                      const targetProductSlug =
                                        firstSizeOption.product?.slug ||
                                        firstSizeOption.productSlug;

                                      if (targetProductSlug) {
                                        // Update selected package/product IDs
                                        if (
                                          firstSizeOption.packageId &&
                                          firstSizeOption.packageId.trim() !==
                                          ""
                                        ) {
                                          setSelectedPackageId(
                                            firstSizeOption.packageId
                                          );
                                        }
                                        if (firstSizeOption.productId) {
                                          setSelectedProductId(
                                            firstSizeOption.productId
                                          );
                                        }

                                        // Use the switchVariant helper to update variant, URL slug, and product state
                                        // This updates product details including heroShotUri (image), descriptions, and URL slug
                                        // Pass the full product object for product details
                                        switchVariant(targetProductSlug, {
                                          ...firstSizeOption,
                                          product:
                                            firstSizeOption.product || null,
                                        });

                                        // Reset image index to 0 when switching variants
                                        setSelectedImageIndex(0);
                                      }
                                    }
                                  }}
                                  variant="icon"
                                  color={isSelectedColor ? "primary" : "white"}
                                  size="icon-sm"
                                  style={{ backgroundColor: colorHex }}
                                  title={color}
                                  aria-label={`Select ${color} color`}
                                />
                              );
                            })}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  {/* Mobile: Carousel Indicators */}
                  {(() => {
                    // Get all images for indicators (same logic as main image)
                    let variantHeroShotUri = product.heroShotUri;
                    let variantProductMedia = getProductMedia(product);

                    if (
                      clothingVariants &&
                      selectedColor &&
                      clothingVariants.colors.length > 0
                    ) {
                      const colorProducts =
                        clothingVariants.variantsByColor.get(selectedColor) ||
                        [];
                      const firstColorProduct =
                        colorProducts.find((p) => p.inStock >= 1) ||
                        colorProducts[0];
                      if (firstColorProduct) {
                        variantHeroShotUri =
                          firstColorProduct.heroShotUri || variantHeroShotUri;
                        const matchingSizeOption = product.sizeOptions?.find(
                          (opt: any) =>
                            opt.productId === firstColorProduct.productId ||
                            opt.packageId === firstColorProduct.packageId
                        );
                        if (
                          matchingSizeOption &&
                          (matchingSizeOption as any).productMedia
                        ) {
                          variantProductMedia = (matchingSizeOption as any)
                            .productMedia;
                        }
                      }
                    }

                    const heroImage = variantHeroShotUri
                      ? {
                        url: variantHeroShotUri,
                        alt:
                          product.name ||
                          cleanProductName(product.name, product.brandName),
                      }
                      : null;
                    const mediaImages = variantProductMedia
                      .filter((m) => m.type === "image")
                      .map((m) => ({
                        url: joinImageUrl(base, m.url),
                        alt:
                          m.alt ||
                          product.name ||
                          cleanProductName(product.name, product.brandName),
                      }));
                    const allImages = heroImage
                      ? [heroImage, ...mediaImages]
                      : mediaImages;

                    if (allImages.length <= 1) return null;

                    return (
                      <div className="sm:hidden flex justify-center gap-2 mt-3 pb-2">
                        {allImages.map((_, index) => (
                          <Button
                            key={index}
                            onClick={() => setSelectedImageIndex(index)}
                            variant="icon"
                            color={selectedImageIndex === index ? "primary" : "white"}
                            size="icon-sm"
                            aria-label={`Go to image ${index + 1}`}
                          />
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Thumbnail Images Carousel */}
                {(() => {
                  // For clothing with color selection, get images from the selected color variant
                  let variantHeroShotUri = product.heroShotUri;
                  let variantProductMedia = getProductMedia(product);

                  if (
                    clothingVariants &&
                    selectedColor &&
                    clothingVariants.colors.length > 0
                  ) {
                    const colorProducts =
                      clothingVariants.variantsByColor.get(selectedColor) || [];
                    const firstColorProduct =
                      colorProducts.find((p) => p.inStock >= 1) ||
                      colorProducts[0];

                    if (firstColorProduct) {
                      // Get heroShotUri from the first product of selected color
                      variantHeroShotUri =
                        firstColorProduct.heroShotUri || variantHeroShotUri;

                      // Try to get productMedia from the matching sizeOption
                      const matchingSizeOption = product.sizeOptions?.find(
                        (opt: any) =>
                          opt.productId === firstColorProduct.productId ||
                          opt.packageId === firstColorProduct.packageId
                      );

                      if (
                        matchingSizeOption &&
                        (matchingSizeOption as any).productMedia
                      ) {
                        variantProductMedia = (matchingSizeOption as any)
                          .productMedia;
                      }
                    }
                  }

                  const heroImage = variantHeroShotUri
                    ? {
                      url: variantHeroShotUri,
                      alt:
                        product.masterProductName ||
                        cleanProductName(product.name, product.brandName),
                    }
                    : null;

                  const mediaImages = variantProductMedia
                    .filter((m) => m.type === "image")
                    .map((m) => ({
                      url: joinImageUrl(base, m.url),
                      alt:
                        m.alt ||
                        product.masterProductName ||
                        cleanProductName(product.name, product.brandName),
                    }));

                  const allImages = heroImage
                    ? [heroImage, ...mediaImages]
                    : mediaImages;
                  const hasMultipleImages = allImages.length > 1;

                  if (!hasMultipleImages) return null;

                  return (
                    <div className="relative">
                      <div
                        ref={thumbStripRef}
                        className="flex items-center gap-2 sm:gap-3 overflow-x-auto scrollbar-hide scroll-smooth py-1"
                      >
                        {allImages.map((img, index) => {
                          const isSelected = selectedImageIndex === index;
                          return (
                            <Button raw
                              key={`thumb-${index}`}
                              ref={(el) => { thumbRefs.current[index] = el; }}
                              type="button"
                              onClick={() => setSelectedImageIndex(index)}
                              aria-label={`Show image ${index + 1}`}
                              aria-pressed={isSelected}
                              // Border stays a constant 2px so the tile size never shifts between
                              // selected / unselected, only the color changes. Earlier the selected
                              // state added a ring + offset which made selected tiles taller than
                              // their neighbors. Using object-cover on an aspect-square wrapper
                              // means every image fills its tile cleanly regardless of aspect ratio.
                              className={`group/thumb relative flex-shrink-0 aspect-square h-[72px] w-[72px] sm:h-[84px] sm:w-[84px] md:h-[96px] md:w-[96px] overflow-hidden rounded-xl border-2 bg-white transition-colors duration-200 ${
                                isSelected
                                  ? "border-blue-100"
                                  : "border-neutral-10 hover:border-blue-80"
                              }`}
                            >
                              <img
                                src={img.url}
                                alt={img.alt}
                                className={`absolute inset-0 h-full w-full object-cover transition-transform duration-200 ${
                                  isSelected ? "" : "group-hover/thumb:scale-105"
                                }`}
                                loading="lazy"
                              />
                            </Button>
                          );
                        })}
                      </div>
                      {/* Edge fades hint that more thumbnails exist off-screen. Pointer-events-none
                          so they don't intercept clicks or scroll gestures on the strip. */}
                      {thumbFadeLeft && (
                        <div
                          aria-hidden
                          className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-bg-1 to-transparent"
                        />
                      )}
                      {thumbFadeRight && (
                        <div
                          aria-hidden
                          className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-bg-1 to-transparent"
                        />
                      )}
                    </div>
                  );
                })()}

                {/* Mobile-only tab switcher: Product details ↔ Nutrition info.
                    Renders only when nutrition is available; desktop ignores this and shows
                    both panels stacked as usual. */}
                {!isAccessory && hasNutritionSection ? (
                  <div className="sm:hidden mt-2 grid grid-cols-2 gap-2 rounded-2xl border border-neutral-10 bg-bg-1 p-1">
                    <Button
                      variant={activeMobileTab === "details" ? "primary" : "bordered"}
                      type="button"
                      onClick={() => setActiveMobileTab("details")}
                      className="rounded-xl"
                      aria-pressed={activeMobileTab === "details"}
                    >
                      Product details
                    </Button>
                    <Button
                      variant={activeMobileTab === "nutrition" ? "primary" : "bordered"}
                      type="button"
                      onClick={() => setActiveMobileTab("nutrition")}
                      className="rounded-xl"
                      aria-pressed={activeMobileTab === "nutrition"}
                    >
                      Nutrition info
                    </Button>
                  </div>
                ) : null}

                {!isAccessory && hasNutritionSection ? (
                  <section
                    aria-label="Nutrition information"
                    className={`space-y-3 sm:space-y-4 ${activeMobileTab === "nutrition" ? "" : "max-sm:hidden"
                      }`}
                  >
                    {nutritionLabelData ? (
                      <div className="w-full rounded-2xl border border-[#D6D9E5] bg-white px-4 py-4 text-blue-100 sm:px-5 sm:py-5">
                        <h3 className="sm:text-[44px] text-2xl font-black leading-[0.95]">
                          {nutritionLabelData.titleEn}
                          <br />
                          {nutritionLabelData.titleFr}
                        </h3>
                        {(nutritionLabelData.servingEn || nutritionLabelData.servingFr) && (
                          <div className="mt-3 text-[18px] font-medium leading-tight">
                            {nutritionLabelData.servingEn ? <div>{nutritionLabelData.servingEn}</div> : null}
                            {nutritionLabelData.servingFr ? <div>{nutritionLabelData.servingFr}</div> : null}
                          </div>
                        )}
                        <div className="my-3 h-px w-full bg-blue-100" />
                        <div className="mb-1 flex items-end justify-between gap-3 text-[15px] leading-tight">
                          <div className="font-black">
                            Calories {nutritionLabelData.calories || "0"}
                          </div>
                          <div className="text-right font-black">
                            <div>% Daily Value *</div>
                            <div>% valeur quotidienne *</div>
                          </div>
                        </div>
                        <div className="my-2 h-[4px] w-full bg-blue-100" />
                        <div className="space-y-1">
                          {nutritionLabelData.rows.map((row, index) => (
                            <div
                              key={`${row.label}-${index}`}
                              className={`flex items-end justify-between gap-3 text-blue-100 ${row.isSubrow ? "pl-4 text-[13px] leading-tight" : "text-[15px] leading-tight"
                                }`}
                            >
                              <span className={row.isSubrow ? "" : "font-semibold"}>{row.label}</span>
                              <span className={row.value ? "font-semibold" : ""}>{row.value}</span>
                            </div>
                          ))}
                        </div>
                        {(nutritionLabelData.footnoteEn || nutritionLabelData.footnoteFr) && (
                          <>
                            <div className="my-3 h-px w-full bg-blue-100" />
                            <div className="text-[11px] leading-snug">
                              {nutritionLabelData.footnoteEn ? <div>{nutritionLabelData.footnoteEn}</div> : null}
                              {nutritionLabelData.footnoteFr ? <div>{nutritionLabelData.footnoteFr}</div> : null}
                            </div>
                          </>
                        )}
                      </div>
                    ) : nutritionHtml ? (
                      <div
                        className="nutrition-label-scope [&_.nutrition-label]:w-full [&_.nutrition-label]:rounded-2xl [&_.nutrition-label]:border [&_.nutrition-label]:border-[#D6D9E5] [&_.nutrition-label]:bg-[#F7F7FA] [&_.nutrition-label]:px-4 [&_.nutrition-label]:py-4 sm:[&_.nutrition-label]:px-5 sm:[&_.nutrition-label]:py-5 [&_.nutrition-label]:text-blue-100 [&_.nutrition-title]:text-[44px] [&_.nutrition-title]:font-black [&_.nutrition-title]:leading-[0.95] [&_.nutrition-serving]:mt-3 [&_.nutrition-serving]:text-[18px] [&_.nutrition-serving]:font-medium [&_.nutrition-serving]:leading-tight [&_.nutrition-divider]:my-3 [&_.nutrition-divider]:h-px [&_.nutrition-divider]:w-full [&_.nutrition-divider]:bg-blue-100 [&_.nutrition-divider-strong]:my-2 [&_.nutrition-divider-strong]:h-[4px] [&_.nutrition-divider-strong]:w-full [&_.nutrition-divider-strong]:bg-blue-100 [&_.nutrition-row]:flex [&_.nutrition-row]:items-end [&_.nutrition-row]:justify-between [&_.nutrition-row]:gap-3 [&_.nutrition-row]:py-0.5 [&_.nutrition-row]:text-[15px] [&_.nutrition-row]:leading-tight [&_.nutrition-row-bold]:font-black [&_.nutrition-subrow]:pl-4 [&_.nutrition-subrow]:text-[13px] [&_.nutrition-subrow]:leading-tight [&_.nutrition-subrow]:text-blue-100 [&_.nutrition-footnote]:mt-3 [&_.nutrition-footnote]:text-body-16-desktop [&_.nutrition-footnote]:leading-snug"
                        dangerouslySetInnerHTML={{ __html: nutritionHtml }}
                      />
                    ) : null}
                    {hasNutritionExtras ? (
                      <div className="w-full rounded-2xl border border-[#D6D9E5] bg-white px-4 py-4 sm:px-8 sm:py-6">
                        <ul className="text-body-14-desktop text-[#5C629A]">
                          {nutritionExtrasRows.map((item) => (
                            <li key={item.label}>
                              <strong className="text-blue-100">{item.label}:</strong>{" "}
                              <span>{item.value}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </section>
                ) : null}
              </div>

              {/* Product Details */}
              <div
                className={`flex-1 rounded-2xl border border-neutral-10 bg-white p-4 sm:p-6 lg:p-8 ${!isAccessory && hasNutritionSection && activeMobileTab !== "details"
                  ? "max-sm:hidden"
                  : ""
                  }`}
              >
                <div className="flex flex-col gap-5 sm:gap-6">
                  {/* Brand, Title and Favorite */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-2">
                      {brand && (
                        <p className="text-sm font-medium tracking-tight text-text-secondary">
                          {brand}
                        </p>
                      )}
                      <h1 className="text-2xl font-bold leading-tight text-blue-100 sm:text-3xl lg:text-[40px] sm:leading-[1.15]">
                        {product.name ||
                          cleanProductName(product.name, product.brandName)}
                      </h1>
                      {pdpSubtitle ? (
                        <p className="text-base font-medium leading-snug text-text-secondary">
                          {pdpSubtitle}
                        </p>
                      ) : null}
                    </div>
                    {/* Favorite Icon Button */}
                    <FavoriteButton productId={product.id} size={20} className="shadow-sm" />
                  </div>


                  {/* Product Tags */}
                  <div className="flex flex-wrap gap-2 sm:gap-2.5">
                    {!isAccessory && strainType && (
                      <span className="inline-flex items-center rounded-md border border-neutral-10 bg-neutral-5 px-2.5 py-1 text-xs font-medium text-text-secondary sm:text-sm sm:px-3 sm:py-1.5">
                        {strainType}
                      </span>
                    )}
                    {classification && (
                      <span className="inline-flex items-center rounded-md border border-neutral-10 bg-neutral-5 px-2.5 py-1 text-xs font-medium text-text-secondary sm:text-sm sm:px-3 sm:py-1.5">
                        {classification}
                      </span>
                    )}
                    {!isAccessory && pdpThcBadge && (
                      <span className="inline-flex items-center rounded-md border border-neutral-10 bg-neutral-5 px-2.5 py-1 text-xs font-medium text-text-secondary sm:text-sm sm:px-3 sm:py-1.5">
                        {pdpThcBadge}
                      </span>
                    )}
                    {discountPercent && (
                      <span className="inline-flex items-center rounded-md bg-yellow-50 px-2.5 py-1 text-xs font-semibold text-blue-100 sm:text-sm sm:px-3 sm:py-1.5">
                        Holiday Heat - {discountPercent}% OFF
                      </span>
                    )}
                  </div>

                  {/* Divider */}
                  <div className="w-full h-[1px] bg-neutral-10"></div>

                  {/* Price and Options */}
                  <div className="space-y-4">
                    {/* Price Row - with Color/Size variants on the right */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-6">
                      {/* Size Options - Show on right side of price row when variants exist */}
                      {(() => {
                        // Debug: Always log available sizeOptions from backend (for all product types)
                        // Include ALL available fields from backend
                        if (process.env.NODE_ENV === "development") {
                          console.log(
                            `[Size Options] Available sizeOptions from backend:`,
                            product.sizeOptions || []
                          );
                        }

                        // Get size options and filter to only show available sizes (inStock > 0)
                        let sizeOptionsToDisplay: any[] = (
                          product.sizeOptions || []
                        ).filter((opt: any) => (opt.inStock || 0) > 0);

                        // For clothing products with color variants, filter by selected color and show exact sizes for that color
                        // IMPORTANT: We only display size options for products that match:
                        // 1. Same group (already filtered by backend in groupProducts)
                        // 2. Same color (filtered by selectedColor)
                        // 3. In stock (inStock > 0) - only show available sizes
                        if (
                          clothingVariants &&
                          clothingVariants.colors.length > 0 &&
                          selectedColor
                        ) {
                          // Get products for selected color - these are all from the same group (same parsedBrand + parsedProductName + classificationName)
                          const colorProducts =
                            clothingVariants.variantsByColor.get(
                              selectedColor
                            ) || [];

                          // Debug: Log selected color and its products (include ALL fields)
                          if (process.env.NODE_ENV === "development") {
                            console.log(
                              `[Size Options] Selected Color: ${selectedColor}, Products (same group only):`,
                              colorProducts
                            );
                          }

                          // Create size options directly from colorProducts (no need to match with product.sizeOptions)
                          // colorProducts already contains all the data we need: productId, packageId, size, inStock, price, etc.
                          // This ensures we only show sizes for the selected color within the same group
                          const sizeOptionMap = new Map<string, any>();

                          colorProducts.forEach((colorProduct) => {
                            // Get size from colorProduct (it has size, sizeOrWeight from allProducts structure)
                            const colorProductSize =
                              colorProduct.size ||
                              colorProduct.sizeOrWeight ||
                              null;
                            if (!colorProductSize) return;

                            // Apply stock-1 rule for display: only show if stock-1 > 0
                            const displayStock = Math.max(
                              0,
                              (colorProduct.inStock || 0) - 1
                            );
                            if (displayStock <= 0) return; // Skip if stock-1 is not > 0

                            // Use values directly from colorProduct (products[index] from backend)
                            // No need to match with product.sizeOptions - colorProduct has all the data we need
                            const packageId = colorProduct.packageId || ""; // Use exact packageId from database
                            const productSlug =
                              colorProduct.product?.slug ||
                              colorProduct.productSlug ||
                              null; // Use product.product.slug

                            // Always use colorProduct.inStock as the source of truth for raw stock
                            // colorProduct.inStock is raw stock from groupProducts (backend response shows inStock: 2, 2, 1, 3, 1)
                            // Apply stock-1 rule: if raw stock is 2, available stock is 1
                            const rawStock = colorProduct.inStock || 0; // Extract raw stock from colorProduct (from product.inStock)
                            const stockValue = Math.max(0, rawStock - 1); // Apply stock-1 rule: 2 -> 1, 1 -> 0, 3 -> 2

                            const sizeOption = {
                              productId: colorProduct.productId,
                              packageId: packageId, // Use exact packageId from database
                              sizeOrWeight: colorProductSize,
                              size:
                                colorProduct.size ||
                                colorProduct.sizeOrWeight ||
                                colorProductSize, // Use colorProduct.size or sizeOrWeight
                              inStock: stockValue, // Always has stock-1 applied (from colorProduct.inStock)
                              price: colorProduct.price || 0, // Use colorProduct.price for strikethrough
                              unitCost: colorProduct.unitCost || null, // Use colorProduct.unitCost for actual price
                              productSlug: productSlug, // Use product.product.slug
                              variationId: colorProduct.variationId || null,
                              heroShotUri:
                                colorProduct.product?.heroShotUri ||
                                colorProduct.heroShotUri ||
                                null,
                              longDescription:
                                colorProduct.product?.longDescription ||
                                (colorProduct as any).longDescription ||
                                null, // Use product.product.longDescription
                              shortDescription:
                                colorProduct.product?.shortDescription ||
                                (colorProduct as any).shortDescription ||
                                null,
                              originalProductName:
                                colorProduct.originalProductName || null,
                              // Include full product object for product details (THC/CBD, etc.)
                              product: colorProduct.product || null,
                            };

                            // Use productSlug for uniqueness when packageId is not available (clothing products)
                            // Otherwise use sizeOrWeight as the key
                            const uniqueKey =
                              packageId && String(packageId).trim() !== ""
                                ? `${String(packageId)}-${String(
                                  sizeOption.sizeOrWeight || ""
                                )}`
                                : productSlug
                                  ? `${String(productSlug)}-${String(
                                    sizeOption.sizeOrWeight || ""
                                  )}`
                                  : String(colorProductSize).toLowerCase().trim();

                            // Only add if we don't already have this unique key, or if this one is in stock and the existing one isn't
                            if (!sizeOptionMap.has(uniqueKey)) {
                              sizeOptionMap.set(uniqueKey, sizeOption);
                            } else {
                              // Prefer in-stock options over out-of-stock
                              const existing = sizeOptionMap.get(uniqueKey);
                              if (
                                sizeOption.inStock > 0 &&
                                existing.inStock < 1
                              ) {
                                sizeOptionMap.set(uniqueKey, sizeOption);
                              }
                            }
                          });

                          // Debug: Log filtered sizes (include ALL fields)
                          if (process.env.NODE_ENV === "development") {
                            console.log(
                              `[Size Options] Filtered sizes for ${selectedColor}:`,
                              Array.from(sizeOptionMap.values())
                            );
                          }

                          // Convert map to array - already filtered by stock-1 > 0 above, just sort by size
                          sizeOptionsToDisplay = Array.from(
                            sizeOptionMap.values()
                          ).sort((a, b) => {
                            const aSize = a.sizeOrWeight || a.size || "";
                            const bSize = b.sizeOrWeight || b.size || "";

                            // Sort sizes: numeric first, then alphabetical
                            const aNum =
                              parseFloat(
                                String(aSize).replace(/[^0-9.]/g, "")
                              ) || 0;
                            const bNum =
                              parseFloat(
                                String(bSize).replace(/[^0-9.]/g, "")
                              ) || 0;
                            if (aNum !== 0 || bNum !== 0) {
                              return aNum - bNum;
                            }
                            // Size order: XS, S, M, L, XL, XXL, etc.
                            const sizeOrder = [
                              "xs",
                              "s",
                              "m",
                              "l",
                              "xl",
                              "xxl",
                              "xxxl",
                              "2xl",
                              "3xl",
                              "4xl",
                              "small",
                              "medium",
                              "large",
                              "extra small",
                              "extra large",
                              "one size",
                            ];
                            const aLower = String(aSize).toLowerCase();
                            const bLower = String(bSize).toLowerCase();
                            const aIndex = sizeOrder.findIndex((size) =>
                              aLower.includes(size)
                            );
                            const bIndex = sizeOrder.findIndex((size) =>
                              bLower.includes(size)
                            );
                            if (aIndex !== -1 && bIndex !== -1)
                              return aIndex - bIndex;
                            if (aIndex !== -1) return -1;
                            if (bIndex !== -1) return 1;
                            return aLower.localeCompare(bLower);
                          });
                        }

                        // Show size selector if there are size options available (even if only one)
                        // When color is selected, show sizes for that color only
                        if (sizeOptionsToDisplay.length > 0) {
                          return (
                            <div className="flex flex-wrap gap-2 sm:gap-2">
                              {sizeOptionsToDisplay.map(
                                (sizeOption, optionIndex) => {
                                  const currentProductSlug =
                                    sizeOption.productSlug;
                                  let selectedSizeOption = null;
                                  if (
                                    selectedPackageId &&
                                    selectedPackageId.trim() !== ""
                                  ) {
                                    selectedSizeOption =
                                      product.sizeOptions?.find(
                                        (opt: any) =>
                                          opt.packageId === selectedPackageId
                                      );
                                  }
                                  if (
                                    !selectedSizeOption &&
                                    selectedProductId
                                  ) {
                                    selectedSizeOption =
                                      product.sizeOptions?.find(
                                        (opt: any) =>
                                          opt.productId === selectedProductId
                                      );
                                  }
                                  // Use productSlug for matching when packageId is not available (clothing products)
                                  const packageMatches =
                                    (selectedPackageId &&
                                      selectedPackageId.trim() !== "" &&
                                      selectedPackageId ===
                                      sizeOption.packageId) ||
                                    (selectedProductId &&
                                      selectedProductId ===
                                      sizeOption.productId);
                                  const selectedSlug =
                                    variantParam ||
                                    selectedSizeOption?.productSlug ||
                                    null;
                                  const slugMatches =
                                    selectedSlug && currentProductSlug
                                      ? selectedSlug === currentProductSlug
                                      : !selectedSlug && !currentProductSlug;
                                  // For clothing products without packageId, use productSlug for selection matching
                                  const slugMatchesForSelection =
                                    !sizeOption.packageId ||
                                      String(sizeOption.packageId).trim() === ""
                                      ? selectedSlug &&
                                      sizeOption.productSlug &&
                                      selectedSlug === sizeOption.productSlug
                                      : true; // If packageId exists, we already checked it with packageMatches
                                  const isSelected =
                                    (packageMatches ||
                                      slugMatchesForSelection) &&
                                    slugMatches;
                                  const sizeLabel =
                                    sizeOption.sizeOrWeight ||
                                    sizeOption.netContent ||
                                    sizeOption.productSlug ||
                                    sizeOption.packageId;
                                  // Use productSlug for uniqueKey when packageId is not available (clothing products)
                                  const uniqueKey =
                                    sizeOption.packageId &&
                                      String(sizeOption.packageId).trim() !== ""
                                      ? `size-${String(
                                        sizeOption.packageId
                                      )}-${String(
                                        sizeOption.productSlug || "noslug"
                                      )}-${String(
                                        sizeOption.sizeOrWeight || "nosize"
                                      )}-${optionIndex}`
                                      : sizeOption.productSlug
                                        ? `size-${String(
                                          sizeOption.productSlug
                                        )}-${String(
                                          sizeOption.sizeOrWeight || "nosize"
                                        )}-${optionIndex}`
                                        : `size-option-${optionIndex}`;

                                  return (
                                    <Button
                                      variant={isSelected ? "primary" : "bordered"}
                                      key={uniqueKey}
                                      type="button"
                                      onClick={async () => {
                                        if (isSelected) return;
                                        setQuantity(1);
                                        const targetSize = (
                                          sizeOption.sizeOrWeight ||
                                          sizeOption.netContent ||
                                          ""
                                        )
                                          .toLowerCase()
                                          .trim();
                                        // Use product.product.slug for variant parameter (from full product object)
                                        const targetProductSlug =
                                          (sizeOption as any).product?.slug ||
                                          sizeOption.productSlug;
                                        const availableProducts = (
                                          product.availabilities || []
                                        ).filter(
                                          (avail: ProductAvailability) =>
                                            avail.inStock >= 1
                                        );
                                        const sameSizePackages =
                                          availableProducts.filter(
                                            (avail: ProductAvailability) => {
                                              const availProductId =
                                                avail.id.split("::")[0];
                                              let matchingOpt = null;
                                              if (
                                                avail.packageId &&
                                                avail.packageId.trim() !== ""
                                              ) {
                                                matchingOpt =
                                                  product.sizeOptions?.find(
                                                    (opt: any) =>
                                                      opt.packageId ===
                                                      avail.packageId
                                                  );
                                              } else {
                                                matchingOpt =
                                                  product.sizeOptions?.find(
                                                    (opt: any) =>
                                                      opt.productId ===
                                                      availProductId
                                                  );
                                              }
                                              if (!matchingOpt) return false;
                                              const availSize = (
                                                matchingOpt.sizeOrWeight ||
                                                matchingOpt.netContent ||
                                                ""
                                              )
                                                .toLowerCase()
                                                .trim();
                                              return availSize === targetSize;
                                            }
                                          );
                                        let selectedAvailability: ProductAvailability | null =
                                          null;
                                        let selectedPackageIdValue:
                                          | string
                                          | null = null;
                                        if (sameSizePackages.length > 0) {
                                          let packagesToSelect =
                                            sameSizePackages;
                                          if (targetProductSlug) {
                                            const variantPackages =
                                              sameSizePackages.filter(
                                                (
                                                  avail: ProductAvailability
                                                ) => {
                                                  const availProductId =
                                                    avail.id.split("::")[0];
                                                  let matchingOpt = null;
                                                  if (
                                                    avail.packageId &&
                                                    avail.packageId.trim() !==
                                                    ""
                                                  ) {
                                                    matchingOpt =
                                                      product.sizeOptions?.find(
                                                        (opt: any) =>
                                                          opt.packageId ===
                                                          avail.packageId
                                                      );
                                                  } else {
                                                    matchingOpt =
                                                      product.sizeOptions?.find(
                                                        (opt: any) =>
                                                          opt.productId ===
                                                          availProductId
                                                      );
                                                  }
                                                  return (
                                                    matchingOpt?.productSlug ===
                                                    targetProductSlug
                                                  );
                                                }
                                              );
                                            if (variantPackages.length > 0) {
                                              packagesToSelect =
                                                variantPackages;
                                            }
                                          }
                                          packagesToSelect.sort(
                                            (
                                              a: ProductAvailability,
                                              b: ProductAvailability
                                            ) => {
                                              const dateA = new Date(
                                                a.createdAt
                                              ).getTime();
                                              const dateB = new Date(
                                                b.createdAt
                                              ).getTime();
                                              return dateA - dateB;
                                            }
                                          );
                                          const availablePackage =
                                            packagesToSelect.find(
                                              (p: ProductAvailability) =>
                                                p.inStock >= 1
                                            );
                                          if (availablePackage) {
                                            selectedAvailability =
                                              availablePackage;
                                            const availProductIdValue =
                                              availablePackage.id.split(
                                                "::"
                                              )[0];
                                            selectedPackageIdValue =
                                              availablePackage.packageId &&
                                                availablePackage.packageId.trim() !==
                                                ""
                                                ? availablePackage.packageId
                                                : availProductIdValue;
                                            setSelectedProductId(
                                              availProductIdValue
                                            );
                                          } else {
                                            selectedAvailability =
                                              product.availabilities?.find(
                                                (a: ProductAvailability) => {
                                                  const availProductId =
                                                    a.id.split("::")[0];
                                                  return (
                                                    (a.packageId &&
                                                      a.packageId.trim() !==
                                                      "" &&
                                                      a.packageId ===
                                                      sizeOption.packageId) ||
                                                    (sizeOption.productId &&
                                                      availProductId ===
                                                      sizeOption.productId)
                                                  );
                                                }
                                              ) || null;
                                            selectedPackageIdValue =
                                              sizeOption.packageId &&
                                                sizeOption.packageId.trim() !== ""
                                                ? sizeOption.packageId
                                                : sizeOption.productId || "";
                                            setSelectedProductId(
                                              sizeOption.productId || null
                                            );
                                          }
                                        } else {
                                          selectedAvailability =
                                            product.availabilities?.find(
                                              (a: ProductAvailability) => {
                                                const availProductId =
                                                  a.id.split("::")[0];
                                                return (
                                                  (a.packageId &&
                                                    a.packageId.trim() !== "" &&
                                                    a.packageId ===
                                                    sizeOption.packageId) ||
                                                  (sizeOption.productId &&
                                                    availProductId ===
                                                    sizeOption.productId)
                                                );
                                              }
                                            ) || null;
                                          selectedPackageIdValue =
                                            sizeOption.packageId &&
                                              sizeOption.packageId.trim() !== ""
                                              ? sizeOption.packageId
                                              : sizeOption.productId || "";
                                          setSelectedProductId(
                                            sizeOption.productId || null
                                          );
                                        }
                                        if (selectedPackageIdValue) {
                                          setSelectedPackageId(
                                            selectedPackageIdValue
                                          );
                                        }
                                        // Use the switchVariant helper to update variant without reload
                                        // This updates product details and URL slug using cached group data
                                        // Pass the full product object from sizeOption.product for product details
                                        switchVariant(targetProductSlug, {
                                          ...sizeOption,
                                          product:
                                            (sizeOption as any).product || null,
                                        });
                                      }}
                                      className="!px-6 !rounded-lg"
                                    >
                                      {sizeLabel}
                                    </Button>
                                  );
                                }
                              )}
                            </div>
                          );
                        }

                        return null;
                      })()}
                      {/* Price */}
                      <div className="flex items-center gap-3">
                        <span className="text-2xl sm:text-[32px] text-blue-100 font-bold leading-tight">
                          ${selectedPrice.toFixed(2)}
                        </span>
                        {originalPrice && originalPrice > selectedPrice && (
                          <span className="text-lg sm:text-xl text-text-disabled font-medium line-through">
                            ${originalPrice.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* In-page Add to Cart, desktop only (mobile uses the sticky drawer below) */}
                    <div className="hidden sm:flex flex-col gap-3 sm:gap-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center border border-neutral-10 rounded-xl bg-constant-white px-3 py-1.5 gap-1">
                          <Button
                            variant="icon"
                            size="icon-sm"
                            type="button"
                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            disabled={quantity <= 1}
                            aria-label="Decrease quantity"
                          >
                            <MinusIcon size={20} color="currentColor" />
                          </Button>
                          <span className="mx-3 text-base text-blue-80 font-semibold min-w-[24px] text-center tabular-nums">
                            {quantity}
                          </span>
                          <Button
                            variant="icon"
                            size="icon-sm"
                            type="button"
                            onClick={() => {
                              const max = selectedAvailability?.inStock || 1;
                              setQuantity(Math.min(max, quantity + 1));
                            }}
                            disabled={!selectedAvailability || quantity >= selectedAvailability.inStock}
                            aria-label="Increase quantity"
                          >
                            <PlusIcon size={20} color="currentColor" />
                          </Button>
                        </div>
                        <Button
                          type="button"
                          variant="primary"
                          size="lg"
                          onClick={handleAddToCart}
                          disabled={isOutOfStock}
                          className="rounded-xl w-full"
                        >
                          <span className="flex items-center justify-center gap-1">
                            <CartIcon size={20} color="currentColor" />
                            {isOutOfStock ? STATUS_OUT_OF_STOCK : CTA_ADD_TO_CART}
                          </span>
                        </Button>
                      </div>
                    </div>

                    {/* Mobile-only sticky bottom drawer, portaled to <body> so no
                        ancestor transform (PageTransition animation, etc.) can pin it
                        to the top of the footer. */}
                    {typeof document !== "undefined"
                      ? createPortal(
                        <div
                          className="sm:hidden fixed bottom-0 left-0 right-0 z-[40] rounded-t-3xl border-t border-neutral-10 bg-constant-white shadow-[0_-12px_32px_-12px_rgba(15,23,42,0.18)]"
                          style={{
                            paddingBottom:
                              "max(0.75rem, env(safe-area-inset-bottom, 0.75rem))",
                          }}
                        >
                          <div
                            aria-hidden
                            className="mx-auto mt-2 h-1 w-10 rounded-full bg-neutral-10"
                          />
                          <div className="px-4 pb-1 pt-2 max-w-screen-xl mx-auto space-y-3">
                            {selectedPrice > 0 && !isOutOfStock ? (
                              <div className="flex items-baseline justify-between gap-3">
                                <div className="text-text-tertiary">
                                  <span className="font-semibold text-text-secondary">
                                    {quantity}
                                  </span>{" "}
                                  x ${selectedPrice.toFixed(2)}
                                  {originalPrice && originalPrice > selectedPrice ? (
                                    <span className="ml-2 text-text-tertiary line-through opacity-70">
                                      ${originalPrice.toFixed(2)}
                                    </span>
                                  ) : null}
                                </div>
                                <div className="text-lg font-bold tabular-nums text-blue-100">
                                  ${(selectedPrice * quantity).toFixed(2)}
                                </div>
                              </div>
                            ) : null}
                            <div className="flex items-center gap-3">
                              <div className="flex items-center border border-neutral-10 rounded-xl bg-constant-white px-2 py-1.5 gap-1">
                                <Button
                                  variant="icon"
                                  size="icon-sm"
                                  type="button"
                                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                  disabled={quantity <= 1}
                                  aria-label="Decrease quantity"
                                >
                                  <MinusIcon size={20} color="currentColor" />
                                </Button>
                                <span className="mx-2 text-base text-blue-80 font-semibold min-w-[24px] text-center tabular-nums">
                                  {quantity}
                                </span>
                                <Button
                                  variant="icon"
                                  size="icon-sm"
                                  type="button"
                                  onClick={() => {
                                    const max = selectedAvailability?.inStock || 1;
                                    setQuantity(Math.min(max, quantity + 1));
                                  }}
                                  disabled={!selectedAvailability || quantity >= selectedAvailability.inStock}
                                  aria-label="Increase quantity"
                                >
                                  <PlusIcon size={20} color="currentColor" />
                                </Button>
                              </div>
                              <Button
                                type="button"
                                variant="primary"
                                size="lg"
                                onClick={handleAddToCart}
                                disabled={isOutOfStock}
                                className="rounded-xl flex-1"
                              >
                                <span className="flex items-center justify-center gap-2">
                                  <CartIcon className="w-5 h-5 flex-shrink-0" color="currentColor" />
                                  {isOutOfStock ? STATUS_OUT_OF_STOCK : CTA_ADD_TO_CART}
                                </span>
                              </Button>
                            </div>
                          </div>
                        </div>,
                        document.body
                      )
                      : null}
                  </div>

                  {/* Divider */}
                  <div className="w-full h-[1px] bg-neutral-10"></div>

                  {/* Product overview */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <h3
                        className="cursor-pointer select-none text-lg font-bold text-blue-100 sm:text-xl"
                        onClick={() =>
                          setExpandedSections((prev) => ({
                            ...prev,
                            productOverview: !prev.productOverview,
                          }))
                        }
                      >
                        Product Overview
                      </h3>
                      <Button
                        type="button"
                        variant="icon"
                        size="icon-sm"
                        onClick={() =>
                          setExpandedSections((prev) => ({
                            ...prev,
                            productOverview: !prev.productOverview,
                          }))
                        }
                      >
                        {expandedSections.productOverview ? (
                          <MinusIcon size={16} color="currentColor" />
                        ) : (
                          <PlusIcon size={16} color="currentColor" />
                        )}
                      </Button>
                    </div>

                    {expandedSections.productOverview && (
                      <div className="space-y-6">
                        {product?.longDescription && (
                          <ExpandableText
                            collapsedHeight={80}
                            className="product-overview"
                            contentClassName="text-sm leading-relaxed text-text-secondary sm:text-base"
                            fadeClassName="h-8 bg-gradient-to-t from-white via-white/90 to-transparent"
                            buttonClassName="mt-3 inline-flex items-center gap-2 self-start rounded-full border border-blue-10 bg-constant-white px-1 py-1 pl-4 text-body-sm font-medium text-blue-80 transition-colors hover:border-blue-15"
                            expandedButtonClassName="border-blue-15 bg-blue-5 text-text-primary"
                            iconWrapperClassName="border-blue-10 bg-blue-5/70"
                            expandedIconWrapperClassName="border-blue-15 bg-constant-white"
                            chevronColor="currentColor"
                            expandLabel="Read more"
                            collapseLabel="Show less"
                          >
                            <p>{product.longDescription}</p>
                          </ExpandableText>
                        )}

                        <div className="w-full max-sm:overflow-x-auto max-sm:pb-3">
                          <div className="flex w-max gap-8 sm:w-full sm:grid sm:max-w-none sm:grid-cols-4 sm:gap-6">
                            <div className="min-w-[7.5rem] flex-shrink-0 sm:min-w-0">
                              <p className="mb-1 text-sm text-text-secondary">
                                THC range
                              </p>
                              <p className="text-base font-bold text-blue-100">
                                {thcRange || "N/A"}
                              </p>
                            </div>
                            <div className="min-w-[7.5rem] flex-shrink-0 sm:min-w-0">
                              <p className="mb-1 text-sm text-text-secondary">
                                CBD range
                              </p>
                              <p className="text-base font-bold text-blue-100">
                                {cbdRange || "N/A"}
                              </p>
                            </div>
                            <div className="min-w-[9rem] flex-shrink-0 sm:min-w-0">
                              <p className="mb-1 text-sm text-text-secondary">
                                Cannabinoids
                              </p>
                              <p className="text-base font-bold text-blue-100">
                                {pdpCannabinoidsLine || "N/A"}
                              </p>
                            </div>
                            <div className="min-w-[7.5rem] flex-shrink-0 sm:min-w-0">
                              <p className="mb-1 text-sm text-text-secondary">
                                Total Terpene
                              </p>
                              <p className="text-base font-bold text-blue-100">
                                {currentLotTotalTerpenePercent !== null &&
                                  currentLotTotalTerpenePercent !== undefined
                                  ? `${Number(currentLotTotalTerpenePercent).toFixed(1)}%`
                                  : "N/A"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Lot information (per-lot lab data, opt-in via admin toggle) */}
                  {product?.showLotInfo && (product?.lots?.length ?? 0) > 0 && (
                    <>
                      <div className="w-full h-[1px] bg-neutral-10"></div>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between gap-3">
                          <h3
                            className="text-lg sm:text-xl font-bold text-blue-100 cursor-pointer select-none"
                            onClick={() => setExpandedSections((prev) => ({ ...prev, lots: !prev.lots }))}
                          >
                            Lot information
                          </h3>
                          <Button
                            type="button"
                            variant="icon"
                            size="icon-sm"
                            onClick={() => setExpandedSections((prev) => ({ ...prev, lots: !prev.lots }))}
                          >
                            {expandedSections.lots ? <MinusIcon size={16} color="currentColor" /> : <PlusIcon size={16} color="currentColor" />}
                          </Button>
                        </div>
                        {expandedSections.lots && (
                          <div className="space-y-3">
                            <p className="text-sm text-blue-60">
                              This product is sold in more than one lot. Each lot has its own lab results — check the lot
                              number on your package.
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full min-w-[640px] text-left text-sm text-blue-100">
                                <thead>
                                  <tr className="border-b border-neutral-10 text-xs uppercase tracking-wide text-blue-60">
                                    <th className="py-2 pr-4 font-semibold">Lot #</th>
                                    <th className="py-2 pr-4 font-semibold">THC</th>
                                    <th className="py-2 pr-4 font-semibold">CBD</th>
                                    <th className="py-2 pr-4 font-semibold">Total terpenes</th>
                                    <th className="py-2 pr-4 font-semibold">Dominant terpene</th>
                                    <th className="py-2 pr-4 font-semibold">Strain</th>
                                    <th className="py-2 pr-4 font-semibold">Harvested</th>
                                    <th className="py-2 font-semibold">Packaged</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(product.lots ?? []).map((lot) => (
                                    <tr key={lot.lotCode} className="border-b border-neutral-10/60 last:border-0 align-top">
                                      <td className="py-2 pr-4 font-semibold">{lot.lotCode}</td>
                                      <td className="py-2 pr-4">
                                        {lot.thcMg != null ? `${lot.thcMg} mg/g` : lot.thcPercent != null ? `${lot.thcPercent}%` : "—"}
                                      </td>
                                      <td className="py-2 pr-4">
                                        {lot.cbdMg != null ? `${lot.cbdMg} mg/g` : lot.cbdPercent != null ? `${lot.cbdPercent}%` : "—"}
                                      </td>
                                      <td className="py-2 pr-4">
                                        {lot.totalTerpenePercent != null ? `${lot.totalTerpenePercent}%` : "—"}
                                      </td>
                                      <td className="py-2 pr-4">{lot.dominantTerpene || "—"}</td>
                                      <td className="py-2 pr-4">{lot.strain || "—"}</td>
                                      <td className="py-2 pr-4 whitespace-nowrap">{lot.harvestDate || "—"}</td>
                                      <td className="py-2 whitespace-nowrap">{lot.packageDate || "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Divider */}
                  <div className="w-full h-[1px] bg-neutral-10"></div>

                  {/* Effects Section */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <h3
                        className="text-lg sm:text-xl font-bold text-blue-100 cursor-pointer select-none"
                        onClick={() => setExpandedSections((prev) => ({ ...prev, effects: !prev.effects }))}
                      >
                        Effects
                      </h3>
                      <Button
                        type="button"
                        variant="icon"
                        size="icon-sm"
                        onClick={() => setExpandedSections((prev) => ({ ...prev, effects: !prev.effects }))}
                      >
                        {expandedSections.effects ? <MinusIcon size={16} color="currentColor" /> : <PlusIcon size={16} color="currentColor" />}
                      </Button>
                    </div>

                    {expandedSections.effects && (
                      <div className="space-y-4">
                        {/* Effects Tags */}
                        {(() => {
                          // Get effects from specifications or options - only show if they exist in product data
                          const effectsFromSpecs =
                            product?.specifications?.find(
                              (s: { name: string; value: string }) =>
                                s.name.toLowerCase() === "effects"
                            )?.value;

                          // Also check options field (JSON) for effects
                          let effectsFromOptions: string | null = null;
                          if (
                            product?.options &&
                            typeof product.options === "object"
                          ) {
                            if ("effects" in product.options) {
                              effectsFromOptions =
                                typeof product.options.effects === "string"
                                  ? product.options.effects
                                  : Array.isArray(product.options.effects)
                                    ? product.options.effects.join(", ")
                                    : String(product.options.effects);
                            }
                          }

                          // Use effects from specifications first, then fallback to options
                          const effectsValue =
                            effectsFromSpecs || effectsFromOptions;

                          // Parse effects (comma-separated, semicolon-separated, or space-separated)
                          let effects: string[] = [];
                          if (effectsValue) {
                            // Try comma-separated first
                            if (effectsValue.includes(",")) {
                              effects = effectsValue
                                .split(",")
                                .map((e) => e.trim())
                                .filter(Boolean);
                            }
                            // Try semicolon-separated
                            else if (effectsValue.includes(";")) {
                              effects = effectsValue
                                .split(";")
                                .map((e) => e.trim())
                                .filter(Boolean);
                            }
                            // Try space-separated (for single words or simple lists)
                            else {
                              effects = effectsValue
                                .split(/\s+/)
                                .map((e) => e.trim())
                                .filter(Boolean);
                            }
                          }

                          // Show effects if they exist, otherwise show a suitable message
                          if (effects.length > 0) {
                            return (
                              <div className="flex flex-wrap gap-2">
                                {effects.map((effect, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center rounded-md border border-neutral-10 bg-neutral-5 px-2.5 py-1 text-sm font-medium text-blue-100 sm:px-3 sm:py-1.5"
                                  >
                                    {effect}
                                  </span>
                                ))}
                              </div>
                            );
                          } else {
                            return (
                              <p className="text-sm text-text-secondary">
                                Effects information is not available for this
                                product.
                              </p>
                            );
                          }
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Divider */}
                  <div className="w-full h-[1px] bg-neutral-10"></div>

                  {/* Brand Details Section */}
                  {brand && (
                    <>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between gap-3">
                          <h3
                            className="cursor-pointer select-none text-lg font-bold text-blue-100 sm:text-xl"
                            onClick={() =>
                              setExpandedSections((prev) => ({
                                ...prev,
                                aboutBrand: !prev.aboutBrand,
                              }))
                            }
                          >
                            About &quot;{brand}&quot; brand
                          </h3>
                          <Button
                            variant="icon"
                            size="icon-sm"
                            type="button"
                            onClick={() =>
                              setExpandedSections((prev) => ({
                                ...prev,
                                aboutBrand: !prev.aboutBrand,
                              }))
                            }
                          >
                            {expandedSections.aboutBrand ? (
                              <MinusIcon size={16} color="currentColor" />
                            ) : (
                              <PlusIcon size={16} color="currentColor" />
                            )}
                          </Button>
                        </div>

                        {expandedSections.aboutBrand && (
                          <div className="space-y-6">
                            {(product as { brand?: { logoUri?: string } })
                              ?.brand?.logoUri && (
                                <div className="flex items-center justify-start">
                                  <img
                                    src={(product as { brand?: { logoUri?: string } }).brand!.logoUri}
                                    alt=""
                                    className="h-16 w-auto object-contain"
                                    onError={(e) => {
                                      (
                                        e.target as HTMLImageElement
                                      ).style.display = "none";
                                    }}
                                  />
                                </div>
                              )}

                            {(() => {
                              const brandDescription = (
                                product as { brand?: { description?: string } }
                              )?.brand?.description;
                              const specDescription = !brandDescription
                                ? product?.specifications?.find(
                                  (s: { name: string; value: string }) =>
                                    [
                                      "brand description",
                                      "about brand",
                                      "brand info",
                                    ].includes(s.name.toLowerCase())
                                )?.value
                                : null;
                              const description =
                                brandDescription || specDescription || null;
                              if (description) {
                                return (
                                  <ExpandableText
                                    collapsedHeight={80}
                                    contentClassName="text-sm leading-relaxed text-text-secondary sm:text-base"
                                    fadeClassName="h-8 bg-gradient-to-t from-white via-white/90 to-transparent"
                                    buttonClassName="mt-3 inline-flex items-center gap-2 self-start rounded-full border border-blue-10 bg-constant-white px-1 py-1 pl-4 text-body-sm font-medium text-blue-80 transition-colors hover:border-blue-15"
                                    expandedButtonClassName="border-blue-15 bg-blue-5 text-text-primary"
                                    iconWrapperClassName="border-blue-10 bg-blue-5/70"
                                    expandedIconWrapperClassName="border-blue-15 bg-constant-white"
                                    chevronColor="currentColor"
                                    expandLabel="Read more"
                                    collapseLabel="Show less"
                                  >
                                    <p>{description}</p>
                                  </ExpandableText>
                                );
                              }
                              return (
                                <p className="text-sm text-text-secondary">
                                  Brand information is not available for {brand}.
                                </p>
                              );
                            })()}

                            <div>
                              <p className="mb-3 text-sm font-semibold text-blue-100">
                                Details
                              </p>
                              <ul className="list-disc space-y-2 pl-5 text-sm text-text-secondary marker:text-blue-80">
                                {(
                                  [
                                    [
                                      "Licensed Producer",
                                      (product as {
                                        brand?: { licensedProducer?: string | null };
                                      })?.brand?.licensedProducer,
                                    ],
                                    [
                                      "Province of Origin",
                                      mergedPdpProduct?.provinceOfOrigin,
                                    ],
                                    [
                                      "Region of Product",
                                      mergedPdpProduct?.regionOfProduct,
                                    ],
                                  ] as const
                                ).map(([label, value]) =>
                                  value && String(value).trim() ? (
                                    <li key={label}>
                                      <span className="font-medium text-blue-100">
                                        {label}:
                                      </span>{" "}
                                      {String(value).trim()}
                                    </li>
                                  ) : null
                                )}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Divider */}
                      <div className="w-full h-[1px] bg-neutral-10"></div>
                    </>
                  )}

                  {/* Product classification */}
                  {showCannabisDetailSections &&
                    product &&
                    (() => {
                      const subcat =
                        mergedPdpProduct?.altSubCategory?.trim() ||
                        specValueByNames(mergedPdpProduct?.specifications, [
                          "subcategory",
                          "sub-category",
                        ]);
                      const typeLine =
                        mergedPdpProduct?.typeAlt?.trim() ||
                        mergedPdpProduct?.format?.trim() ||
                        specValueByNames(mergedPdpProduct?.specifications, [
                          "type",
                          "plant type",
                        ]);
                      const cat =
                        mergedPdpProduct?.altCategory?.trim() ||
                        mergedPdpProduct?.classificationName?.trim();
                      const grow =
                        mergedPdpProduct?.growingMethod?.trim() ||
                        specValueByNames(mergedPdpProduct?.specifications, [
                          "growing method",
                        ]);
                      let rows = [
                        ["Category", cat],
                        ["Subcategory", subcat],
                        ["Type", typeLine],
                        ["Growing Method", grow],
                      ].filter(([, v]) => v && String(v).trim());
                      if (rows.length === 0) {
                        rows = [
                          [
                            "Category",
                            mergedPdpProduct?.classificationName?.trim() ||
                            classification ||
                            "—",
                          ],
                        ];
                      }
                      return (
                        <>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between gap-3">
                              <h3
                                className="cursor-pointer select-none text-lg font-bold text-blue-100 sm:text-xl"
                                onClick={() =>
                                  setExpandedSections((prev) => ({
                                    ...prev,
                                    classification: !prev.classification,
                                  }))
                                }
                              >
                                Product Classification
                              </h3>
                              <Button
                                variant="icon"
                                size="icon-sm"
                                type="button"
                                onClick={() =>
                                  setExpandedSections((prev) => ({
                                    ...prev,
                                    classification: !prev.classification,
                                  }))
                                }
                              >
                                {expandedSections.classification ? (
                                  <MinusIcon size={16} color="currentColor" />
                                ) : (
                                  <PlusIcon size={16} color="currentColor" />
                                )}
                              </Button>
                            </div>
                            {expandedSections.classification && (
                              <ul className="list-disc space-y-2 pl-5 text-sm text-text-secondary marker:text-blue-80">
                                {rows.map(([label, value]) => (
                                  <li key={String(label)}>
                                    <span className="font-medium text-blue-100">
                                      {label}:
                                    </span>{" "}
                                    {String(value).trim()}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div className="h-px w-full bg-neutral-10" />
                        </>
                      );
                    })()}

                  {/* Terpenes & Flavors from imported weekly AGLC profile fields */}
                  {showCannabisDetailSections && (() => {
                    const flavorTags = !isAccessory ? pdpTerpeneProfile.other : [];
                    const dominantRows = !isAccessory ? pdpTerpeneProfile.dominant : [];
                    const showTerpenesSection =
                      !isAccessory &&
                      (flavorTags.length > 0 ||
                        dominantRows.length > 0);
                    if (!showTerpenesSection) return null;
                    const terpBarMax = Math.max(
                      ...dominantRows.map((t) => t.percent),
                      0.001
                    );
                    return (
                      <>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between gap-3">
                            <h3
                              className="cursor-pointer select-none text-lg font-bold text-blue-100 sm:text-xl"
                              onClick={() => setExpandedSections((prev) => ({ ...prev, terpenes: !prev.terpenes }))}
                            >
                              Terpenes &amp; Flavors
                            </h3>
                            <Button
                              variant="icon"
                              size="icon-sm"
                              type="button"
                              onClick={() => setExpandedSections((prev) => ({ ...prev, terpenes: !prev.terpenes }))}
                            >
                              {expandedSections.terpenes ? <MinusIcon size={16} color="currentColor" /> : <PlusIcon size={16} color="currentColor" />}
                            </Button>
                          </div>

                          {expandedSections.terpenes && (
                            <div className="space-y-5">
                              {flavorTags.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {flavorTags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="inline-flex items-center rounded-md border border-neutral-10 bg-neutral-5 px-2.5 py-1 text-sm font-medium text-blue-100 sm:px-3 sm:py-1.5"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {dominantRows.length > 0 && (
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                                  {dominantRows.map((row) => (
                                    <div key={row.name} className="w-full">
                                      <div className="mb-2 flex items-center justify-between">
                                        <span className="text-sm font-semibold capitalize text-blue-100">
                                          {row.name}
                                        </span>
                                        <span className="text-sm font-semibold text-blue-100">
                                          {row.percent}%
                                        </span>
                                      </div>
                                      <div className="h-2 overflow-hidden rounded-full bg-neutral-10">
                                        <div
                                          className="h-full rounded-full bg-blue-80 transition-[width] duration-300"
                                          style={{
                                            width: `${Math.min(
                                              100,
                                              (row.percent / terpBarMax) * 100
                                            )}%`,
                                          }}
                                        />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                            </div>
                          )}
                        </div>

                        {/* Divider */}
                        <div className="w-full h-[1px] bg-neutral-10"></div>
                      </>
                    );
                  })()}

                  {/* Format & pack */}
                  {showCannabisDetailSections && product && (
                    <>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between gap-3">
                          <h3
                            className="cursor-pointer select-none text-lg font-bold text-blue-100 sm:text-xl"
                            onClick={() =>
                              setExpandedSections((prev) => ({
                                ...prev,
                                formatPack: !prev.formatPack,
                              }))
                            }
                          >
                            Format &amp; Pack details
                          </h3>
                          <Button
                            variant="icon"
                            size="icon-sm"
                            onClick={() =>
                              setExpandedSections((prev) => ({
                                ...prev,
                                formatPack: !prev.formatPack,
                              }))
                            }
                          >
                            {expandedSections.formatPack ? (
                              <MinusIcon size={16} color="currentColor" />
                            ) : (
                              <PlusIcon size={16} color="currentColor" />
                            )}
                          </Button>
                        </div>
                        {expandedSections.formatPack && (
                          <ul className="list-disc space-y-2 pl-5 text-sm text-text-secondary marker:text-blue-80">
                            <li>
                              <span className="font-medium text-blue-100">
                                Net Content:
                              </span>{" "}
                              {mergedPdpProduct?.netContent?.trim() || "—"}
                            </li>
                            <li>
                              <span className="font-medium text-blue-100">
                                Equivalence:
                              </span>{" "}
                              {mergedPdpProduct?.equivalenceDriedFlowerG !=
                                null &&
                                mergedPdpProduct?.equivalenceDriedFlowerG !==
                                undefined
                                ? `${Number(mergedPdpProduct.equivalenceDriedFlowerG)}g`
                                : "—"}
                            </li>
                          </ul>
                        )}
                      </div>
                      <div className="h-px w-full bg-neutral-10" />
                    </>
                  )}

                  {/* Directions & use */}
                  {showCannabisDetailSections && product && (
                    <>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between gap-3">
                          <h3
                            className="cursor-pointer select-none text-lg font-bold text-blue-100 sm:text-xl"
                            onClick={() =>
                              setExpandedSections((prev) => ({
                                ...prev,
                                directions: !prev.directions,
                              }))
                            }
                          >
                            Directions &amp; Use
                          </h3>
                          <Button
                            variant="icon"
                            size="icon-sm"
                            type="button"
                            onClick={() =>
                              setExpandedSections((prev) => ({
                                ...prev,
                                directions: !prev.directions,
                              }))
                            }
                          >
                            {expandedSections.directions ? (
                              <MinusIcon size={16} color="currentColor" />
                            ) : (
                              <PlusIcon size={16} color="currentColor" />
                            )}
                          </Button>
                        </div>
                        {expandedSections.directions && (
                          <div className="text-sm leading-relaxed text-text-secondary sm:text-base">
                            {mergedPdpProduct?.directions?.trim() ? (
                              <p className="whitespace-pre-line">
                                {mergedPdpProduct.directions.trim()}
                              </p>
                            ) : (
                              <p>
                                Directions are not available for this product.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="h-px w-full bg-neutral-10" />
                    </>
                  )}

                  {/* Create Account Section */}
                  <div className="relative max-sm:h-[430px] w-full overflow-hidden rounded-2xl border border-rose-100/80 bg-gradient-to-br from-rose-50 via-pink-50 to-rose-100">
                    {/* Background Image */}
                    <div className="absolute inset-0 max-sm:hidden">
                      <img
                        src="/media/account/create-account.webp"
                        alt=""
                        className="absolute object-cover top-0 right-0 h-full w-auto"
                      />
                    </div>

                    {/* Mobile Background Image */}
                    <div className="absolute inset-0 sm:hidden">
                      <img
                        src="/media/account/create-account-mobile.webp"
                        alt=""
                        className="absolute object-cover top-0 right-0 h-full w-auto"
                      />
                    </div>

                    {/* Content Overlay */}
                    <div className="relative z-10 flex flex-col lg:flex-row items-center">
                      <div className="flex-1 p-4 sm:p-5 flex flex-col justify-center">
                        <h3 className="text-xl sm:text-2xl font-bold text-blue-100 mb-2 sm:mb-3 max-w-[520px]">
                          Create Account to Unlock Purchases, Discounts &
                          Exclusive Drops
                        </h3>
                        <p className="text-xs sm:text-sm md:text-base text-text-secondary font-medium mb-4 sm:mb-6 max-w-[520px]">
                          Sign up in 30 seconds: buy cannabis, track orders, get
                          personal discounts and early access to new strains.
                          21+ only
                        </p>
                        <Button asChild variant="primary" size="lg" className="w-fit rounded-xl">
                          <Link href="/account">
                            Create account
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PDP recommendations (#43) */}
          <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 pb-8 sm:pb-12 lg:pb-16 sm:mt-16 mt-8">
            <div className="space-y-6 sm:space-y-8">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-6">
                <h2 className="text-2xl sm:text-[32px] text-blue-100 font-bold leading-tight">
                  You might also like
                </h2>
                <Button asChild variant="primary" size="lg" className="w-fit rounded-xl">
                  <Link href={shopAllHref}>{shopAllLabel}</Link>
                </Button>
              </div>

              {/* Related products grid */}
              {loadingRelatedProducts ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="bg-white border border-neutral-10 rounded-xl p-4 animate-pulse"
                    >
                      <div className="w-full h-48 bg-neutral-10 rounded-lg mb-4"></div>
                      <div className="h-4 bg-neutral-10 rounded mb-2"></div>
                      <div className="h-4 bg-neutral-10 rounded w-2/3"></div>
                    </div>
                  ))}
                </div>
              ) : visibleRelatedProducts.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                  {visibleRelatedProducts.map(({ item, price }) => (
                    <ProductCard
                      key={item.id}
                      item={item}
                      price={price}
                      relatedProducts={true}
                      analyticsSource="pdp_recommendations"
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-neutral-10 px-6 py-10 text-center">
                  <p className="text-text-secondary text-sm">
                    No related picks for this item right now. Use Shop all to explore more.
                  </p>
                </div>
              )}

              {!loadingRelatedProducts && visibleRelatedProducts.length > 0 ? (
                <div className="flex flex-col items-center gap-3 border-t border-neutral-10 pt-6">
                  {pdpRecFeedbackDone ? (
                    <div className="inline-flex items-center gap-2 rounded-full bg-green-5 px-4 py-2 text-body-14-desktop font-medium text-green-60">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      Thanks for the feedback.
                    </div>
                  ) : (
                    <>
                      <p className="text-body-14-desktop text-text-secondary">
                        Were these suggestions helpful?
                      </p>
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        <Button
                          variant="bordered"
                          type="button"
                          aria-label="Yes, these suggestions were helpful"
                          className="rounded-xl"
                          onClick={() => {
                            setPdpRecFeedbackDone(true);
                            trackFunnelEvent({
                              event: "funnel_recommendation_feedback",
                              placement: "pdp",
                              helpful: true,
                              product_id: product?.id,
                            });
                          }}
                        >
                          <span className="inline-flex items-center gap-1">
                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10H9M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                            </svg>
                            Yes
                          </span>
                        </Button>
                        <Button
                          variant="bordered"
                          type="button"
                          aria-label="No, these suggestions were not helpful"
                          className="rounded-xl"
                          onClick={() => {
                            setPdpRecFeedbackDone(true);
                            trackFunnelEvent({
                              event: "funnel_recommendation_feedback",
                              placement: "pdp",
                              helpful: false,
                              product_id: product?.id,
                            });
                          }}
                        >
                          <span className="inline-flex items-center gap-1">
                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06L17 4m-7 10v5a2 2 0 002 2h.095c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                            </svg>
                            No
                          </span>
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

// Helper function to get terpene information
// Uses Tailwind design tokens (bg-*, text-*) instead of raw hex colors.
function getTerpeneInfo(terpeneName: string): {
  bgClass: string;
  icon: React.ReactElement;
  description: string;
} {
  const lower = terpeneName.toLowerCase();

  // Caryophyllene
  if (lower.includes("caryophyllene")) {
    return {
      bgClass: "bg-blue-5",
      icon: (
        <img
          src="https://a-ca.storyblok.com/f/2000389/112x112/cdb1734ffd/acorn.png"
          alt="Caryophyllene"
          className="w-7 h-7 object-contain"
          crossOrigin="anonymous"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      ),
      description:
        "Present in black pepper, cloves and many other herbs and spices.",
    };
  }

  // Limonene
  if (lower.includes("limonene")) {
    return {
      bgClass: "bg-yellow-5",
      icon: (
        <img
          src="https://a-ca.storyblok.com/f/2000389/55x54/cccb239352/limonene.png"
          alt="Limonene"
          className="w-7 h-7 object-contain"
          crossOrigin="anonymous"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      ),
      description:
        "Limonene is known for its refreshing, citrusy aroma and flavour.",
    };
  }

  // Myrcene
  if (lower.includes("myrcene")) {
    return {
      bgClass: "bg-yellow-15",
      icon: (
        <img
          src="https://a-ca.storyblok.com/f/2000389/76x110/0d567ece64/mango.png"
          alt="Myrcene"
          className="w-7 h-7 object-contain"
          crossOrigin="anonymous"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      ),
      description:
        "Myrcene is associated with a sweet, earthy, fruity, and musky aroma. Beyond cannabis, myrcene is found in mangoes, hops, thyme, lemongrass, and cloves. When myrcene is the dominant terpene, some report a calming sensation.",
    };
  }

  // Pinene
  if (lower.includes("pinene")) {
    return {
      bgClass: "bg-green-5",
      icon: (
        <svg
          className="w-8 h-8 text-slate-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
          />
        </svg>
      ),
      description:
        "Pinene is known for its pine-like aroma and is found in pine needles, rosemary, and basil. It may help with alertness and focus.",
    };
  }

  // Linalool
  if (lower.includes("linalool")) {
    return {
      bgClass: "bg-blue-15",
      icon: (
        <svg
          className="w-8 h-8 text-slate-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      ),
      description:
        "Linalool is found in lavender and has a floral, sweet aroma. It is often associated with relaxation and stress relief.",
    };
  }

  // Default terpene
  return {
    bgClass: "bg-neutral-5",
    icon: (
      <svg
        className="w-8 h-8 text-slate-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      </svg>
    ),
    description: `Learn more about ${terpeneName} and its effects.`,
  };
}
